import Phaser from 'phaser';
import { BASE_INTEGRITY_MULTIPLIER, BATTLE_H, BATTLE_W, BOSS_HP_MULTIPLIER, COLORS, COIN_REWARD_MULTIPLIER, CREDIT_REWARD_MULTIPLIER, EARNED_INCOME_MULTIPLIER, ENEMY_HEALTH_MULTIPLIER, ENEMY_POWER_MULTIPLIER, GAME_HEIGHT, GAME_WIDTH, MAP_CLEAR_DATA_REWARD_BASE, MAP_FIELD_BOTTOM, MAP_FRAME, MAX_COMMAND_RANK, NIGHTMARE_BOSS_HP_MULTIPLIER, NIGHTMARE_EARNED_INCOME_MULTIPLIER, NIGHTMARE_STARTING_SUPPLY_MULTIPLIER, NORMAL_BOSS_HP_MULTIPLIER, NORMAL_ENEMY_HEALTH_MULTIPLIER, NORMAL_KILL_REWARD_MULTIPLIER, NORMAL_STARTING_SUPPLY, NORMAL_WEAPON_DAMAGE_MULTIPLIER, NOTIFICATION_TOAST_BACKGROUND_ALPHA, NOTIFICATION_TOAST_DURATION_SECONDS, PRIORITY, ROLLING_DEATH_DAMAGE_MULTIPLIER, scaleDataReward, scaleSupplyCost, scaleUpgradeCost, STARTING_SUPPLY_BONUS, STARTING_SUPPLY_MULTIPLIER, SURVIVAL_HP_INCREASE_PER_WAVE, UI_BOTTOM_H, UI_TOP_H, WEAPON_DAMAGE_MULTIPLIER, XP_REWARD_MULTIPLIER, type TargetPriority } from '../config/constants';
import { ENEMIES, type EnemyDef } from '../data/enemies';
import { MAP_PROP_FRAMES, MAP_PROP_LABELS, MAP_PROP_TEXTURES } from '../data/mapProps';
import { getMission, getMissionMap, getMissionWaves, getNextUnbeatenMissionId, MISSIONS } from '../data/missions';
import { getMapRoutes, type MapDef, type MapEnvironment } from '../data/map';
import { TOWER_ART, type TowerArtSpec } from '../data/towerVisuals';
import type { WaveDef, WaveGroup } from '../data/waves';
import { TOWERS, TOWER_ORDER } from '../data/towers';
import { SYMBIOSIS, SYMBIOSIS_ORDER, type SymbiosisDef } from '../data/symbiosis';
import { ABILITIES } from '../data/abilities';
import { bus, Events } from '../core/EventBus';
import { getRankPerkBonuses, saveManager, type ActiveRunSnapshot, type RankPerkBonuses } from '../core/SaveManager';
import { applyAudioSettings } from '../core/Settings';
import { rememberScene } from '../core/SessionMemory';
import { applyUiTextScale } from '../ui/UiText';
import { voice } from '../core/Voice';
import { audio } from '../core/AudioSynth';
import { navigateTo } from '../core/SceneTransition';
import { announceAchievement } from '../core/AchievementAudio';
import { researchManager, type ResearchBonuses } from '../core/ResearchManager';
import { achievements } from '../core/AchievementManager';
import { music } from '../core/MusicSystem';
import { localMusic } from '../core/LocalMusicPlayer';
import { announcer } from '../core/Announcer';
import { buildPathCache, distanceToPathSegment, samplePath, type PathCache } from '../sim/Path';
import { buildRoadRibbon, getRoadHalfWidth, smoothRoadPoints } from '../sim/RoadGeometry';
import { filterSafeFieldPads, isFieldTurretPlacementSafe } from '../sim/PlacementRules';
import { VfxSystem } from '../sim/Vfx';
import { getTheme, type MapTheme } from '../config/themes';
import { Enemy, nextEntityId } from '../entities/Enemy';
import { formatVeterancy, Tower, type TowerBonuses } from '../entities/Tower';
import { ProjectilePool } from '../entities/Projectile';
import { Barrier, Drone, placeBarrierOnPath } from '../entities/Support';
import { TowerMenu } from '../ui/TowerMenu';
import { FieldManualPanel } from '../ui/FieldManualPanel';
import { addAudioToggleButtons, addFullscreenIconButton, addRetroButtonHover, drawRetroPanel, drawScanlines } from '../ui/Retro';
import { comboKey, comboVoiceId, SYMBIOSIS_COMBO_NAMES, TOP_TIER_CALLOUTS, topTierVoiceId } from '../data/voiceCallouts';

interface PadView {
  id: number;
  x: number;
  y: number;
  gfx: Phaser.GameObjects.Graphics;
  occupied: boolean;
  towerId: number | null;
  kind: 'field' | 'base';
}

interface LootDrop {
  id: number;
  x: number;
  y: number;
  amount: number;
  kind: 'supply' | 'research';
  source?: 'boss' | 'elite' | 'treasure';
  life: number;
  bob: number;
}

type TowerVisual = {
  body: Phaser.GameObjects.Image;
  weapon?: Phaser.GameObjects.Image;
};

type PortalVisual = { sprite: Phaser.GameObjects.Image; base: boolean };

interface LootFlight {
  sx: number;
  sy: number;
  amount: number;
  kind: 'supply' | 'research';
  life: number;
  maxLife: number;
}

interface TreasureSite {
  id: number;
  padId: number;
  x: number;
  y: number;
  amount: number;
  kind: LootDrop['kind'];
  claimed: boolean;
}

interface RollingBombState {
  id: number;
  routeIndex: number;
  travel: number;
  speed: number;
  x: number;
  y: number;
  angle: number;
  mode: 'explosive' | 'snow';
  active: boolean;
}

interface EnemyShot {
  id: number;
  x: number;
  y: number;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  towerId: number;
  age: number;
  life: number;
  colour: number;
}

interface WeatherDrop {
  x: number;
  y: number;
  speed: number;
  length: number;
  drift: number;
  alpha: number;
}

type PlaceMode = 'none' | 'build' | 'airstrike' | 'barrier' | 'special-artillery' | 'special-energy';

interface TerrainPoint {
  x: number;
  y: number;
}

type SwarmDronePhase = 'docked' | 'sortie' | 'returning';
type SwarmDroneWeapon = 'machine-gun' | 'bomb' | 'micro-missile';

interface SwarmDroneState {
  id: number;
  x: number;
  y: number;
  orbit: number;
  phase: SwarmDronePhase;
  weapon: SwarmDroneWeapon;
  targetId: number | null;
  fireCooldown: number;
  sortieTime: number;
  fireFlash: number;
  angle: number;
  cohesion: boolean;
}

function nearbyWingmatePositions(drone: SwarmDroneState, candidates: SwarmDroneState[]): SwarmDroneState[] {
  return candidates.filter((mate) => Phaser.Math.Distance.Between(drone.x, drone.y, mate.x, mate.y) <= 150);
}

function pointInPolygon(point: TerrainPoint, polygon: TerrainPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const crosses = a.y > point.y !== b.y > point.y;
    if (crosses && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || 1) + a.x) inside = !inside;
  }
  return inside;
}

function orientation(a: TerrainPoint, b: TerrainPoint, c: TerrainPoint): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsIntersect(a: TerrainPoint, b: TerrainPoint, c: TerrainPoint, d: TerrainPoint): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  const eps = 0.001;
  return abC * abD <= eps && cdA * cdB <= eps;
}

function segmentHitsPolygon(a: TerrainPoint, b: TerrainPoint, polygon: TerrainPoint[]): boolean {
  if (pointInPolygon(a, polygon) || pointInPolygon(b, polygon)) return true;
  for (let i = 0; i < polygon.length; i++) {
    if (segmentsIntersect(a, b, polygon[i], polygon[(i + 1) % polygon.length])) return true;
  }
  return false;
}

export class BattleScene extends Phaser.Scene {
  private static readonly SIM_SPEEDS = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5] as const;
  private static readonly DECAL_LIFETIME_SEC = 30;
  private static readonly MAX_WAVE_RUSHES = 2;
  private static readonly WAVE_CONSOLE_X = 600;
  private static readonly WAVE_CONSOLE_W = 360;
  private static readonly ABILITY_CONSOLE_GAP = 10;
  private static readonly ABILITY_FIRST_INSET = 48;
  private static readonly ABILITY_STEP = 72;
  private static readonly KILL_STREAK_STEP = 5;
  private static readonly KILL_STREAK_BASE_REWARD = 5;
  private static readonly KILL_STREAK_MAX_REWARD = 15;

  /** How long the auto-advance clock runs before the next wave calls itself. */
  private static readonly AUTO_WAVE_DELAY_MS = 6000;
  private static readonly BASE_UPGRADE_COST = 180;

  private static formatSpeed(v: number): string {
    return Number.isInteger(v) ? `${v.toFixed(1)}×` : `${v}×`;
  }

  private missionId = 'kappa';
  private difficulty: 'normal' | 'hard' | 'nightmare' = 'normal';
  private mapDef: MapDef = getMissionMap('kappa');
  private customMapDef?: MapDef;
  private customBackgroundDataUrl?: string;
  private waveList: WaveDef[] = getMissionWaves('kappa');
  private theme: MapTheme = getTheme('kappa');
  private ambientT = 0;
  private hitstop = 0;
  private damageNums: { x: number; y: number; vy: number; life: number; text: string; colour: string; size: number; obj?: Phaser.GameObjects.Text }[] = [];
  private towerSprites = new Map<number, TowerVisual>();
  private enemySprites = new Map<number, Phaser.GameObjects.Image>();
  private bossTextureAliases = new Map<string, string>();
  private swarmDroneSprites = new Map<number, Phaser.GameObjects.Image>();
  private cinematic = 0;
  private cinematicCol = 0xffffff;
  private camZoom = 1;
  private camFocusX = BATTLE_W / 2;
  private camFocusY = (UI_TOP_H + MAP_FIELD_BOTTOM) / 2;
  private uiCam?: Phaser.Cameras.Scene2D.Camera;
  private hudScale = 1;
  private worldObjs: Phaser.GameObjects.GameObject[] = [];
  private path!: PathCache;
  private routeCaches: PathCache[] = [];
  private pads: PadView[] = [];
  private enemies: Enemy[] = [];
  private towers: Tower[] = [];
  private swarmDrones = new Map<number, SwarmDroneState[]>();
  private swarmDroneCounts = new Map<number, number>();
  private barriers: Barrier[] = [];
  private drone!: Drone;
  private projectiles!: ProjectilePool;
  private rollingBombs: RollingBombState[] = [];
  private rollingBombQueue: { routeIndex: number; delay: number }[] = [];
  private rollingBombSprites = new Map<number, Phaser.GameObjects.Image>();
  private rollingBombMode: 'explosive' | 'snow' = 'explosive';
  private enemyShots: EnemyShot[] = [];
  /** Global spacing keeps counter-fire readable when several ranged units spawn together. */
  private enemyShotClock = 0;

  private supply = 0;
  private integrity = 0;
  private maxIntegrity = 20;
  private score = 0;
  private waveIndex = 0;
  private waveActive = false;
  private spawnQueue: { def: EnemyDef; t: number; lane: number; formationSlot?: number; formationSize?: number }[] = [];
  private spawnClock = 0;
  private allWavesDone = false;
  private sawFirstBlood = false;
  private perfectSaid = 0;
  private killTimes: number[] = [];
  private killStreak = 0;
  private waveStartIntegrity = 0;
  private integrityBand = 0;
  private gameOver = false;
  private autoAdvance = false;
  private autoTimer: Phaser.Time.TimerEvent | null = null;

  private hpMul = 1;
  private speedMul = 1;
  private bountyMul = 1;
  private devCreditMul = 1;
  private devSquadSize = 1;
  private survival = false;
  private survivalTime = 0;
  private realElapsed = 0;
  private simElapsed = 0;
  private survivalHpScale = 1;
  private gameSpeed = 1;
  private waveHostileBudget = 0;
  /** Number of voluntary overlapping calls made during the active combat set. */
  private rushesUsed = 0;
  private autosaveClock = 0;
  private resumeRun?: ActiveRunSnapshot;
  private researchPointsAtSortieStart = 0;
  private baseLevel = 0;
  private research!: ResearchBonuses;
  private rankPerks!: RankPerkBonuses;

  private placeMode: PlaceMode = 'none';
  private pendingTowerId = 'ballistic';
  private selectedTower: Tower | null = null;
  private selectedEnemy: Enemy | null = null;
  private baseSelected = false;

  private abilityCd: Record<string, number> = { airstrike: 0, barrier: 0, rollingdeath: 0, emp: 0 };
  private abilityReadyFlash: Record<string, number> = {};

  // Graphics layers
  private gTerrain!: Phaser.GameObjects.Graphics;
  private gPath!: Phaser.GameObjects.Graphics;
  private gElevation!: Phaser.GameObjects.Graphics;
  private gPads!: Phaser.GameObjects.Graphics;
  private gFx!: Phaser.GameObjects.Graphics;
  private gMuzzle!: Phaser.GameObjects.Graphics;
  private gEntities!: Phaser.GameObjects.Graphics;
  private gOverlay!: Phaser.GameObjects.Graphics;
  private gHud!: Phaser.GameObjects.Graphics;
  private gHudMotion!: Phaser.GameObjects.Graphics;
  private gHudScanlines!: Phaser.GameObjects.Graphics;
  private gAtmo!: Phaser.GameObjects.Graphics;
  private gWeather!: Phaser.GameObjects.Graphics;
  private gZones!: Phaser.GameObjects.Graphics;
  private commandBaseSprite?: Phaser.GameObjects.Image;
  private portalSprites: PortalVisual[] = [];
  private portalPulse = 0;
  private buildGhost: Phaser.GameObjects.Image | null = null;

  // HUD objects
  private txtSupply!: Phaser.GameObjects.Text;
  private txtWave!: Phaser.GameObjects.Text;
  private txtIntegrity!: Phaser.GameObjects.Text;
  private txtKillStreak!: Phaser.GameObjects.Text;
  private txtScore!: Phaser.GameObjects.Text;
  private txtEnemies!: Phaser.GameObjects.Text;
  private txtGameSpeed!: Phaser.GameObjects.Text;
  private txtMsg!: Phaser.GameObjects.Text;
  private txtBanner!: Phaser.GameObjects.Text;
  private txtMusic!: Phaser.GameObjects.Text;
  private achievementToast?: Phaser.GameObjects.Container;
  private currentTrackLabel = '';
  private bannerT = 0;
  private txtPanel!: Phaser.GameObjects.Text;
  private txtTowerList!: Phaser.GameObjects.Text;
  private txtDrone!: Phaser.GameObjects.Text;
  private abilityTexts: Record<string, Phaser.GameObjects.Text> = {};
  private abilityBars: Record<string, Phaser.GameObjects.Graphics> = {};
  private abilityGlyphs: Record<string, Phaser.GameObjects.Text> = {};
  private abilityIcons: Record<string, Phaser.GameObjects.Image> = {};
  private towerBtnTexts: Phaser.GameObjects.Text[] = [];
  private buildBtnInfo: { tid: string; label: Phaser.GameObjects.Text; costTxt: Phaser.GameObjects.Text; cost: number }[] = [];
  private buildTooltip?: Phaser.GameObjects.Container;
  private abilityTooltip?: Phaser.GameObjects.Container;
  private baseTooltip?: Phaser.GameObjects.Container;
  private baseContextMenu?: Phaser.GameObjects.Container;
  private settingsTooltip?: Phaser.GameObjects.Container;
  private missionExitWarning?: Phaser.GameObjects.Container;
  private missionExitOpen = false;

  private msgTimer = 0;
  private achievementToastTimer = 0;
  private shake = 0;
  private vfx = new VfxSystem();
  private decals: { x: number; y: number; r: number; a: number; scorch?: boolean; life: number }[] = [];
  private markers: { x: number; y: number; t: number; kind: string; max?: number; radius?: number }[] = [];
  private loot: LootDrop[] = [];
  private lootFlights: LootFlight[] = [];
  private treasureSites: TreasureSite[] = [];
  private bossResearchDrops = 0;

  private earlyCallBtn!: Phaser.GameObjects.Text;
  private waveControl!: Phaser.GameObjects.Graphics;
  private waveControlHit!: Phaser.GameObjects.Rectangle;
  private waveControlHint!: Phaser.GameObjects.Text;
  private txtWavePreview?: Phaser.GameObjects.Text;
  private wavePreviewButtons: Phaser.GameObjects.Rectangle[] = [];
  private wavePreviewIcons: Phaser.GameObjects.Image[] = [];
  private wavePreviewCounts: Phaser.GameObjects.Text[] = [];
  private wavePreviewHit!: Phaser.GameObjects.Rectangle;
  private wavePreviewToggle!: Phaser.GameObjects.Text;
  private wavePreviewExpanded = true;
  private wavePreviewItemCount = 0;
  private waveControlHover = false;
  private waveControlPressed = false;
  private waveControlState = '';
  private wavePreviewState = '';
  private wavePrompt!: Phaser.GameObjects.Container;
  private gWavePrompt!: Phaser.GameObjects.Graphics;
  private txtWavePrompt!: Phaser.GameObjects.Text;
  private txtWaveSub!: Phaser.GameObjects.Text;
  private nextWaveBtn!: Phaser.GameObjects.Text;
  private panelBtns: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text; action: () => void }[] = [];
  private baseUpgradeBtn?: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private towerMenu!: TowerMenu;
  private fieldManual!: FieldManualPanel;

  private cable: Phaser.Curves.Path | null = null;
  private reducedMotion = false;
  private weatherDrops: WeatherDrop[] = [];

  private get environment(): MapEnvironment {
    return this.mapDef.environment ?? {
      terrain: this.theme.ambient === 'snow' ? 'snow' : this.theme.ambient === 'ember' ? 'lava' : 'rock',
      weather: this.theme.ambient === 'snow' ? 'snow' : this.theme.ambient === 'ember' ? 'meteors' : 'clear',
      intensity: 0.35,
    };
  }

  constructor() {
    super('Battle');
  }

  init(data: { difficulty?: 'normal' | 'hard' | 'nightmare'; survival?: boolean; missionId?: string; customMap?: MapDef; customBackgroundDataUrl?: string; resume?: boolean } = {}): void {
    const savedRun = data.resume ? saveManager.data.activeRun : undefined;
    this.resumeRun = savedRun;
    const diff = savedRun?.difficulty ?? data.difficulty ?? 'normal';
    this.difficulty = diff;
    this.missionId = savedRun?.missionId ?? data.missionId ?? 'kappa';
    this.customMapDef = data.customMap;
    this.customBackgroundDataUrl = data.customBackgroundDataUrl;
    this.mapDef = this.customMapDef ?? getMissionMap(this.missionId);
    this.waveList = getMissionWaves(this.missionId);
    this.theme = getTheme(this.missionId);
    this.survival = savedRun?.survival ?? !!data.survival;
    this.survivalTime = 0;
    this.realElapsed = 0;
    this.simElapsed = 0;
    this.survivalHpScale = 1;
    this.gameSpeed = 1;
    this.researchPointsAtSortieStart = saveManager.data.researchPoints;
    if (!savedRun) saveManager.clearActiveRun();
    // Developer sorties start with the Bastion hardpoints online so every
    // tower mount and upgrade path can be exercised immediately.
    this.baseLevel = saveManager.data.settings.devMode ? 1 : 0;
    if (diff === 'hard') {
      this.hpMul = 2.5;
      this.speedMul = 1.08;
      this.bountyMul = 1.1;
    } else if (diff === 'nightmare') {
      this.hpMul = 4;
      this.speedMul = 1.15;
      this.bountyMul = 1.2;
    } else {
      this.hpMul = 1.75 * NORMAL_ENEMY_HEALTH_MULTIPLIER;
      this.speedMul = 1;
      this.bountyMul = 1;
    }
  }

  preload(): void {
    // User-authored plates are supplied as either data URLs (local uploads) or
    // stable asset paths (published/generated plates). Load URL plates through
    // Phaser's loader before create() builds the world so play-test and live
    // combat share the exact same artwork frame.
    const url = this.customBackgroundDataUrl;
    if (url) {
      const key = `custom-map-${this.mapDef.id}-bg`;
      if (!this.textures.exists(key)) this.load.image(key, url);
    }
  }

  create(): void {
    bus.clear();
    // Phaser reuses a scene instance after `stop` / `start`. Reset every
    // collection that holds display objects before rebuilding the HUD; leaving
    // old labels in these arrays caused a second deployment to update already
    // destroyed WebGL textures.
    this.pads = [];
    this.worldObjs = [];
    this.towerBtnTexts = [];
    this.buildBtnInfo = [];
    this.panelBtns = [];
    this.wavePreviewButtons = [];
    this.wavePreviewIcons = [];
    this.wavePreviewCounts = [];
    this.wavePreviewItemCount = 0;
    this.abilityTexts = {};
    this.abilityBars = {};
    this.abilityGlyphs = {};
    this.abilityIcons = {};
    this.baseUpgradeBtn = undefined;
    this.buildTooltip = undefined;
    this.abilityTooltip = undefined;
    this.baseTooltip = undefined;
    this.baseContextMenu = undefined;
    this.settingsTooltip = undefined;
    this.missionExitWarning = undefined;
    this.missionExitOpen = false;
    this.achievementToast = undefined;
    this.achievementToastTimer = 0;
    this.research = researchManager.bonuses();
    this.rankPerks = getRankPerkBonuses(this.activeCampaignRank());
    const dev = saveManager.data.settings;
    this.devCreditMul = dev.devMode ? Phaser.Math.Clamp(dev.devCreditMultiplier ?? 1, 0.25, 3) : 1;
    this.devSquadSize = dev.devMode ? Phaser.Math.Clamp(Math.round(dev.devSquadSize ?? 1), 1, 4) : 1;
    if (dev.devMode) this.hpMul *= Phaser.Math.Clamp(dev.devEnemyHpMultiplier ?? 1, 0.5, 2.5);
    this.hudScale = Phaser.Math.Clamp(saveManager.data.settings.hudScale ?? 1, 0.85, 1.15);
    this.routeCaches = getMapRoutes(this.mapDef).map((route) => buildPathCache(route));
    this.path = this.routeCaches[0];
    const nightmareSupplyMul = this.difficulty === 'nightmare' ? NIGHTMARE_STARTING_SUPPLY_MULTIPLIER : 1;
    const defaultSupply = this.difficulty === 'normal' ? NORMAL_STARTING_SUPPLY : Math.floor(((this.mapDef.startSupply + STARTING_SUPPLY_BONUS) + this.research.startSupplyBonus + this.rankPerks.startSupplyBonus) * STARTING_SUPPLY_MULTIPLIER * nightmareSupplyMul);
    this.supply = dev.devMode && (dev.devStartingSupply ?? 0) > 0 ? Math.round(dev.devStartingSupply) : defaultSupply;
    const devIntegrityMul = dev.devMode ? Phaser.Math.Clamp(dev.devBaseIntegrityMultiplier ?? 1, 0.5, 5) : 1;
    this.maxIntegrity = Math.round(this.mapDef.command.hp * BASE_INTEGRITY_MULTIPLIER * devIntegrityMul)
      + this.research.integrityBonus + this.rankPerks.integrityBonus;
    this.integrity = this.maxIntegrity;
    this.score = 0;
    this.waveIndex = 0;
    this.waveActive = false;
    this.allWavesDone = false;
    this.waveHostileBudget = 0;
    this.rushesUsed = 0;
    this.sawFirstBlood = false;
    this.perfectSaid = 0;
    this.killTimes = [];
    this.killStreak = 0;
    this.integrityBand = 0;
    this.autoAdvance = false;
    this.autoTimer?.remove();
    this.autoTimer = null;
    this.gameOver = false;
    this.enemies = [];
    this.towers = [];
    this.swarmDrones.clear();
    this.swarmDroneCounts.clear();
    this.barriers = [];
    this.projectiles = new ProjectilePool(200);
    this.vfx.clear();
    this.decals = [];
    this.markers = [];
    this.loot = [];
    this.lootFlights = [];
    this.treasureSites = [];
    this.rollingBombs = [];
    this.rollingBombQueue = [];
    this.rollingBombMode = this.research.rollingBombSnowUnlocked ? 'snow' : 'explosive';
    this.enemyShots = [];
    this.enemyShotClock = 0;
    this.weatherDrops = [];
    this.bossResearchDrops = 0;
    this.abilityCd = { airstrike: 0, barrier: 0, rollingdeath: 0, emp: 0 };
    this.abilityReadyFlash = {};
    this.drone = new Drone(this.mapDef.command.x - 40, this.mapDef.command.y - 40);
    this.selectedTower = null;
    this.selectedEnemy = null;
    this.baseSelected = false;
    this.hideBaseTooltip();
    this.placeMode = 'none';
    this.hitstop = 0;
    this.damageNums = [];
    this.autosaveClock = 0;
    // Scene restarts can leave the previous sprite objects attached until the
    // display list is rebuilt. Destroy them before clearing the lookup maps so
    // an old boss texture cannot remain underneath the freshly spawned one.
    for (const visual of this.towerSprites.values()) {
      visual.body.destroy();
      visual.weapon?.destroy();
    }
    this.towerSprites.clear();
    for (const portal of this.portalSprites) portal.sprite.destroy();
    this.portalSprites = [];
    this.portalPulse = 0;
    for (const sprite of this.enemySprites.values()) sprite.destroy();
    this.enemySprites.clear();
    this.bossTextureAliases.clear();
    for (const sprite of this.swarmDroneSprites.values()) sprite.destroy();
    this.swarmDroneSprites.clear();
    for (const sprite of this.rollingBombSprites.values()) sprite.destroy();
    this.rollingBombSprites.clear();
    this.buildGhost = null;
    this.bannerT = 0;
    this.currentTrackLabel = '';
    this.cinematic = 0;
    this.time.timeScale = this.gameSpeed;
    this.reducedMotion =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    applyAudioSettings();
    music.unlock();
    music.setMenuMode(false);
    music.setIntensity(0.25);
    // Every map begins on a different authored playlist cut, then cycles
    // through the remaining tracks without immediately repeating one.
    music.stopBattleOpening();
    music.playBattleOpening(this.mapDef.id);

    this.buildWorld();
    this.worldObjs = [...this.children.list];
    this.buildHud();
    this.buildMissionExitWarning();
    this.fieldManual = new FieldManualPanel(this, 130);
    applyUiTextScale(this);
    this.setupCameras();
    if (this.resumeRun) this.restoreActiveRun(this.resumeRun);
    rememberScene('Battle');
    const pd = saveManager.data.settings.particles;
    this.vfx.density = pd === 'low' ? 0.45 : pd === 'medium' ? 0.7 : 1;
    this.time.delayedCall(700, () => {
      if (!voice.play(this, `mission_${this.missionId}`, { priority: true, cooldown: 0 })) {
        voice.playCategory(this, 'mission', { priority: true, cooldown: 0 });
      }
    });
    this.showMessage(
      this.survival
        ? `SURVIVAL — WAVE 0 · SPACE STARTS WAVE 1`
        : `${this.mapDef.name} — SPACE starts wave 1, then waves auto-advance. Build and hold.`,
    );
    this.cameras.main.fadeIn(350, 0, 0, 0);

    // Input
    this.input.mouse?.disableContextMenu();
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.keyboard?.on('keydown-Q', () => this.tryAbility('airstrike'));
    this.input.keyboard?.on('keydown-W', () => this.tryAbility('barrier'));
    this.input.keyboard?.on('keydown-E', () => this.tryAbility('rollingdeath'));
    this.input.keyboard?.on('keydown-R', () => this.tryAbility('emp'));
    this.input.keyboard?.on('keydown-SPACE', () => this.callNextWave());
    this.input.keyboard?.on('keydown-ESC', () => this.missionExitOpen ? this.hideMissionExitWarning() : this.openBattleSettings());
    this.input.keyboard?.on('keydown-ONE', () => this.selectBuild('ballistic'));
    this.input.keyboard?.on('keydown-TWO', () => this.selectBuild('thermal'));
    this.input.keyboard?.on('keydown-THREE', () => this.selectBuild('artillery'));
    this.input.keyboard?.on('keydown-FOUR', () => this.selectBuild('energy'));
    this.input.keyboard?.on('keydown-FIVE', () => this.selectBuild('vulcan'));
    this.input.keyboard?.on('keydown-SIX', () => this.selectBuild('sabre'));
    this.input.keyboard?.on('keydown-SEVEN', () => this.selectBuild('tesla'));
    this.input.keyboard?.on('keydown-EIGHT', () => this.selectBuild('cryo'));
    this.input.keyboard?.on('keydown-NINE', () => this.selectBuild('dronehive'));
    this.input.keyboard?.on('keydown-T', () => this.toggleRollingDeathMode());
    this.input.keyboard?.on('keydown-H', () => this.fieldManual?.toggle());
    this.input.keyboard?.on('keydown-Z', () => {
      // Reset the view.
      this.camZoom = 1;
      this.camFocusX = BATTLE_W / 2;
      this.camFocusY = (UI_TOP_H + MAP_FIELD_BOTTOM) / 2;
      audio.click();
      this.showMessage('VIEW RESET');
    });

    // The battlefield stays at a fixed readable scale. Mouse-wheel input is
    // intentionally not bound here so scrolling cannot zoom or shift the map.

    bus.on<{ id: string; name: string; desc: string }>('achievement', (def) => {
      if (!def) return;
      this.showMessage(`COMMENDATION — ${def.name}`);
      this.showAchievementToast(def);
      announceAchievement(this, def);
    });

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => bus.clear());
    if (typeof window !== 'undefined') {
      const onHudScale = (event: Event) => {
        const requested = Number((event as CustomEvent<number>).detail);
        if (!Number.isFinite(requested)) return;
        this.hudScale = Phaser.Math.Clamp(requested, 0.85, 1.15);
        saveManager.data.settings.hudScale = this.hudScale;
        saveManager.save();
        if (this.uiCam) {
          this.uiCam.setZoom(this.hudScale);
          this.uiCam.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);
        }
        this.showMessage(`HUD SCALE  ${Math.round(this.hudScale * 100)}%`);
      };
      window.addEventListener('apogee-hud-scale', onHudScale);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => window.removeEventListener('apogee-hud-scale', onHudScale));
    }
  }

  // ---------- WORLD ----------

  private buildWorld(): void {
    this.gTerrain = this.add.graphics().setDepth(0);
    this.gPath = this.add.graphics().setDepth(1);
    this.gElevation = this.add.graphics().setDepth(1.2);
    this.gZones = this.add.graphics().setDepth(1.5);
    // Pads sit above the environmental atmosphere so the two bastion roof
    // hardpoints remain readable on top of the generated building sprite.
    this.gPads = this.add.graphics().setDepth(7);
    this.gDecals = this.add.graphics().setDepth(3);
    this.gAtmo = this.add.graphics().setDepth(5);
    this.gWeather = this.add.graphics().setDepth(5.5);
    this.gEntities = this.add.graphics().setDepth(10);
    this.gFx = this.add.graphics().setDepth(20);
    this.gMuzzle = this.add.graphics().setDepth(25);
    this.gOverlay = this.add.graphics().setDepth(30);

    // Terrain
    const fieldBottom = MAP_FIELD_BOTTOM;
    this.gTerrain.fillStyle(COLORS.bg, 1);
    this.gTerrain.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    this.gTerrain.fillStyle(this.environmentTerrainColour(), 1);
    this.gTerrain.fillRect(0, UI_TOP_H, BATTLE_W, fieldBottom - UI_TOP_H);

    // Each theater gets a full environmental plate behind the gameplay-readable path.
    // The tactical route, pads, units, and VFX remain code-native layers above it.
    const backgroundKey = this.customBackgroundDataUrl ? `custom-map-${this.mapDef.id}-bg` : `battle-${this.missionId}-bg`;
    if (this.customBackgroundDataUrl && !this.textures.exists(backgroundKey)) {
      this.textures.addBase64(backgroundKey, this.customBackgroundDataUrl);
    }
    if (this.textures.exists(backgroundKey)) {
      // Keep the live plate on the exact authored frame used by Map Forge.
      // Route points are stored in this same screen-space coordinate system;
      // using a shared frame prevents the background and path from drifting
      // when the saved layout is replayed under the HUD camera.
      const bgOffset = this.mapDef.background;
      const plate = this.add.image(
        MAP_FRAME.x + MAP_FRAME.width / 2 + (bgOffset?.offsetX ?? 0),
        MAP_FRAME.y + MAP_FRAME.height / 2 + (bgOffset?.offsetY ?? 0),
        backgroundKey,
      );
      // Crop the decorative plate slightly, never the authored HUD. FIT keeps
      // the complete 16:9 interface visible on ultrawide browser embeds.
      const crop = Math.max(MAP_FRAME.width / plate.width, MAP_FRAME.height / plate.height) * 1.08;
      plate.setScale(crop * (bgOffset?.scale ?? 1)).setAlpha(0.78).setDepth(0.5);
    }

    // Grid
    if (saveManager.data.settings.grid) {
      this.gTerrain.lineStyle(1, this.theme.grid, 0.55);
      for (let x = 0; x < BATTLE_W; x += 40) {
        this.gTerrain.lineBetween(x, UI_TOP_H, x, fieldBottom);
      }
      for (let y = UI_TOP_H; y < fieldBottom; y += 40) {
        this.gTerrain.lineBetween(0, y, BATTLE_W, y);
      }
    }

    // Paths — a ground-plane ribbon rather than a uniform top-down stroke. The
    // route centreline remains unchanged, so travel, collisions and targeting
    // continue to use precisely the same authored coordinates.
    const routes = getMapRoutes(this.mapDef);
    const renderRoutes = routes.map((route) => smoothRoadPoints(route, 4));
    const roadHalfWidth = (y: number) => getRoadHalfWidth(y, UI_TOP_H, fieldBottom);
    const drawRibbon = (g: Phaser.GameObjects.Graphics, inset: number, colour: number, alpha: number, shadow = false) => {
      for (const pts of renderRoutes) {
        const ribbon = buildRoadRibbon(pts, roadHalfWidth, inset);
        const drop = shadow ? 4 : 0;
        g.fillStyle(colour, alpha);
        g.fillPoints(
          [...ribbon.left, ...ribbon.right.slice().reverse()].map((p) => new Phaser.Geom.Point(p.x, p.y + drop)),
          true,
        );
      }
    };

    // A restrained shoulder, compact asphalt surface and low directional
    // shadow make the road sit on the same oblique terrain plane as the plate.
    drawRibbon(this.gPath, 4, 0x000000, 0.24, true);
    drawRibbon(this.gPath, 2, this.theme.terrainAlt, 0.52);
    drawRibbon(this.gPath, -1.5, this.theme.path, 0.88);
    drawRibbon(this.gPath, -7, 0x1b242a, 0.26);
    // Minimal outer edge lighting: readable without turning the route neon.
    for (const pts of renderRoutes) {
      const ribbon = buildRoadRibbon(pts, roadHalfWidth);
      for (let i = 1; i < ribbon.left.length; i++) {
        const leftA = ribbon.left[i - 1];
        const leftB = ribbon.left[i];
        const rightA = ribbon.right[i - 1];
        const rightB = ribbon.right[i];
        this.gPath.lineStyle(1.5, this.theme.pathEdge, 0.7);
        this.gPath.lineBetween(leftA.x, leftA.y, leftB.x, leftB.y);
        this.gPath.lineStyle(1, 0x071015, 0.36);
        this.gPath.lineBetween(rightA.x, rightA.y, rightB.x, rightB.y);
      }
    }

    // Direction dashes along the centreline — bright enough to read the lane flow.
    const step = 92;
    for (const cache of this.routeCaches) {
      for (let d = step * 0.5; d < cache.total - 10; d += step) {
        const s = samplePath(cache, d);
        const s2 = samplePath(cache, Math.min(cache.total, d + 14));
        const ang = Math.atan2(s2.y - s.y, s2.x - s.x);
        const a1 = ang + 2.65;
        const a2 = ang - 2.65;
        this.gPath.lineStyle(2.2, 0xb8e0f2, 0.78);
        this.gPath.lineBetween(s.x, s.y, s.x + Math.cos(a1) * 8, s.y + Math.sin(a1) * 8);
        this.gPath.lineBetween(s.x, s.y, s.x + Math.cos(a2) * 8, s.y + Math.sin(a2) * 8);
        this.gPath.lineStyle(1, 0x5ec8ff, 0.35);
        this.gPath.lineBetween(s.x, s.y, s2.x, s2.y);
      }
    }

    // Entry marker — hostile breach portal (red). Distinct from the cyan bastion gate.
    const entries = this.mapDef.entries ?? [this.mapDef.entry];
    for (const entry of entries) {
      this.gTerrain.fillStyle(this.environment.weather === 'alien-assault' ? 0x241b38 : 0x3a2020, 1);
      this.gTerrain.fillCircle(entry.x, entry.y, 16);
      this.gTerrain.lineStyle(2.5, COLORS.danger, 1);
      this.gTerrain.strokeCircle(entry.x, entry.y, 16);
      this.addWarpGate(entry.x, entry.y, false);
      this.worldAdd(
        this.add.text(entry.x, entry.y - 28, 'SPAWN', {
          fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#ff8a7a', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(7),
      );
    }

    // Command centre
    this.drawCommandBase();
    const command = this.mapDef.command;
    this.addWarpGate(command.x - 34, command.y - 12, true);
    this.worldAdd(
      this.add.text(command.x, command.y - 52, 'DEFEND', {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#7dceff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(7),
    );
    const baseArtKey = this.textures.exists('command-bastion-v2') ? 'command-bastion-v2' : 'command-bastion-v1';
    if (this.textures.exists(baseArtKey)) {
      this.commandBaseSprite = this.worldAdd(
        this.add.image(command.x, command.y, baseArtKey)
          .setDisplaySize(120 * this.terrainDepthScale(command.x, command.y), 120 * this.terrainDepthScale(command.x, command.y))
          .setRotation(0)
          .setDepth(6)
          .setAlpha(0.96),
      );
    }

    // Industrial decorations (non-colliding)
    this.drawDecorations();

    // Pads
    // Never render a ground socket which cannot legally host its full turret
    // footprint. This protects authored maps and stale save/rebuild states.
    const fieldPads = filterSafeFieldPads(this.mapDef.pads, getMapRoutes(this.mapDef)).map((p) => ({
      id: p.id,
      x: p.x,
      y: p.y,
      gfx: this.add.graphics().setDepth(4),
      occupied: false,
      towerId: null,
      kind: 'field' as const,
    }));
    const basePadOffsets = this.getBaseHardpointCount() >= 3
      ? [{ x: -34, y: -34 }, { x: 34, y: -34 }, { x: 0, y: -68 }]
      : [{ x: -32, y: -34 }, { x: 32, y: -34 }];
    const basePads = basePadOffsets.map((offset, index) => ({
      id: 100 + index,
      x: command.x + offset.x,
      y: command.y + offset.y,
    })).map((p) => ({
      ...p,
      gfx: this.add.graphics().setDepth(4),
      occupied: false,
      towerId: null,
      kind: 'base' as const,
    }));
    this.pads = [...fieldPads, ...basePads];
    this.treasureSites = this.createTreasureSites();
    this.redrawPads();
    this.drawAtmosphere();
    const fieldTop = UI_TOP_H;
    // Keep the weather deterministic so screenshots and authored maps remain stable.
    for (let i = 0; i < 56; i++) {
      const seed = (i * 73 + this.missionId.length * 41) % 997;
      this.weatherDrops.push({
        x: (seed * 17) % BATTLE_W,
        y: fieldTop + ((seed * 29) % Math.max(1, fieldBottom - fieldTop)),
        speed: 150 + (seed % 90),
        length: 7 + (seed % 9),
        drift: -18 - (seed % 16),
        alpha: 0.12 + (seed % 12) / 100,
      });
    }
  }

  /** Assign hidden caches to a random selection of legal field pads. */
  private createTreasureSites(): TreasureSite[] {
    const pads = this.pads.filter((pad) => pad.kind === 'field');
    if (!pads.length) return [];

    // Resume with the exact assignments from the checkpoint. Without this,
    // a refresh would silently move the winning pads and invalidate the
    // player's previously discovered luck.
    const savedSites = this.resumeRun?.treasureSites;
    if (savedSites?.length) {
      return savedSites.flatMap((saved) => {
        const pad = pads.find((candidate) => candidate.id === saved.padId);
        return pad ? [{ ...saved, x: pad.x, y: pad.y, claimed: false }] : [];
      });
    }

    const shuffledPads = [...pads];
    for (let i = shuffledPads.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledPads[i], shuffledPads[j]] = [shuffledPads[j], shuffledPads[i]];
    }
    const count = Math.min(pads.length, Math.max(1, Math.min(5, Math.ceil(pads.length / 3))));
    const sites: TreasureSite[] = [];
    for (let index = 0; index < count; index++) {
      const pad = shuffledPads[index];
      const kind: LootDrop['kind'] = Math.random() < 0.2 ? 'research' : 'supply';
      sites.push({
        id: index,
        padId: pad.id,
        x: pad.x,
        y: pad.y,
        amount: kind === 'research' ? scaleDataReward(2) : Math.floor((30 + Math.random() * 42) * COIN_REWARD_MULTIPLIER),
        kind,
        claimed: false,
      });
    }
    return sites;
  }

  /**
   * Split rendering into two cameras: the main camera shows the battlefield,
   * while a full-screen UI camera renders the HUD so it never scales or drifts.
   */
  private setupCameras(): void {
    const fieldBottom = MAP_FIELD_BOTTOM;
    this.camZoom = 1;
    this.camFocusX = BATTLE_W / 2;
    this.camFocusY = (UI_TOP_H + fieldBottom) / 2;

    // Drop any camera left over from a previous run.
    while (this.cameras.cameras.length > 1) this.cameras.remove(this.cameras.cameras[1]);

    const hud: Phaser.GameObjects.GameObject[] = [];
    for (const o of this.children.list) {
      if (!this.worldObjs.includes(o)) hud.push(o);
    }

    this.uiCam = this.cameras.add(0, 0, GAME_WIDTH, GAME_HEIGHT);
    this.uiCam.setZoom(this.hudScale);
    this.uiCam.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.uiCam.ignore(this.worldObjs);
    this.cameras.main.ignore(hud);
    this.cameras.main.setViewport(0, UI_TOP_H, BATTLE_W, fieldBottom - UI_TOP_H);
    this.applyCamera();
  }

  private openBattleSettings(): void {
    this.placeMode = 'none';
    this.selectedTower = null;
    this.selectedEnemy = null;
    this.baseSelected = false;
    this.towerMenu?.close();
    this.settingsTooltip?.setVisible(false);
    this.scene.pause();
    this.scene.launch('Pause', { from: 'Battle' });
  }

  private buildMissionExitWarning(): void {
    const overlay = this.add.container(0, 0).setDepth(200).setVisible(false);
    const veil = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x02070c, 0.82).setOrigin(0).setInteractive();
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 520, 220, 0x0b1720, 1)
      .setStrokeStyle(2, COLORS.danger);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 78, 'ABANDON SORTIE?', {
      fontFamily: 'Consolas, monospace', fontSize: '24px', color: '#ff7474', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 28,
      'A checkpoint is saved automatically.\nContinue can restore this sortie, or open the mission board.', {
        fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#c8d4e0', align: 'center', lineSpacing: 7,
      }).setOrigin(0.5);
    overlay.add([veil, panel, title, body]);
    const makeButton = (x: number, label: string, colour: number, action: () => void) => {
      const bg = this.add.rectangle(x, GAME_HEIGHT / 2 + 64, 210, 38, 0x152028, 1)
        .setStrokeStyle(1.5, colour).setInteractive({ useHandCursor: true });
      addRetroButtonHover(bg);
      const text = this.add.text(x, GAME_HEIGHT / 2 + 64, label, {
        fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d8f6ff', align: 'center',
      }).setOrigin(0.5);
      bg.on('pointerover', () => bg.setFillStyle(0x1d3a49));
      bg.on('pointerout', () => bg.setFillStyle(0x152028));
      bg.on('pointerdown', action);
      overlay.add([bg, text]);
    };
    makeButton(GAME_WIDTH / 2 - 120, 'RETURN TO SORTIE', COLORS.accent, () => this.hideMissionExitWarning());
    makeButton(GAME_WIDTH / 2 + 120, 'ABANDON & OPEN MISSIONS', COLORS.danger, () => {
      this.hideMissionExitWarning();
      music.setMenuMode(true);
      navigateTo(this, 'Command');
    });
    this.missionExitWarning = overlay;
  }

  private showMissionExitWarning(): void {
    if (this.gameOver) return;
    this.saveActiveRun();
    this.placeMode = 'none';
    this.towerMenu?.close();
    this.hideBaseContextMenu();
    this.missionExitOpen = true;
    this.missionExitWarning?.setVisible(true);
  }

  private hideMissionExitWarning(): void {
    this.missionExitOpen = false;
    this.missionExitWarning?.setVisible(false);
  }

  /** Screen (page) → battlefield world coordinates for the zoomable main camera. */
  private screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const cam = this.cameras.main;
    return {
      x: cam.scrollX + (sx - cam.x) / cam.zoom,
      y: cam.scrollY + (sy - cam.y) / cam.zoom,
    };
  }

  /** Keep an object on the world camera (so the UI camera skips it). */
  private worldAdd<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    this.uiCam?.ignore(obj);
    return obj;
  }

  private applyCamera(): void {
    const cam = this.cameras.main;
    const fieldBottom = MAP_FIELD_BOTTOM;

    const zoom = this.camZoom * (this.cinematic > 0 ? 1 + this.cinematic * 0.08 : 1);
    cam.setZoom(zoom);

    const viewW = BATTLE_W / zoom;
    const viewH = (fieldBottom - UI_TOP_H) / zoom;
    let sx = this.camFocusX - viewW / 2;
    let sy = this.camFocusY - viewH / 2;
    sx = Phaser.Math.Clamp(sx, 0, Math.max(0, BATTLE_W - viewW));
    sy = Phaser.Math.Clamp(sy, UI_TOP_H, Math.max(UI_TOP_H, fieldBottom - viewH));
    this.camFocusX = sx + viewW / 2;
    this.camFocusY = sy + viewH / 2;

    const shake = this.shake > 0 && saveManager.data.settings.shake ? this.shake : 0;
    const shakeX = shake ? (Math.random() - 0.5) * shake * 0.5 : 0;
    const shakeY = shake ? (Math.random() - 0.5) * shake * 0.5 : 0;
    cam.setScroll(sx + shakeX, sy + shakeY);
  }

  private drawAtmosphere(): void {
    const g = this.gAtmo;
    const top = UI_TOP_H;
    const bottom = GAME_HEIGHT - UI_BOTTOM_H;
    const h = bottom - top;
    // Corner darkening unifies the photographic plate with the vector overlay.
    g.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.72, 0.72, 0, 0);
    g.fillRect(0, top, BATTLE_W, 118);
    g.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.76, 0.76);
    g.fillRect(0, bottom - 142, BATTLE_W, 142);
    g.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.55, 0, 0.55, 0);
    g.fillRect(0, top, 180, h);
    g.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0.5, 0, 0.5);
    g.fillRect(BATTLE_W - 180, top, 180, h);
    // Corner wedges hide the rectangular plate edge against the HUD chrome.
    g.fillStyle(0x000000, 0.16);
    g.fillTriangle(0, top, 220, top, 0, top + 150);
    g.fillTriangle(BATTLE_W, top, BATTLE_W - 220, top, BATTLE_W, top + 150);
    g.fillTriangle(0, bottom, 250, bottom, 0, bottom - 130);
    g.fillTriangle(BATTLE_W, bottom, BATTLE_W - 250, bottom, BATTLE_W, bottom - 130);
    // Faint scanlines for the retro command-console feel.
    if (saveManager.data.settings.scanlines) {
      g.lineStyle(1, 0x000000, 0.045);
      for (let y = top; y < bottom; y += 4) g.lineBetween(0, y, BATTLE_W, y);
    }
  }

  private environmentTerrainColour(): number {
    switch (this.environment.terrain) {
      case 'snow': return 0x273743;
      case 'lava': return 0x2b1714;
      case 'acid': return 0x17291f;
      default: return this.theme.terrain;
    }
  }

  private addWarpGate(x: number, y: number, base: boolean): void {
    if (!this.textures.exists('map-portal-warp-v1')) return;
    const depth = base ? 5.4 : 5.8;
    const sprite = this.worldAdd(this.add.image(x, y, 'map-portal-warp-v1'))
      .setDisplaySize(base ? 72 : 64, base ? 72 : 64)
      .setDepth(depth)
      // Spawn reads red/hostile; bastion gate reads cyan/defend.
      .setTint(base ? 0x5ec8ff : 0xff5a4a)
      .setAlpha(base ? 0.8 : 0.92)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.portalSprites.push({ sprite, base });
  }

  private animateWarpGates(dt: number): void {
    const weatherBoost = this.environment.weather === 'alien-assault' ? 1.25 : 1;
    this.portalPulse = Math.max(0, this.portalPulse - dt);
    for (const portal of this.portalSprites) {
      const pulse = Math.sin(this.time.now / 240 + (portal.base ? 1.4 : 0)) * 0.06;
      const surge = this.portalPulse > 0 ? Math.sin(this.time.now / 70) * 0.14 + this.portalPulse * 0.06 : 0;
      portal.sprite.rotation += dt * (portal.base ? -0.6 : 0.85) * weatherBoost;
      const size = (portal.base ? 66 : 58) * ((portal.base ? 0.84 : 0.74) + pulse + surge);
      portal.sprite.setDisplaySize(size, size);
      portal.sprite.setAlpha((portal.base ? 0.65 : 0.82) + Math.max(0, surge) * 0.7);
    }
  }

  /** Subtle mist and rain pass on its own layer, below units and combat VFX. */
  private drawWeather(dt: number): void {
    const g = this.gWeather;
    if (!g) return;
    g.clear();
    const top = UI_TOP_H;
    const bottom = MAP_FIELD_BOTTOM;
    const height = bottom - top;
    const now = this.time.now;
    const particleSetting = saveManager.data.settings.particles;
    const intensity = Phaser.Math.Clamp(this.environment.intensity, 0, 1);
    const count = Math.round((particleSetting === 'low' ? 20 : particleSetting === 'medium' ? 36 : 52) * (0.35 + intensity));
    const weather = this.environment.weather;

    for (let i = 0; i < 3; i++) {
      const drift = this.reducedMotion ? i * 0.7 : now / (9000 + i * 1800);
      const x = ((drift * 180 + i * 310) % (BATTLE_W + 360)) - 180;
      const y = top + height * (0.22 + i * 0.28);
      g.fillStyle(i === 1 ? 0x9fc8d8 : 0x748b99, this.reducedMotion ? 0.035 : 0.05);
      g.fillEllipse(x, y, 360, 78);
    }

    if (!this.reducedMotion && weather !== 'clear') {
      for (let i = 0; i < count; i++) {
        const drop = this.weatherDrops[i];
        drop.y += drop.speed * dt;
        drop.x += drop.drift * dt;
        if (drop.y > bottom + drop.length) {
          drop.y = top - drop.length;
          drop.x = (drop.x + BATTLE_W + 40) % (BATTLE_W + 40) - 20;
        }
        if (drop.x < -24) drop.x = BATTLE_W + 18;
        if (weather === 'meteors') {
          g.lineStyle(2.4, 0xff8b45, Math.min(0.8, drop.alpha * 2.8));
          g.lineBetween(drop.x, drop.y, drop.x + drop.drift * 0.9, drop.y + drop.length * 2.2);
          g.fillStyle(0xffd08a, Math.min(0.9, drop.alpha * 3));
          g.fillCircle(drop.x, drop.y, 2.2);
        } else {
          const colour = weather === 'acid-rain' ? 0x8dff77 : weather === 'snow' ? 0xd4f5ff : weather === 'alien-assault' ? 0xb86cff : 0xb8e2ef;
          g.lineStyle(weather === 'acid-rain' ? 1.4 : 1, colour, Math.min(0.72, drop.alpha * (0.8 + intensity)));
          g.lineBetween(drop.x, drop.y, drop.x + drop.drift * 0.035, drop.y + drop.length);
        }
      }
    }

    if (!this.reducedMotion && weather === 'orbital-lasers') {
      const pulse = (now / 900) % 1;
      for (let i = 0; i < Math.max(1, Math.round(2 + intensity * 3)); i++) {
        const x = 120 + ((i * 277 + Math.floor(now / 900) * 91) % Math.max(1, BATTLE_W - 160));
        const width = 3 + Math.sin(now / 110 + i) * 1.2;
        g.fillStyle(0x7eeaff, 0.06 + pulse * 0.08);
        g.fillRect(x - width * 3, top, width * 6, height);
        g.lineStyle(2, 0x8ff4ff, 0.25 + pulse * 0.35);
        g.lineBetween(x, top, x, bottom);
      }
    }
    if (!this.reducedMotion && weather === 'alien-assault') {
      const pulse = 0.2 + (Math.sin(now / 210) + 1) * 0.1;
      g.lineStyle(1.4, 0xc07cff, pulse);
      for (let i = 0; i < 3; i++) {
        const x = 180 + ((i * 310 + Math.floor(now / 1200) * 37) % Math.max(1, BATTLE_W - 260));
        const y = top + 70 + i * 150;
        g.strokeCircle(x, y, 22 + Math.sin(now / 180 + i) * 5);
        g.lineBetween(x - 32, y, x + 32, y);
      }
    }
  }

  /**
   * Isometric depth scale: objects at the far/top edge visibly recede while
   * near/bottom objects gain enough size to feel close to the commander.
   * UI remains on its independent camera and is deliberately not affected.
   */
  private terrainDepthScale(x: number, y: number): number {
    const depth = this.terrainDepthValue(x, y);
    const far = Phaser.Math.Clamp(this.mapDef.perspective?.farScale ?? 0.68, 0.45, 0.95);
    const near = Phaser.Math.Clamp(this.mapDef.perspective?.nearScale ?? 1.1, 0.9, 1.45);
    return Phaser.Math.Clamp(Phaser.Math.Linear(far, near, depth), 0.45, 1.45);
  }

  /** Normalized ground-plane position used for scale and painter's depth. */
  private terrainDepthValue(x: number, y: number): number {
    const fieldTop = UI_TOP_H;
    const fieldBottom = MAP_FIELD_BOTTOM;
    let depth = Phaser.Math.Clamp((y - fieldTop) / Math.max(1, fieldBottom - fieldTop), 0, 1);
    const corners = this.mapDef.perspective?.corners;
    if (corners) {
      const [tl, tr, br, bl] = corners;
      const u = Phaser.Math.Clamp(x / Math.max(1, BATTLE_W), 0, 1);
      const topY = Phaser.Math.Linear(tl.y, tr.y, u);
      const bottomY = Phaser.Math.Linear(bl.y, br.y, u);
      depth = Phaser.Math.Clamp((y - topY) / Math.max(1, bottomY - topY), 0, 1);
    }
    return depth;
  }

  /** Keep nearer tower footprints above farther ones when they overlap. */
  private towerWorldDepth(x: number, y: number): number {
    return 11 + this.terrainDepthValue(x, y) * 0.8;
  }

  /** The mounting plane owns both foreshortening and its screen rotation. */
  private terrainPlane(_x?: number, y?: number): { depthRatio: number; rotation: number } {
    const plane = this.mapDef.perspective;
    let rotation = plane?.rotation ?? 0;
    const corners = plane?.corners;
    if (corners) {
      // Match the editor's four-corner plane. Rings follow the local horizon
      // of the authored battlefield instead of a global flat ellipse.
      const row = Phaser.Math.Clamp(((y ?? (UI_TOP_H + MAP_FIELD_BOTTOM) * 0.5) - UI_TOP_H) / Math.max(1, MAP_FIELD_BOTTOM - UI_TOP_H), 0, 1);
      const [tl, tr, br, bl] = corners;
      const left = { x: Phaser.Math.Linear(tl.x, bl.x, row), y: Phaser.Math.Linear(tl.y, bl.y, row) };
      const right = { x: Phaser.Math.Linear(tr.x, br.x, row), y: Phaser.Math.Linear(tr.y, br.y, row) };
      rotation = Math.atan2(right.y - left.y, right.x - left.x);
    }
    return {
      depthRatio: Phaser.Math.Clamp(plane?.tilt ?? 0.766, 0.3, 0.95),
      rotation,
    };
  }

  /**
   * Scale the upright turret body by terrain depth. The authored plane's tilt
   * belongs to ground contact (rings and shadows), not the standing chassis;
   * squashing the whole sprite makes idle turrets look flattened and hides
   * the same detail that is visible while they are aiming. The base ellipse is
   * aligned to the plane by rotation instead.
   */
  private towerDisplayScale(t: Tower, baseScale: number): { x: number; y: number } {
    const depthScale = this.terrainDepthScale(t.x, t.y);
    return {
      x: baseScale * depthScale,
      y: baseScale * depthScale,
    };
  }

  private traceGroundEllipse(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radiusX: number,
    radiusY: number,
    rotation: number,
  ): void {
    g.beginPath();
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      const ex = Math.cos(a) * radiusX;
      const ey = Math.sin(a) * radiusY;
      const px = x + ex * Math.cos(rotation) - ey * Math.sin(rotation);
      const py = y + ex * Math.sin(rotation) + ey * Math.cos(rotation);
      if (i === 0) g.moveTo(px, py);
      else g.lineTo(px, py);
    }
    g.closePath();
  }

  /**
   * Low-flying effects stay on the flat tactical surface.  When their motion
   * segment crosses a raised terrain polygon, they are retired before the
   * next frame so shots cannot visibly tunnel through foreground cover.
   */
  private terrainBlocksSegment(a: TerrainPoint, b: TerrainPoint): boolean {
    const mask = this.mapDef.terrainMask;
    if (!mask) return false;
    if (!pointInPolygon(b, mask.bounds)) return true;
    for (const blocker of mask.blockers) {
      // Projectiles spawned from a hardpoint inside a blocker are allowed to
      // leave it; only an external shot entering raised terrain is occluded.
      if (!pointInPolygon(a, blocker) && segmentHitsPolygon(a, b, blocker)) return true;
    }
    return false;
  }

  private gDecals!: Phaser.GameObjects.Graphics;

  private drawDecorations(): void {
    const g = this.gTerrain;
    // Elevated shelves remain in the map definition and terrain mask for
    // projectile occlusion, but their authoring overlay belongs in Map Forge
    // only. Gameplay should show the photographic plate without red terrain
    // polygons competing with the tactical route.
    const props: { x: number; y: number; w: number; h: number; kind: 'pipe' | 'tank' | 'crate' | 'vent' }[] = [
      { x: 80, y: 120, w: 36, h: 18, kind: 'crate' },
      { x: 200, y: 460, w: 48, h: 20, kind: 'pipe' },
      { x: 400, y: 480, w: 28, h: 28, kind: 'tank' },
      { x: 640, y: 120, w: 40, h: 16, kind: 'vent' },
      { x: 780, y: 440, w: 36, h: 18, kind: 'crate' },
      { x: 140, y: 120, w: 22, h: 22, kind: 'tank' },
      { x: 880, y: 160, w: 30, h: 14, kind: 'pipe' },
    ];
    for (const p of props) {
      g.fillStyle(this.theme.prop, 1);
      g.fillRect(p.x, p.y, p.w, p.h);
      g.lineStyle(1, this.theme.propEdge, 1);
      g.strokeRect(p.x, p.y, p.w, p.h);
      if (p.kind === 'tank') {
        g.lineStyle(1, this.theme.propEdge, 1);
        g.strokeCircle(p.x + p.w / 2, p.y + p.h / 2, p.w * 0.35);
      }
      if (p.kind === 'vent') {
        g.fillStyle(this.theme.terrainAlt, 1);
        g.fillRect(p.x + 4, p.y + 4, p.w - 8, 3);
        g.fillRect(p.x + 4, p.y + 10, p.w - 8, 3);
      }
    }

    // Commander-authored props sit above the photographic terrain and below
    // towers/enemies. Their height and obstruction metadata are preserved so
    // the future line-of-sight pass can use the same authored objects.
    for (const prop of this.mapDef.props ?? []) {
      const key = MAP_PROP_TEXTURES[prop.kind];
      if (!this.textures.exists(key)) continue;
      const frame = MAP_PROP_FRAMES[prop.kind];
      const frameData = frame != null ? this.textures.get(key).get(frame) : undefined;
      const source = (frameData ? { width: frameData.cutWidth, height: frameData.cutHeight } : this.textures.get(key).getSourceImage()) as { width: number; height: number };
      const depth = 3.8 + prop.depth * 0.05 + this.terrainDepthValue(prop.x, prop.y) * 0.2;
      this.worldAdd(this.add.image(prop.x, prop.y, key, frame)
        .setDisplaySize(source.width * prop.scale * this.terrainDepthScale(prop.x, prop.y), source.height * prop.scale * this.terrainDepthScale(prop.x, prop.y))
        .setRotation(prop.rotation)
        .setDepth(depth)
        .setAlpha(0.94)
        .setData('mapPropKind', MAP_PROP_LABELS[prop.kind])
        .setData('blocksLineOfSight', prop.blocksLineOfSight));
    }
  }

  private drawCommandBase(): void {
    const c = this.mapDef.command;
    const col = this.theme.commandColour;
    const route = getMapRoutes(this.mapDef)[0];
    const before = route[Math.max(0, route.length - 2)] ?? c;
    const end = route[route.length - 1] ?? c;
    // Outward points from the command back along the final hostile approach.
    const len = Math.hypot(end.x - before.x, end.y - before.y) || 1;
    const ox = -(end.x - before.x) / len;
    const oy = -(end.y - before.y) / len;
    // Turn the gate's short vestibule toward both the approach and the camera.
    // An exactly side-on facade collapses to a line on near-horizontal routes.
    const facingY = Math.max(0.55, oy);
    const facingLength = Math.hypot(ox, facingY) || 1;
    const faceX = ox / facingLength;
    const faceY = facingY / facingLength;
    const nx = -faceY;
    const ny = faceX * 0.5;
    const baseScale = this.terrainDepthScale(c.x, c.y);
    const doorX = c.x + faceX * 43 * baseScale;
    const doorY = c.y + Math.max(34, faceY * 43) * baseScale;

    // Flattened arrival apron: its far edge meets the road and its near edge
    // sits beneath the upright bastion instead of pretending the building is a
    // rotatable vehicle sprite.
    const apron = this.worldAdd(this.add.graphics().setDepth(6.1));
    apron.fillStyle(0x0b1217, 0.7);
    apron.fillPoints([
      new Phaser.Geom.Point(c.x + ox * 62 + nx * 18, c.y + oy * 62 + ny * 18),
      new Phaser.Geom.Point(c.x + ox * 62 - nx * 18, c.y + oy * 62 - ny * 18),
      new Phaser.Geom.Point(doorX - nx * 15, doorY - ny * 15),
      new Phaser.Geom.Point(doorX + nx * 15, doorY + ny * 15),
    ], true);
    apron.lineStyle(1, col, 0.18);
    apron.lineBetween(c.x + ox * 62 + nx * 18, c.y + oy * 62 + ny * 18, doorX + nx * 15, doorY + ny * 15);
    apron.lineBetween(c.x + ox * 62 - nx * 18, c.y + oy * 62 - ny * 18, doorX - nx * 15, doorY - ny * 15);

    // A door overlay remains legible even when the generated upright sprite is used.
    const detail = this.worldAdd(this.add.graphics().setDepth(6.2));
    const leftX = doorX + nx * 16;
    const leftY = doorY + ny * 16;
    const rightX = doorX - nx * 16;
    const rightY = doorY - ny * 16;
    const rise = 25;
    detail.fillStyle(0x050b0e, 0.94);
    detail.fillPoints([
      new Phaser.Geom.Point(leftX, leftY),
      new Phaser.Geom.Point(rightX, rightY),
      new Phaser.Geom.Point(rightX, rightY - rise),
      new Phaser.Geom.Point(leftX, leftY - rise),
    ], true);
    detail.lineStyle(4, 0x34424c, 1);
    detail.lineBetween(leftX, leftY, leftX, leftY - rise);
    detail.lineBetween(rightX, rightY, rightX, rightY - rise);
    detail.lineBetween(leftX, leftY - rise, rightX, rightY - rise);
    detail.lineStyle(1, col, 0.34);
    detail.lineBetween(leftX, leftY, leftX, leftY - rise);
    detail.lineBetween(rightX, rightY, rightX, rightY - rise);
    detail.lineBetween(leftX, leftY - rise, rightX, rightY - rise);
    detail.lineStyle(2, col, 0.32);
    detail.lineBetween(leftX, leftY, rightX, rightY);
    detail.lineStyle(1, 0xd8f6ff, 0.42);
    detail.lineBetween(doorX, doorY - 4, doorX, doorY - rise + 4);

    // The generated bastion is the authoritative base art. Keep the compact
    // vector fallback only for builds where the art asset is unavailable.
    if (this.textures.exists('command-bastion-v2') || this.textures.exists('command-bastion-v1')) return;
    this.gTerrain.fillStyle(0x152028, 1);
    this.gTerrain.fillRoundedRect(c.x - 36, c.y - 28, 72, 56, 8);
    this.gTerrain.lineStyle(2.5, col, 1);
    this.gTerrain.strokeRoundedRect(c.x - 36, c.y - 28, 72, 56, 8);
    this.gTerrain.fillStyle(0x0a1218, 1);
    this.gTerrain.fillRect(c.x - 18, c.y - 12, 36, 24);
    this.gTerrain.fillStyle(col, 0.95);
    this.gTerrain.fillRect(c.x - 8, c.y - 6, 16, 12);
    this.gTerrain.lineStyle(1, col, 0.5);
    this.gTerrain.lineBetween(c.x, c.y - 28, c.x, c.y - 40);
    this.gTerrain.strokeCircle(c.x, c.y - 44, 4);
  }

  private drawZones(t: number): void {
    const g = this.gZones;
    g.clear();
    const time = this.reducedMotion ? 0 : t;
    const entries = this.mapDef.entries ?? [this.mapDef.entry];
    entries.forEach((entry, lane) => {
      const route = this.mapDef.routes?.[lane] ?? this.mapDef.path;

    // ---- Breach portal (spawn) ----
    const ep = 0.5 + 0.5 * Math.sin(time / 300);
    const spin = time / 1200;
    g.fillStyle(0x3a0d12, 0.32);
    g.fillCircle(entry.x, entry.y, 36 + ep * 5);
    g.fillStyle(0xff4a4a, 0.07 + ep * 0.07);
    g.fillCircle(entry.x, entry.y, 26 + ep * 6);
    g.lineStyle(2, 0xff6a5a, 0.85);
    const segs = 12;
    for (let i = 0; i < segs; i++) {
      const a0 = spin + (i / segs) * Math.PI * 2;
      g.beginPath();
      g.arc(entry.x, entry.y, 21, a0, a0 + (Math.PI * 2 / segs) * 0.5);
      g.strokePath();
    }
    g.lineStyle(1.5, 0xffc0b0, 0.6);
    for (let i = 0; i < 6; i++) {
      const a0 = -spin * 1.7 + (i / 6) * Math.PI * 2;
      g.beginPath();
      g.arc(entry.x, entry.y, 12, a0, a0 + 0.6);
      g.strokePath();
    }
    g.fillStyle(0xffe4da, 0.75 + ep * 0.25);
    g.fillCircle(entry.x, entry.y, 3.5 + ep * 2);
    // Hazard chevrons streaming down the route.
    const next = route[1] ?? { x: entry.x + 40, y: entry.y };
    const dir = Math.atan2(next.y - entry.y, next.x - entry.x);
    for (let i = 0; i < 3; i++) {
      const d = 32 + i * 13 + ((time / 60) % 13);
      const cx = entry.x + Math.cos(dir) * d;
      const cy = entry.y + Math.sin(dir) * d;
      g.lineStyle(2, 0xff6a5a, Math.max(0.1, 0.5 - i * 0.12));
      g.lineBetween(cx + Math.cos(dir + 2.5) * 6, cy + Math.sin(dir + 2.5) * 6, cx, cy);
      g.lineBetween(cx + Math.cos(dir - 2.5) * 6, cy + Math.sin(dir - 2.5) * 6, cx, cy);
    }
    });

  }

  private redrawPads(): void {
    this.gPads.clear();
    const pointer = this.input.activePointer;
    const wp = this.screenToWorld(pointer.x, pointer.y);
    for (const pad of this.pads) {
      // Defensive redraw guard for injected/stale pads. Elevated base sockets
      // are deliberately exempt because they are bastion hardpoints.
      if (pad.kind === 'field' && !this.isFieldPadSafe(pad.x, pad.y)) continue;
      const R = (pad.kind === 'base' ? 14 : 21) * this.terrainDepthScale(pad.x, pad.y);
      const hover = Phaser.Math.Distance.Between(wp.x, wp.y, pad.x, pad.y) < 22;
      const lockedBase = pad.kind === 'base' && this.baseLevel < 1;
      const routeClear = pad.kind === 'base' || this.isFieldPadSafe(pad.x, pad.y);
      const buildable = this.placeMode === 'build' && !pad.occupied && !lockedBase && routeClear;
      const plane = this.terrainPlane(pad.x, pad.y);
      // Drop shadow
      this.gPads.fillStyle(0x000000, 0.38);
      this.gPads.fillEllipse(pad.x + 2, pad.y + 5, R * 2.1, R * 1.15);

      // A permanent, perspective-correct metal mounting ring is part of the
      // battlefield. It remains visible when empty and frames a built turret
      // without using a ghost sprite or an animated targeting reticle.
      const ringY = R * plane.depthRatio;
      const metal = lockedBase ? 0x27343b : pad.occupied ? 0x45545b : 0x64747a;
      this.gPads.fillStyle(0x10171c, 1);
      this.traceGroundEllipse(this.gPads, pad.x, pad.y, R, ringY, plane.rotation);
      this.gPads.fillPath();
      this.gPads.lineStyle(2.2, metal, 1);
      this.traceGroundEllipse(this.gPads, pad.x, pad.y, R, ringY, plane.rotation);
      this.gPads.strokePath();
      this.gPads.lineStyle(1, 0xb3c4c7, pad.occupied ? 0.38 : 0.62);
      this.traceGroundEllipse(this.gPads, pad.x - 1, pad.y - 1, R * 0.86, ringY * 0.8, plane.rotation);
      this.gPads.strokePath();
      this.gPads.lineStyle(1.2, 0x18242b, 0.95);
      this.traceGroundEllipse(this.gPads, pad.x, pad.y + 1, R * 0.7, ringY * 0.59, plane.rotation);
      this.gPads.strokePath();

      // Corner bolts and a dormant power core make every field pad read as a
      // deliberate tactical site even before the commander opens build mode.
      this.gPads.fillStyle(lockedBase ? 0x26313a : pad.occupied ? 0x22303a : 0x46606f, 0.9);
      for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + (i * Math.PI) / 2;
        this.gPads.fillCircle(pad.x + Math.cos(a) * (R - 3.5), pad.y + Math.sin(a) * (R - 3.5), 1.4);
      }

      if (lockedBase) {
        this.gPads.lineStyle(1.5, 0x65737c, 0.8);
        this.gPads.lineBetween(pad.x - 6, pad.y - 6, pad.x + 6, pad.y + 6);
        this.gPads.lineBetween(pad.x + 6, pad.y - 6, pad.x - 6, pad.y + 6);
      }

      // Legal sockets glow cyan in build mode; blocked ones glow red on hover.
      if (this.placeMode === 'build' && !pad.occupied && !lockedBase && routeClear) {
        this.gPads.lineStyle(hover ? 2.4 : 1.6, COLORS.accent, hover ? 0.95 : 0.72);
        this.gPads.strokeEllipse(pad.x, pad.y - 2, R * 1.35, ringY * 1.35);
      }
      if (this.placeMode === 'build' && hover && (pad.occupied || lockedBase || !routeClear)) {
        this.gPads.lineStyle(2.2, 0xff555a, 0.9);
        this.gPads.strokeEllipse(pad.x, pad.y - 2, R * 1.35, ringY * 1.35);
      }
      if (buildable && hover) {
        this.gPads.lineStyle(2.2, COLORS.accent, 0.95);
        this.traceGroundEllipse(this.gPads, pad.x, pad.y, R * 1.08, ringY * 1.08, plane.rotation);
        this.gPads.strokePath();
      }
    }
  }

  // ---------- HUD ----------

  private buildHud(): void {
    // These layers are created after worldObjs is captured so the UI camera owns
    // them exclusively and map zoom never scales the command console.
    this.gHud = this.add.graphics().setDepth(40);
    this.gHudMotion = this.add.graphics().setDepth(41);
    this.gHudScanlines = this.add.graphics().setDepth(42);
    const top = UI_TOP_H;
    // The legacy command rail is intentionally kept off-canvas so the map can
    // use the full width; tooltips, toasts and the manual replace its copy.
    const rightX = GAME_WIDTH + 24;
    const panelW = 280;

    const tacticalPanel = (x: number, y: number, w: number, h: number, bevel = 6, alpha = 0.85, edge = 0x48c3e6) => {
      const points = [
        new Phaser.Geom.Point(x + bevel, y), new Phaser.Geom.Point(x + w, y),
        new Phaser.Geom.Point(x + w, y + h - bevel), new Phaser.Geom.Point(x + w - bevel, y + h),
        new Phaser.Geom.Point(x, y + h), new Phaser.Geom.Point(x, y + bevel),
      ];
      const glassAlpha = Phaser.Math.Clamp(alpha * 0.78, 0.32, 0.8);
      this.gHud.fillStyle(0x0a1b2a, glassAlpha);
      this.gHud.fillPoints(points, true);
      this.gHud.lineStyle(1, edge, 0.72);
      this.gHud.strokePoints(points, true);
      // Two restrained highlight passes sell the panel as laminated glass:
      // a bright upper rim and a diagonal reflected sheen, while the darker
      // lower rim keeps text legible over the animated battlefield.
      this.gHud.lineStyle(1, 0xd9f8ff, 0.22);
      this.gHud.lineBetween(x + bevel + 2, y + 2, x + w - 3, y + 2);
      this.gHud.lineStyle(1, 0x07111b, 0.44);
      this.gHud.lineBetween(x + 4, y + h - 2, x + w - bevel - 2, y + h - 2);
      this.gHud.fillStyle(0xbdefff, 0.055);
      this.gHud.fillPoints([
        new Phaser.Geom.Point(x + bevel + 3, y + 4),
        new Phaser.Geom.Point(x + Math.min(w * 0.62, 88), y + 4),
        new Phaser.Geom.Point(x + Math.min(w * 0.48, 68), y + Math.min(h * 0.34, 22)),
        new Phaser.Geom.Point(x + bevel + 3, y + Math.min(h * 0.34, 22)),
      ], true);
    };

    // A dark translucent backer keeps the HUD readable while preserving the
    // battlefield texture underneath the glass treatment.
    this.gHud.fillStyle(0x050b10, 0.74);
    this.gHud.fillRect(0, 0, GAME_WIDTH, UI_TOP_H);
    // Premium command strip: short, unambiguous modules with enough room for
    // the XL text setting. Wave and score intentionally share one pod.
    tacticalPanel(16, 5, 174, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(198, 5, 150, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(356, 5, 150, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(514, 5, 300, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(822, 5, 208, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(1038, 5, 108, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(1154, 5, 36, 46, 8, 0.98, 0x48c3e6);
    tacticalPanel(1198, 5, 66, 46, 8, 0.98, 0x48c3e6);
    this.gHud.fillStyle(0x38d6f8, 0.72);
    this.gHud.fillRect(22, 50, 52, 1);

    // Bottom command deck is intentionally divided into a build rack, wave
    // control, and quick-cast rack rather than a continuous opaque bar.
    const deckY = GAME_HEIGHT - UI_BOTTOM_H;
    // The deck is a smoked-glass surface rather than an opaque slab, so the
    // map remains visually present behind the command modules.
    this.gHud.fillStyle(0x050b10, 0.76);
    this.gHud.fillRect(0, deckY, GAME_WIDTH, UI_BOTTOM_H);
    tacticalPanel(12, deckY + 8, 590, UI_BOTTOM_H - 16, 10, 0.92);
    // The wave command is one integrated glass console. Its wider upper cap
    // owns the full threat readout and leaves the rush details room to breathe.
    const waveConsoleX = BattleScene.WAVE_CONSOLE_X;
    const waveConsoleW = BattleScene.WAVE_CONSOLE_W;
    const abilityConsoleX = waveConsoleX + waveConsoleW + BattleScene.ABILITY_CONSOLE_GAP;
    const abilityConsoleW = GAME_WIDTH - abilityConsoleX - 12;
    // Keep the command console the same height as before, but lock its lower
    // edge to the command deck. The threat card is one uninterrupted surface
    // so its enemy buttons cannot straddle a background boundary.
    const waveConsoleY = deckY - 14;
    const threatCardY = waveConsoleY + 8;
    // Group the audio controls into the threat display rather than leaving two
    // floating button frames at its edge.
    const waveAudioX = waveConsoleX + waveConsoleW - 60;
    const waveAudioY = threatCardY + 16;
    const waveAudioTrayX = waveAudioX - 16;
    tacticalPanel(waveConsoleX, waveConsoleY, waveConsoleW, UI_BOTTOM_H + 14, 10, 0.94);
    this.gHud.fillStyle(0x12364a, 0.42).fillRoundedRect(waveConsoleX + 10, threatCardY, waveConsoleW - 20, 60, 7);
    this.gHud.lineStyle(1, 0x8eeaff, 0.28).strokeRoundedRect(waveConsoleX + 10, threatCardY, waveConsoleW - 20, 60, 7);
    this.gHud.lineStyle(1, 0xd9f8ff, 0.16).lineBetween(waveConsoleX + 20, threatCardY + 2, waveConsoleX + waveConsoleW - 20, threatCardY + 2);
    this.gHud.fillStyle(0x071722, 0.88).fillRoundedRect(waveAudioTrayX, waveAudioY - 15, 62, 30, 5);
    this.gHud.lineStyle(1, 0x3c8faa, 0.7).strokeRoundedRect(waveAudioTrayX, waveAudioY - 15, 62, 30, 5);
    this.gHud.lineStyle(1, 0x5ec8ff, 0.22).lineBetween(waveAudioX + 15, waveAudioY - 9, waveAudioX + 15, waveAudioY + 9);
    tacticalPanel(abilityConsoleX, deckY + 8, abilityConsoleW, UI_BOTTOM_H - 16, 10, 0.92);

    addAudioToggleButtons(this, waveAudioX, waveAudioY);

    // CRT texture is purposefully subtle so it sells the tactical treatment
    // without compromising target readability.
    this.gHudScanlines.fillStyle(0x000000, 0.10);
    for (let y = 0; y < GAME_HEIGHT; y += 3) this.gHudScanlines.fillRect(0, y, GAME_WIDTH, 1);

    // Retro vector chrome — corner brackets frame the tactical view.
    const bracket = 14;
    const corners: [number, number, number, number][] = [
      [0, top, 1, 1],
      [BATTLE_W, top, -1, 1],
      [0, GAME_HEIGHT - UI_BOTTOM_H, 1, -1],
      [BATTLE_W, GAME_HEIGHT - UI_BOTTOM_H, -1, -1],
    ];
    this.gHud.lineStyle(2, COLORS.accent, 0.45);
    for (const [cx, cy, sx, sy] of corners) {
      this.gHud.lineBetween(cx, cy, cx + bracket * sx, cy);
      this.gHud.lineBetween(cx, cy, cx, cy + bracket * sy);
    }
    this.gHud.lineStyle(1, COLORS.accent, 0.2);
    for (const [cx, cy, sx, sy] of corners) {
      this.gHud.lineBetween(cx + 6 * sx, cy, cx + (bracket + 6) * sx, cy);
      this.gHud.lineBetween(cx, cy + 6 * sy, cx, cy + (bracket + 6) * sy);
    }

    const mk = (x: number, y: number, size = 14, color = '#c8d4e0') =>
      this.add
        .text(x, y, '', { fontFamily: 'Consolas, Menlo, monospace', fontSize: `${size}px`, color })
        .setDepth(50);

    this.txtSupply = mk(28, 18, 13, '#ffb347');
    this.txtWave = mk(212, 10, 11, '#d8eef8');
    this.txtScore = mk(212, 29, 10, '#a8c8d8');
    this.txtEnemies = mk(370, 18, 11, '#f0c4d0');
    this.txtIntegrity = mk(532, 9, 12, '#ffffff');
    this.txtKillStreak = mk(370, 32, 9, '#ffd27a');
    // Always-on keybind strip — not buried in the lowest-contrast corner.
    this.add.text(28, GAME_HEIGHT - UI_BOTTOM_H + 4, 'KEYS  1–9 BUILD  ·  SPACE WAVE  ·  Q/W/E/R ABILITIES  ·  CLICK PAD TO PLACE  ·  H MANUAL  ·  ESC MENU', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8',
    }).setDepth(50);

    const speedButton = (x: number, label: string, delta: -1 | 1) => {
      const button = this.add
        .rectangle(x, top / 2, 28, 28, 0x152028, 1)
        .setStrokeStyle(1, 0x2a3e4a)
        .setInteractive({ useHandCursor: true })
        .setDepth(50);
      button.on('pointerover', () => button.setFillStyle(0x1a2830));
      button.on('pointerout', () => button.setFillStyle(0x152028));
      button.on('pointerdown', () => this.adjustGameSpeed(delta));
      this.add
        .text(x, top / 2, label, {
          fontFamily: 'Consolas, monospace',
          fontSize: '16px',
          color: '#9ad4e8',
        })
        .setOrigin(0.5)
        .setDepth(51);
    };
    speedButton(850, '−', -1);
    this.txtGameSpeed = this.add
      .text(926, top / 2, 'SIM 1.0×', {        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#9ad4e8',
      })
      .setOrigin(0.5)
      .setDepth(51);
    speedButton(1002, '+', 1);

    const missionLabel = this.add
      .text(1092, top / 2, 'MISSIONS', {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#9ad4e8',
      })
      .setOrigin(0.5)
      .setDepth(51);
    const back = this.add
      .rectangle(1092, top / 2, 112, 32, 0x152028, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(50);
    back.on('pointerover', () => missionLabel.setColor('#e0f4ff'));
    back.on('pointerout', () => missionLabel.setColor('#9ad4e8'));
    back.on('pointerdown', () => {
      this.showMissionExitWarning();
    });
    // A compact cog keeps settings discoverable during a sortie without
    // taking space away from the mission navigation or fullscreen control.
    const settingsGlyph = this.add
      .text(1172, top / 2 + 1, '⚙', {
        fontFamily: 'Segoe UI Symbol, Consolas, monospace',
        fontSize: '17px',
        color: '#9ad4e8',
      })
      .setOrigin(0.5)
      .setDepth(51);
    const settingsButton = this.add
      .rectangle(1172, top / 2, 36, 34, 0x152028, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(50);
    settingsButton.on('pointerover', () => {
      settingsGlyph.setColor('#e0f4ff');
      this.settingsTooltip?.setVisible(true);
    });
    settingsButton.on('pointerout', () => {
      settingsGlyph.setColor('#9ad4e8');
      this.settingsTooltip?.setVisible(false);
    });
    settingsButton.on('pointerdown', () => {
      audio.click();
      this.openBattleSettings();
    });
    const settingsTipBg = this.add.graphics().setDepth(70);
    settingsTipBg.fillStyle(0x071017, 0.92).fillRoundedRect(0, 0, 78, 24, 5);
    settingsTipBg.lineStyle(1, COLORS.accent, 0.85).strokeRoundedRect(0, 0, 78, 24, 5);
    const settingsTipText = this.add.text(39, 12, 'SETTINGS', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d7f6ff',
    }).setOrigin(0.5).setDepth(71);
    this.settingsTooltip = this.add.container(1134, top + 2, [settingsTipBg, settingsTipText])
      .setDepth(70)
      .setVisible(false);

    addFullscreenIconButton(this, 1231, top / 2);

    this.txtMsg = this.add
      .text(BATTLE_W / 2, top + 14, '', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '15px',
        color: '#cfeaff',
        align: 'center',
        backgroundColor: '#081018dd',
        padding: { x: 16, y: 7 },
        wordWrap: { width: 600, useAdvancedWrap: true },
        stroke: '#04121c',
        strokeThickness: 3,
        shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 8, fill: true },
      })
      .setOrigin(0.5, 0)
      .setDepth(50);

    this.txtBanner = this.add
      .text(BATTLE_W / 2, 176, '', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '30px',
        color: '#cfeaff',
        align: 'center',
        stroke: '#04121c',
        strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 3, color: '#000000', blur: 10, fill: true },
      })
      .setOrigin(0.5)
      .setDepth(55)
      .setAlpha(0);
    this.add
      .text(12, top + 8, this.mapDef.name, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#8fa8b8',
        backgroundColor: '#081018aa',
        padding: { x: 6, y: 3 },
        letterSpacing: 1,
      })
      .setDepth(50)
      .setVisible(false);
    this.txtMusic = this.add
      .text(12, top + 28, '', {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: '#d4a24a',
        backgroundColor: '#081018aa',
        padding: { x: 6, y: 2 },
        letterSpacing: 0.8,
      })
      .setDepth(50)
      .setVisible(false);
    this.refreshMusicLabel();

    const sectionLabel = (y: number, text: string) =>
      this.add
        .text(rightX + 4, y, text, {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: '#4a5560',
          letterSpacing: 1,
        })
        .setDepth(50);
    sectionLabel(64, 'ACTIVE TOWERS');
    sectionLabel(196, 'SELECTION');
    sectionLabel(492, 'WAVE CONTROL');

    this.txtTowerList = this.add
      .text(rightX + 4, 80, '', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '11px',
        color: '#c8d4e0',
        wordWrap: { width: panelW - 8 },
        lineSpacing: 3,
      })
      .setDepth(50);

    this.txtPanel = this.add
      .text(rightX + 4, 212, '', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '12px',
        color: '#9ad4e8',
        wordWrap: { width: panelW - 8 },
        lineSpacing: 2,
      })
      .setDepth(50);

    this.txtDrone = this.add
      .text(rightX + 4, 584, '', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '11px',
        color: '#7dce5a',
        wordWrap: { width: panelW - 8 },
        lineSpacing: 2,
      })
      .setDepth(50);

    // Selected-tower action buttons
    const panelBtnY = 358;
    const mkPanelBtn = (label: string, dy: number, action: () => void) => {
      const bg = this.add
        .rectangle(rightX + 8, panelBtnY + dy, panelW - 16, 26, 0x152028, 1)
        .setStrokeStyle(1, 0x2a3e4a)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true })
        .setDepth(50)
        .setVisible(false);
      const txt = this.add
        .text(rightX + panelW / 2, panelBtnY + dy + 13, label, {
          fontFamily: 'Consolas, monospace',
          fontSize: '12px',
          color: '#c8d4e0',
        })
        .setOrigin(0.5)
        .setDepth(51)
        .setVisible(false);
      bg.on('pointerdown', () => action());
      bg.on('pointerover', () => bg.setFillStyle(0x1a2830));
      bg.on('pointerout', () => bg.setFillStyle(0x152028));
      this.panelBtns.push({ bg, label: txt, action });
    };
    mkPanelBtn('UPGRADE [U]', 0, () => this.upgradeSelected());
    mkPanelBtn('PRIORITY [T]', 32, () => this.cyclePriority());
    mkPanelBtn('SPECIAL [S]', 64, () => {
      if (this.selectedTower) this.tryTowerSpecial(this.selectedTower);
    });
    mkPanelBtn('SELL [X]', 96, () => this.sellSelected());

    const baseBg = this.add
      .rectangle(rightX + 8, panelBtnY, panelW - 16, 26, 0x152028, 1)
      .setStrokeStyle(1, this.theme.commandColour)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(50)
      .setVisible(false);
    const baseLabel = this.add
      .text(rightX + panelW / 2, panelBtnY + 13, 'UPGRADE BASE  $180  [B]', {
        fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#c8d4e0',
      })
      .setOrigin(0.5)
      .setDepth(51)
      .setVisible(false);
    baseBg.on('pointerdown', () => this.showBaseUpgradeMenu());
    baseBg.on('pointerover', () => {
      baseBg.setFillStyle(0x1a2830);
      this.showBuildTooltip('base', rightX + 8, panelBtnY);
    });
    baseBg.on('pointerout', () => {
      baseBg.setFillStyle(0x152028);
      this.hideBuildTooltip();
    });
    this.baseUpgradeBtn = { bg: baseBg, label: baseLabel };

    // Tower build cards retain every existing tower and hotkey in one compact
    // rack. The angled card geometry mirrors the supplied build-card spec.
    const by = GAME_HEIGHT - UI_BOTTOM_H;
    const buildButtonW = 58;
    const buildStep = 62;
    const buildY = by + 20;
    this.add
      .text(26, by + 12, '[1–9]', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#7da5b8', letterSpacing: 1 })
      .setDepth(50);

    const ICONS: Record<string, string> = {
      ballistic: 'icon-kestrel',
      thermal: 'icon-pyre',
      artillery: 'icon-mordant',
      energy: 'icon-helios',
      vulcan: 'icon-vulcan',
      sabre: 'icon-sabre',
      tesla: 'icon-tesla',
      cryo: 'icon-cryo',
      dronehive: 'tower-dronehive-v2',
    };

    TOWER_ORDER.forEach((tid, i) => {
      const def = TOWERS[tid];
      const unlocked = this.isTowerUnlocked(tid);
      const x = 22 + i * buildStep;
      const y = buildY;
      tacticalPanel(x, y, buildButtonW, 106, 8, unlocked ? 0.92 : 0.42, unlocked ? 0x48c3e6 : 0x38434c);
      const btn = this.add
        .rectangle(x + buildButtonW / 2, y + 53, buildButtonW, 106, 0x38d6f8, 0.001)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: unlocked })
        .setDepth(50);
      btn.on('pointerdown', () => this.selectBuild(tid));
      btn.on('pointerover', () => {
        if (!unlocked) return;
        btn.setFillStyle(0x38d6f8, 0.13);
        this.showBuildTooltip(tid, x, y);
      });
      btn.on('pointerout', () => {
        btn.setFillStyle(0x38d6f8, 0.001);
        this.hideBuildTooltip();
      });
      const iconKey = ICONS[tid];
      if (iconKey && this.textures.exists(iconKey)) {
        this.add.image(x + buildButtonW / 2, y + 31, iconKey).setDisplaySize(38, 38).setDepth(52).setAlpha(unlocked ? 1 : 0.25);
      } else {
        this.add
          .rectangle(x + buildButtonW / 2, y + 31, 28, 28, def.colour, 0.25)
          .setStrokeStyle(1, def.colour, 0.8)
          .setDepth(52);
      }
      const label = this.add
        .text(x + buildButtonW / 2, y + 56, `[${i + 1}]\n${def.short}`, {
          fontFamily: 'Consolas, monospace',
          fontSize: '9px',
          color: unlocked ? '#c8d4e0' : '#55636e',
          align: 'center',
          lineSpacing: 1,
        })
        .setOrigin(0.5, 0)
        .setDepth(51);
      const costTxt = this.add
        .text(x + buildButtonW / 2, y + 88, unlocked ? `$${scaleSupplyCost(def.stages[0].cost)}` : 'LOCKED', {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: unlocked ? '#d4a24a' : '#46525c',
        })
        .setOrigin(0.5, 0)
        .setDepth(51);
      this.towerBtnTexts.push(label);
      this.buildBtnInfo.push({ tid, label, costTxt, cost: scaleSupplyCost(def.stages[0].cost) });
    });

    // Ability buttons
    const ax = abilityConsoleX + BattleScene.ABILITY_FIRST_INSET;
    const abilityStep = BattleScene.ABILITY_STEP;
    // The weapon art is the visual anchor. Keep the hit area invisible so the
    // rack reads as four weapons rather than four rotated UI diamonds.
    const abilityW = 62;
    this.add
      .text(abilityConsoleX + 20, by + 12, 'SUPERWEAPONS  [Q W E R]  ·  T: MODE', {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: '#6a7a88',
      })
      .setDepth(50);

    const ABILITY_ICONS: Record<string, string> = {
      airstrike: 'ability-spear-v2', barrier: 'ability-barrier-v2', rollingdeath: 'rolling-death-balls-v1', emp: 'ability-emp-v2',
    };
    ABILITIES.forEach((ab, i) => {
      const x = ax + i * abilityStep;
      const y = by + 65;
      const btn = this.add
        .rectangle(x, y, abilityW, abilityW, 0x0c1a28, 0)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(50);
      btn.on('pointerdown', () => this.tryAbility(ab.id));
      btn.on('pointerover', () => {
        btn.setScale(1.08);
        this.showAbilityTooltip(ab.id, x, y - 12);
      });
      btn.on('pointerout', () => {
        btn.setScale(1);
        this.hideAbilityTooltip();
      });
      const bar = this.add.graphics().setDepth(51);
      this.abilityBars[ab.id] = bar;
      const glyph = ['◈', '⬢', '◇', 'ϟ'][i] ?? '•';
      const iconKey = ABILITY_ICONS[ab.id];
      if (iconKey && this.textures.exists(iconKey)) {
        this.abilityIcons[ab.id] = this.add.image(x, y - 4, iconKey).setDisplaySize(34, 34).setDepth(52);
      } else {
        this.abilityGlyphs[ab.id] = this.add.text(x, y - 4, glyph, {
          fontFamily: 'Segoe UI Symbol, Consolas, monospace', fontSize: '18px', color: '#5ec8ff',
        }).setOrigin(0.5).setDepth(52);
      }
      const abilityLabel = this.add
        .text(x, y + 29, `${ab.key}\n${ab.short}`, {
          fontFamily: 'Consolas, monospace',
          fontSize: '9px',
          color: '#5ec8ff',
          align: 'center',
          lineSpacing: 1,
        })
        .setOrigin(0.5, 0)
        .setDepth(52);
      this.abilityTexts[ab.id] = abilityLabel;
    });

    // Central tactical deployment control.
    this.waveControlHover = false;
    this.waveControlPressed = false;
    this.waveControlState = '';
    this.wavePreviewState = '';
    this.wavePreviewExpanded = true;
    this.waveControl = this.add.graphics().setDepth(50);
    const waveConsoleCentre = waveConsoleX + waveConsoleW / 2;
    const waveConsoleRight = waveConsoleX + waveConsoleW;
    this.add
      .text(waveConsoleX + 16, by - 2, 'NEXT THREAT', {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: '#d4a24a',
        letterSpacing: 1,
      })
      .setDepth(51);
    this.gHud.lineStyle(1, 0xd4a24a, 0.42).lineBetween(waveConsoleX + 16, by + 10, waveAudioTrayX - 8, by + 10);
    this.txtWavePreview = this.add
      .text(waveConsoleCentre, by + 20, '', {
        fontFamily: 'Consolas, monospace',
        fontSize: '8px',
        color: '#9ad4e8',
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(51);
    this.wavePreviewToggle = this.add
      .text(waveAudioTrayX - 10, by - 2, '⌃', {
        fontFamily: 'Segoe UI Symbol, Consolas, monospace',
        fontSize: '13px',
        color: '#9ad4e8',
      })
      .setOrigin(1, 0.5)
      .setDepth(53);
    for (let i = 0; i < 11; i++) {
      const x = waveConsoleX + 24 + i * 25;
      this.wavePreviewButtons.push(
        this.add.rectangle(x, by + 38, 22, 26, 0x152028, 1)
          .setOrigin(0.5)
          .setStrokeStyle(1, 0x31566a, 1)
          .setDepth(52)
          .setVisible(false),
      );
      this.wavePreviewIcons.push(
        this.add.image(x, by + 34, 'enemy-scuttler-v2')
          .setDisplaySize(15, 15)
          .setDepth(53)
          .setVisible(false),
      );
      this.wavePreviewCounts.push(
        this.add.text(x, by + 44, '', {
          fontFamily: 'Consolas, monospace',
          fontSize: '6px',
          color: '#d4a24a',
          align: 'center',
        })
          .setOrigin(0.5, 0)
          .setDepth(53)
          .setVisible(false),
      );
    }
    this.wavePreviewHit = this.add
      .rectangle(waveConsoleCentre, by + 24, waveConsoleW - 12, 60, 0x000000, 0)
      .setDepth(54)
      .setInteractive({ useHandCursor: true });
    this.wavePreviewHit.on('pointerover', () => this.wavePreviewToggle.setColor('#e0f4ff'));
    this.wavePreviewHit.on('pointerout', () => this.wavePreviewToggle.setColor('#9ad4e8'));
    this.wavePreviewHit.on('pointerdown', () => this.setWavePreviewExpanded(!this.wavePreviewExpanded));
    this.earlyCallBtn = this.add
      .text(waveConsoleCentre, by + 90, 'START WAVE', {
        fontFamily: 'Consolas, monospace',
        fontSize: '17px',
        fontStyle: 'bold',
        color: '#d9f8ff',
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(51);
    this.waveControlHint = this.add.text(waveConsoleCentre, by + 116, 'SPACE  ·  DEPLOY', {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#81bac9', align: 'center',
    }).setOrigin(0.5).setDepth(51);
    this.waveControlHit = this.add.rectangle(waveConsoleCentre, by + 103, waveConsoleW - 12, 78, 0x000000, 0)
      .setDepth(52).setInteractive({ useHandCursor: true });
    this.waveControlHit.on('pointerover', () => { this.waveControlHover = true; this.refreshWaveControl(); });
    this.waveControlHit.on('pointerout', () => {
      this.waveControlHover = false; this.waveControlPressed = false; this.refreshWaveControl();
    });
    this.waveControlHit.on('pointerdown', () => { this.waveControlPressed = true; this.refreshWaveControl(); });
    this.waveControlHit.on('pointerup', () => {
      const activate = this.waveControlPressed;
      this.waveControlPressed = false;
      if (activate) this.callNextWave();
      this.refreshWaveControl();
    });

    // ---- Big on-battlefield START WAVE prompt (animated) ----
    this.wavePrompt = this.add.container(BATTLE_W / 2, 552).setDepth(46).setVisible(false);
    this.gWavePrompt = this.add.graphics();
    this.txtWavePrompt = this.add
      .text(0, -10, 'START WAVE', {
        fontFamily: 'Consolas, monospace',
        fontSize: '22px',
        color: '#ffd27a',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.txtWaveSub = this.add
      .text(0, 16, 'SPACE  ·  or click here', {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#c8d4e0',
      })
      .setOrigin(0.5);
    const waveHit = this.add
      .rectangle(0, 2, 280, 84, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });
    waveHit.on('pointerdown', () => {
      if (!this.waveActive && !this.allWavesDone && !this.gameOver) this.callNextWave();
    });
    this.wavePrompt.add([this.gWavePrompt, this.txtWavePrompt, this.txtWaveSub, waveHit]);

    // Panel actions drawn as text in right panel handled via keyboard + click targets
    this.input.keyboard?.on('keydown-U', () => this.upgradeSelected());
    this.input.keyboard?.on('keydown-X', () => this.sellSelected());
    this.input.keyboard?.on('keydown-T', () => this.cyclePriority());
    this.input.keyboard?.on('keydown-S', () => {
      if (this.selectedTower?.isMax) {
        this.tryTowerSpecial(this.selectedTower);
      }
    });

    this.towerMenu = new TowerMenu(this, {
      onUpgrade: (t) => this.upgradeTower(t),
      onPriority: (t) => this.cyclePriorityTower(t),
      onRepair: (t) => this.repairTower(t),
      onEvolve: (t, to) => this.evolveTower(t, to),
      onSymbiosis: (t, mid) => this.attachSymbiosis(t, mid),
      onBuyDrone: (t) => this.buySwarmDrone(t),
      onSell: (t) => this.sellTower(t),
      onClose: () => this.towerMenu.close(),
      getSupply: () => this.isDevMode() ? Number.MAX_SAFE_INTEGER : this.supply,
      getDroneCount: (t) => this.getSwarmDroneCount(t),
      getDroneCost: (t) => this.getSwarmDroneCost(t),
      getRepairCost: (t) => t.hp < t.maxHp ? 10 : null,
    });

    this.refreshHud();
  }

  private restoreActiveRun(snapshot: ActiveRunSnapshot): void {
    this.supply = snapshot.supply;
    this.integrity = snapshot.integrity;
    this.maxIntegrity = snapshot.maxIntegrity;
    this.score = snapshot.score;
    this.baseLevel = snapshot.baseLevel;
    const resumeWave = snapshot.waveActive ? Math.max(0, Math.floor(snapshot.waveIndex) - 1) : Math.floor(snapshot.waveIndex);
    this.waveIndex = Phaser.Math.Clamp(resumeWave, 0, this.waveList.length);
    this.simElapsed = Math.max(0, snapshot.simElapsed);
    this.realElapsed = Math.max(0, snapshot.realElapsed);
    this.gameSpeed = Phaser.Math.Clamp(snapshot.gameSpeed, 0.5, 3);
    this.time.timeScale = this.gameSpeed;
    for (const saved of snapshot.towers) {
      const pad = this.pads.find((candidate) => candidate.id === saved.padId && !candidate.occupied);
      if (!pad || !TOWERS[saved.id]) continue;
      const tower = new Tower(saved.id, pad.id, pad.x, pad.y, this.towerBonuses(saved.id, pad.kind === 'base'));
      tower.stageIndex = Phaser.Math.Clamp(Math.floor(saved.stage), 0, tower.maxStage);
      tower.priority = (saved.priority as TargetPriority) ?? 'FIRST';
      if (saved.secondary && SYMBIOSIS[saved.secondary]) {
        tower.attachSecondary(saved.secondary);
        tower.secondaryLevel = Phaser.Math.Clamp(Math.floor(saved.secondaryLevel ?? 1), 1, 3);
        tower.secondaryInvested = Math.max(tower.secondaryInvested, saved.secondaryInvested ?? tower.secondaryInvested);
      }
      this.towers.push(tower);
      if (tower.def.id === 'dronehive') this.swarmDroneCounts.set(tower.id, 3);
      pad.occupied = true;
      pad.towerId = tower.id;
    }
    const claimedTreasures = new Set(snapshot.treasureSites ? snapshot.claimedTreasures ?? [] : []);
    for (const site of this.treasureSites) site.claimed = claimedTreasures.has(site.id);
    this.resumeRun = undefined;
    this.showMessage(`CHECKPOINT RESTORED  ·  WAVE ${this.waveIndex}/${this.waveList.length}  ·  BUILD AND CONTINUE`, false);
  }

  private saveActiveRun(): void {
    if (this.gameOver || this.missionExitOpen) return;
    saveManager.saveActiveRun({
      missionId: this.missionId,
      difficulty: this.difficulty,
      survival: this.survival,
      waveIndex: this.waveIndex,
      waveActive: this.waveActive,
      supply: Math.floor(this.supply),
      integrity: Math.floor(this.integrity),
      maxIntegrity: this.maxIntegrity,
      score: Math.floor(this.score),
      baseLevel: this.baseLevel,
      gameSpeed: this.gameSpeed,
      simElapsed: this.simElapsed,
      realElapsed: this.realElapsed,
      towers: this.towers.map((tower) => ({
        id: tower.def.id,
        padId: tower.padId,
        stage: tower.stageIndex,
        priority: tower.priority,
        secondary: tower.secondary,
        secondaryLevel: tower.secondaryLevel,
        secondaryInvested: tower.secondaryInvested,
      })),
      claimedTreasures: this.treasureSites.filter((site) => site.claimed).map((site) => site.id),
      treasureSites: this.treasureSites.map(({ id, padId, amount, kind }) => ({ id, padId, amount, kind })),
      savedAt: Date.now(),
    });
  }

  // ---------- WAVE ----------

  private get maxCampaignWaves(): number {
    return this.waveList.length;
  }

  private callNextWave(auto = false): void {
    if (this.gameOver || this.allWavesDone) return;
    // Manual calls can now rush the next wave while the current formation is
    // still on the route. Automatic calls remain conservative and wait for a
    // clear so a saved run cannot accidentally create a second timer chain.
    if (this.waveActive && auto) return;

    const rushing = this.waveActive && !auto;
    if (rushing && this.rushesUsed >= BattleScene.MAX_WAVE_RUSHES) {
      audio.deny();
      this.showMessage('RUSH LIMIT REACHED — CLEAR ALL HOSTILES TO RESET', false);
      return;
    }

    const campaign = !this.survival;
    if (campaign && this.waveIndex >= this.waveList.length) return;

    // Reward for calling the wave before the auto-advance clock expires.
    const earlyReward = auto ? 0 : rushing ? this.earlyRushBonus() : this.earlyCallBonus();
    const rushNumber = rushing ? this.rushesUsed + 1 : 0;
    const formationWidth = rushing ? 2 ** rushNumber : 1;

    // First manual call arms auto-advance for the rest of the mission
    if (!auto) this.autoAdvance = true;
    this.autoTimer?.remove();
    this.autoTimer = null;

    let bonus: number;
    let label = '';
    let groups: WaveGroup[] = [];

    if (this.survival) {
      const w = this.waveIndex + 1;
      bonus = Math.floor(18 + w * 3);
      // The persistent HUD already names the mode and wave. Keeping the
      // transient callout to one wave number avoids the old duplicated
      // "WAVE n — SURVIVAL n" message stack.
      label = '';
      groups = this.buildSurvivalWave(w);
      this.survivalHpScale = 1 + w * SURVIVAL_HP_INCREASE_PER_WAVE;
    } else {
      const wave = this.waveList[this.waveIndex];
      bonus = wave.earlyBonus;
      const nightmareGroups = this.nightmareMutatedGroups(wave.groups, wave.id);
      const mutated = nightmareGroups.length > wave.groups.length;
      label = this.difficulty === 'nightmare'
        ? (mutated ? `${wave.label ? `${wave.label} · ` : ''}MUTATED` : wave.label ?? 'NIGHTMARE')
        : wave.label ?? '';
      groups = nightmareGroups;
      if (wave.id === this.waveList.length) {
        this.time.delayedCall(2000, () => this.showMessage('FINAL WAVE — HEAVY SIGNATURES'));
      }
    }

    // Auto-called waves still grant the call bonus (automation is the default flow)
    bonus = Math.floor(bonus * this.research.earlyWaveBonusMul * this.rankPerks.waveRewardMul * EARNED_INCOME_MULTIPLIER * this.nightmareIncomeMultiplier() * CREDIT_REWARD_MULTIPLIER * COIN_REWARD_MULTIPLIER * this.devCreditMul);
    this.supply += bonus;
    this.score += bonus;
    if (earlyReward > 0) {
      const reducedEarlyReward = Math.floor(earlyReward * this.rankPerks.waveRewardMul * EARNED_INCOME_MULTIPLIER * this.nightmareIncomeMultiplier() * CREDIT_REWARD_MULTIPLIER * COIN_REWARD_MULTIPLIER * this.devCreditMul);
      this.supply += reducedEarlyReward;
      this.score += reducedEarlyReward;
      this.showMessage(`${rushing ? 'RUSH' : 'EARLY CALL'} BONUS  +${reducedEarlyReward} SUPPLY`);
      const pop = this.add
        .text(BATTLE_W / 2, 524, `+${reducedEarlyReward} ${rushing ? 'RUSH' : 'EARLY'} BONUS`, {
          fontFamily: 'Consolas, monospace',
          fontSize: '16px',
          color: '#7dce5a',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setDepth(48);
      this.tweens.add({
        targets: pop,
        y: 468,
        alpha: 0,
        duration: 1500,
        ease: 'Cubic.easeOut',
        onComplete: () => pop.destroy(),
      });
    }
    this.waveActive = true;
    this.waveIndex++;
    this.portalPulse = Math.max(this.portalPulse, rushing ? 1.1 : 1.8);
    achievements.noteWave(this.waveIndex, this.survival, this.difficulty);
    if (!rushing) {
      this.spawnQueue = [];
      this.spawnClock = 0;
      this.rushesUsed = 0;
    }
    // A rush appends to the live queue rather than replacing it. This is what
    // makes an early call genuinely overlap the existing formation.
    this.enqueueWaveGroups(groups, formationWidth, rushing ? this.spawnClock + 0.12 : 0);
    if (rushing) this.rushesUsed = rushNumber;
    this.waveHostileBudget = this.spawnQueue.length + this.enemies.filter((enemy) => enemy.alive).length;

    audio.wave();
    const autoTag = auto ? '  AUTO' : rushing ? `  RUSH ${rushNumber}/${BattleScene.MAX_WAVE_RUSHES} · ${formationWidth} ABREAST` : '';
    const labelTxt = label ? ` — ${label}` : '';
    announcer.say(auto ? 'wave' : 'wave', `WAVE ${this.waveIndex}${labelTxt}  +${bonus} SUPPLY${autoTag}`);
    this.showMessage(announcer.dequeue() ?? `WAVE ${this.waveIndex}${labelTxt}  +${bonus} SUPPLY`);
    // Also show the tactical line
    this.showMessage(`WAVE ${this.waveIndex}${labelTxt}  +${bonus} SUPPLY`, false);
    this.showBanner(
      rushing
        ? `RUSH ${rushNumber} · ${formationWidth} ABREAST`
        : this.survival
          ? `SURVIVAL · WAVE ${this.waveIndex}`
          : `WAVE ${this.waveIndex}${labelTxt}`,
    );
    if (!rushing) this.waveStartIntegrity = this.integrity;

    // Voice: boss / final wave / routine wave callout.
    const hasBoss = groups.some((g) => ENEMIES[g.enemy]?.boss);
    const hasElite = groups.some((g) => ENEMIES[g.enemy]?.elite);
    const isFinal = !this.survival && this.waveIndex >= this.waveList.length;
    if (rushing) {
      const rushCategory = rushNumber === 1 ? 'rush_single' : 'rush_double';
      voice.playCategory(this, rushCategory, { priority: true, cooldown: 0 });
      if (formationWidth >= 4) {
        // Give the double rush a second, distinct mayhem beat after the first
        // callout so the four-wide formation gets its own readable warning.
        this.time.delayedCall(2200, () => {
          if (!this.gameOver) voice.playCategory(this, 'mayhem', { priority: true, cooldown: 0 });
        });
      }
    } else if (isFinal) voice.playCategory(this, 'lastwave', { priority: true, cooldown: 0 });
    else if (hasBoss || hasElite) voice.playCategory(this, 'boss', { cooldown: 3 });
    else voice.playCategory(this, 'wave', { cooldown: 6 });

    this.refreshHud();
  }

  /** Add a wave to the shared queue without disrupting units already inbound. */
  private enqueueWaveGroups(groups: WaveGroup[], formationWidth: number, queueStart: number): void {
    for (const g of groups) {
      const def = ENEMIES[g.enemy];
      if (!def) continue;
      const offset = g.offset ?? 0;
      // Elites and bosses retain a single centred lane: their footprint must
      // never form a four-wide line across the road.
      const canFormLine = !def.boss && !def.elite;
      const squadSize = canFormLine
        ? formationWidth > 1
          ? formationWidth
          : (g.formation === 'line' || this.devSquadSize > 1 ? this.devSquadSize : 1)
        : 1;
      const squadCount = g.count * squadSize;
      for (let i = 0; i < squadCount; i++) {
        const formationSlot = squadSize > 1 ? i % squadSize : 0;
        const squadIndex = squadSize > 1 ? Math.floor(i / squadSize) : i;
        this.spawnQueue.push({
          def,
          t: queueStart + offset + squadIndex * g.delay,
          lane: this.resolveSpawnLane(g.lane),
          formationSlot,
          formationSize: squadSize,
        });
      }
    }
    this.spawnQueue.sort((a, b) => a.t - b.t);
  }

  private scheduleAutoWave(): void {
    if (!this.autoAdvance || this.gameOver || this.allWavesDone) return;
    if (!saveManager.data.settings.autoWaves) return;
    if (!this.survival && this.waveIndex >= this.waveList.length) return;
    this.autoTimer?.remove();
    this.autoTimer = this.time.delayedCall(BattleScene.AUTO_WAVE_DELAY_MS, () => {
      if (this.gameOver || this.waveActive) return;
      this.callNextWave(true);
    });
  }

  /** Supply granted for calling the wave before the auto-advance clock runs out. */
  private earlyCallBonus(): number {
    const remaining = this.autoTimer?.getRemaining() ?? 0;
    if (remaining <= 100) return 0;
    const frac = Phaser.Math.Clamp(remaining / BattleScene.AUTO_WAVE_DELAY_MS, 0, 1);
    return Math.round((14 + this.waveIndex * 5) * frac * this.research.earlyWaveBonusMul * this.rankPerks.waveRewardMul);
  }

  /**
   * Risk/reward for deliberately stacking a wave. The percentage is based on
   * hostiles still queued or alive, so a commander earns the biggest payout
   * when calling into the next wave before the current lane has been solved.
   */
  private earlyRushBonus(): number {
    if (!this.waveActive || this.waveHostileBudget <= 0) return 0;
    const remaining = this.spawnQueue.length + this.enemies.filter((enemy) => enemy.alive).length;
    const risk = Phaser.Math.Clamp(remaining / this.waveHostileBudget, 0, 1);
    return Math.round((24 + this.waveIndex * 6) * risk * this.research.earlyWaveBonusMul * this.rankPerks.waveRewardMul);
  }

  /** Nightmare has its own economy without changing the authored map budgets. */
  private nightmareIncomeMultiplier(): number {
    return this.difficulty === 'nightmare' ? NIGHTMARE_EARNED_INCOME_MULTIPLIER : 1;
  }

  /** Keeps the preview honest when a commander changes their dominant damage type. */
  private nightmareMutationSignature(): string {
    if (this.difficulty !== 'nightmare') return '';
    const counts = new Map<string, number>();
    for (const tower of this.towers) {
      const type = tower.stage.damageType;
      counts.set(type, (counts.get(type) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, count]) => `${type}:${count}`)
      .join(',');
  }

  /**
   * Add small, legible counter-formations to Nightmare waves. The original wave
   * remains intact; mutations add a pressure unit that asks for a different
   * tactical answer rather than simply turning every hostile into a sponge.
   */
  private nightmareMutatedGroups(source: WaveGroup[], waveNumber: number): WaveGroup[] {
    if (this.difficulty !== 'nightmare') return source;

    const groups = source.map((group) => ({ ...group }));
    const lanes = this.routeCaches.length;
    const lane = (shift = 0) => lanes > 1 ? (waveNumber + shift) % lanes : 0;
    const latestOffset = () => Math.max(0, ...groups.map((group) => (group.offset ?? 0) + Math.min(group.count, 5) * group.delay));
    const add = (enemy: string, count: number, delay: number, offset: number, laneIndex = 0) => {
      if (ENEMIES[enemy]) groups.push({ enemy, count, delay, offset, lane: laneIndex });
    };

    // Armoured units arrive while a fresh build still has limited upgrade depth.
    if (waveNumber >= 2 && waveNumber <= 8 && waveNumber % 2 === 0) {
      add('plateback', 1 + Math.floor(waveNumber / 3), 1.2, 0.7, lane());
    }
    // Shield/rush pairs make an energy answer and target priority meaningful.
    if (waveNumber >= 4 && waveNumber % 3 === 1) {
      add('veilguard', 1 + Math.floor(waveNumber / 8), 1.1, 0.45, lane());
      add('dashling', 4 + Math.floor(waveNumber / 4), 0.32, 1.1, lane(1));
    }
    // Jammers and ranged pressure are escorted rather than arriving alone.
    if (waveNumber >= 6 && waveNumber % 4 === 2) {
      add('nulltick', 1 + Math.floor(waveNumber / 12), 1.3, 0.6, lane());
      add('gunner', 2 + Math.floor(waveNumber / 8), 0.75, 1.25, lane(1));
    }
    // Later maps turn their extra routes into a real coverage choice.
    if (waveNumber >= 12 && lanes > 1 && waveNumber % 2 === 0) {
      add('dashling', 5 + Math.floor(waveNumber / 3), 0.28, latestOffset() + 0.7, lane(1));
      if (lanes > 2) add('sporethrower', 2 + Math.floor(waveNumber / 10), 1.1, latestOffset() + 0.8, lane(2));
    }

    // Every boss receives escorts selected to expose the most common damage type.
    if (groups.some((group) => ENEMIES[group.enemy]?.boss)) {
      const typeCounts = new Map<string, number>();
      for (const tower of this.towers) {
        const type = tower.stage.damageType;
        typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1);
      }
      const dominant = [...typeCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'BALLISTIC';
      const offset = latestOffset() + 0.55;
      if (dominant === 'THERMAL') {
        add('veilguard', 2, 1.0, offset, lane());
        add('dashling', 6, 0.3, offset + 0.5, lane(1));
      } else if (dominant === 'EXPLOSIVE') {
        add('goliath', 1, 1.4, offset, lane());
        add('dashling', 6, 0.3, offset + 0.6, lane(1));
      } else if (dominant === 'ENERGY') {
        add('nulltick', 2, 1.1, offset, lane());
        add('raider', 4, 0.65, offset + 0.5, lane(1));
      } else {
        add('plateback', 2, 1.15, offset, lane());
        add('mender', 1, 1.6, offset + 0.5, lane(1));
      }
    }
    return groups;
  }

  private buildSurvivalWave(n: number): WaveGroup[] {
    const groups: WaveGroup[] = [];
    const base = 6 + Math.floor(n * 1.4);
    groups.push({ enemy: 'scuttler', count: Math.min(40, base), delay: Math.max(0.18, 0.55 - n * 0.01) });
    if (n >= 2) groups.push({ enemy: 'husk', count: Math.min(20, 3 + n), delay: 0.8, offset: 1 });
    if (n >= 3) groups.push({ enemy: 'dashling', count: Math.min(16, 2 + Math.floor(n * 0.6)), delay: 0.5, offset: 2 });
    if (n >= 4) groups.push({ enemy: 'plateback', count: Math.min(10, Math.floor(n / 2)), delay: 1.4, offset: 3 });
    if (n >= 6) groups.push({ enemy: 'mender', count: Math.min(4, Math.floor(n / 5)), delay: 2, offset: 4 });
    if (n >= 7) groups.push({ enemy: 'veilguard', count: Math.min(6, Math.floor(n / 4)), delay: 1.5, offset: 5 });
    if (n >= 8) groups.push({ enemy: 'goliath', count: Math.min(5, Math.floor(n / 6) + 1), delay: 2.5, offset: 6 });
    if (n >= 9) groups.push({ enemy: 'broodnode', count: Math.min(3, Math.floor(n / 8) + 1), delay: 3, offset: 7 });
    if (n >= 10) groups.push({ enemy: 'nulltick', count: Math.min(3, Math.floor(n / 7)), delay: 2, offset: 8 });
    if (n >= 12) groups.push({ enemy: 'sporethrower', count: Math.min(4, Math.floor(n / 6)), delay: 1.8, offset: 9 });
    if (n % 5 === 0) groups.push({ enemy: 'archon', count: Math.min(3, Math.floor(n / 10) + 1), delay: 2, offset: 10 });
    if (n % 10 === 0) groups.push({ enemy: 'colossus', count: 1, delay: 1, offset: 12 });
    return groups;
  }

  /** Pick an un-authored lane using the developer's current map weighting. */
  private resolveSpawnLane(authoredLane?: number): number {
    if (authoredLane != null || this.routeCaches.length <= 1 || !this.isDevMode()) {
      return Phaser.Math.Clamp(authoredLane ?? 0, 0, this.routeCaches.length - 1);
    }
    const saved = saveManager.data.settings.devMapLaneWeights?.[this.missionId] ?? [];
    const weights = this.routeCaches.map((_, index) => Math.max(0, Number(saved[index] ?? 1)));
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total <= 0) return 0;
    let roll = Math.random() * total;
    for (let index = 0; index < weights.length; index++) {
      roll -= weights[index];
      if (roll <= 0) return index;
    }
    return weights.length - 1;
  }

  private spawnEnemy(def: EnemyDef, distOffset = 0, lane = 0, route?: PathCache, formationSlot = 0, formationSize = 1): void {
    const bossHpMultiplier = def.boss
      ? (this.difficulty === 'nightmare'
        ? NIGHTMARE_BOSS_HP_MULTIPLIER
        : this.difficulty === 'normal' ? NORMAL_BOSS_HP_MULTIPLIER : BOSS_HP_MULTIPLIER)
      : 1;
    const e = new Enemy(def, this.hpMul * this.survivalHpScale * bossHpMultiplier * ENEMY_HEALTH_MULTIPLIER, this.speedMul, ENEMY_POWER_MULTIPLIER);
    e.weaponDamageMul = this.difficulty === 'normal' ? NORMAL_WEAPON_DAMAGE_MULTIPLIER : 1;
    e.lane = Phaser.Math.Clamp(lane, 0, this.routeCaches.length - 1);
    e.route = route ?? this.routeCaches[e.lane] ?? this.path;
    e.dist = distOffset;
    const safeFormationSize = def.boss || def.elite ? 1 : formationSize;
    e.formationSlot = safeFormationSize === 1 ? 0 : formationSlot;
    e.formationSize = safeFormationSize;
    const slotCentre = (safeFormationSize - 1) / 2;
    e.formationOffset = safeFormationSize === 1 ? 0 : (formationSlot - slotCentre) * Math.max(18, def.radius * 2.35);
    const p = samplePath(e.route, e.dist);
    e.x = p.x;
    e.y = p.y;
    e.angle = p.angle;
    this.enemies.push(e);
    saveManager.unlockEnemy(def.id);
    bus.emit(Events.EnemySpawned, def.id);
    if (def.elite) {
      announcer.say('elite');
      this.showMessage(announcer.dequeue() ?? `ELITE: ${def.name}`);
      audio.special();
    }
    if (def.boss) {
      announcer.say('boss');
      this.showMessage(announcer.dequeue() ?? `BOSS: ${def.name}`);
      this.showBanner('BOSS INBOUND', '#ff5a5a');
      this.cinematic = 0.6;
      this.cinematicCol = 0xff2020;
      audio.special();
    }
  }

  // ---------- BUILD ----------

  private getBaseUpgradeCost(): number {
    return scaleUpgradeCost(BattleScene.BASE_UPGRADE_COST * this.research.baseUpgradeCostMul);
  }

  private getBaseHardpointCount(): number {
    return 2 + (this.rankPerks?.baseHardpointsBonus ?? 0);
  }

  private getSwarmDroneCount(t: Tower): number {
    return this.swarmDroneCounts.get(t.id) ?? Math.min(6, 3 + t.stageIndex);
  }

  private getSwarmDroneCost(t: Tower): number | null {
    const count = this.getSwarmDroneCount(t);
    return count >= 6 ? null : scaleSupplyCost(45 + count * 20);
  }

  private abilityCooldown(id: string, base: number): number {
    return base * (this.research.abilityCdMul[id] ?? 1) * (this.rankPerks?.abilityCooldownMul ?? 1);
  }

  /** Keep late rank perks paced against the authored campaign. */
  private activeCampaignRank(): number {
    const mapNumber = Phaser.Math.Clamp(getMission(this.missionId).index, 1, MISSIONS.length);
    const mapRankCap = Math.ceil((mapNumber / Math.max(1, MISSIONS.length)) * MAX_COMMAND_RANK);
    return Math.min(saveManager.data.rank, mapRankCap);
  }

  private showBuildTooltip(tid: string, x: number, y: number): void {
    this.hideBuildTooltip();
    const isBase = tid === 'base';
    const def = !isBase ? TOWERS[tid] : undefined;
    const pct = (value: number) => `${Math.round(value * 100)}%`;
    const activeBonuses = def
      ? [
          this.research.damageMul[tid] > 1 ? `+${pct(this.research.damageMul[tid] - 1)} DAMAGE` : '',
          this.research.rangeMul[tid] > 1 ? `+${pct(this.research.rangeMul[tid] - 1)} RANGE` : '',
          this.research.rateMul[tid] > 1 ? `+${pct(this.research.rateMul[tid] - 1)} FIRE RATE` : '',
          this.research.armourPenAdd[tid] > 0 ? `+${pct(this.research.armourPenAdd[tid])} ARMOUR PEN` : '',
        ].filter(Boolean)
      : [];
    const text = isBase
      ? this.baseLevel >= 1
        ? `COMMAND BASTION\nBASE MK II ONLINE — ${this.getBaseHardpointCount()} ROOF HARDPOINTS\nSelect a tower, then click a roof pad.`
        : `COMMAND BASTION\nBASE MK II unlocks ${this.getBaseHardpointCount()} roof hardpoints\nCost $${this.getBaseUpgradeCost()}  ·  right-click base`
      : `${def?.short ?? tid.toUpperCase()}  ·  ${def?.targeting === 'ground' ? 'LAND' : def?.targeting === 'air' ? 'AIR' : 'LAND / AIR'} CLASS\n${def?.stages[0].damageType ?? ''} weapon  ·  DMG ${def?.stages[0].damage ?? 0}  ·  EFFECTIVE ×${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)}  ·  RNG ${def?.stages[0].range ?? 0}\n${activeBonuses.length ? `RESEARCH  ${activeBonuses.join('  ·  ')}` : 'RESEARCH  — no active tower bonus'}\n${def?.desc ?? ''}`;
    const width = 290;
    const height = isBase ? 64 : 100;
    const bg = this.add.graphics().setDepth(120);
    bg.fillStyle(0x071017, 0.78);
    bg.fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1, isBase ? this.theme.commandColour : def?.colour ?? COLORS.accent, 0.9);
    bg.strokeRoundedRect(0, 0, width, height, 6);
    const label = this.add.text(10, 8, text, {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#c8d4e0',
      wordWrap: { width: width - 20 }, lineSpacing: 3,
    }).setDepth(121);
    const container = this.add.container(0, 0, [bg, label]).setDepth(120);
    const fieldBottom = MAP_FIELD_BOTTOM;
    container.setPosition(
      Phaser.Math.Clamp(x, 8, BATTLE_W - width - 8),
      Phaser.Math.Clamp(y - height - 8, UI_TOP_H + 8, fieldBottom - height - 8),
    );
    this.buildTooltip = container;
  }

  private hideBuildTooltip(): void {
    this.buildTooltip?.destroy(true);
    this.buildTooltip = undefined;
  }

  private showAbilityTooltip(id: string, x: number, y: number): void {
    this.hideAbilityTooltip();
    const ability = ABILITIES.find((candidate) => candidate.id === id);
    if (!ability) return;
    const cooldown = this.abilityCooldown(id, ability.cooldown);
    const lines: string[] = [`${ability.name.toUpperCase()}  ·  ${ability.key}`, `COOLDOWN ${cooldown.toFixed(0)}s`];
    if (id === 'airstrike') {
      lines.push(
        `DAMAGE  EXPLOSIVE  ${Math.round(560 * WEAPON_DAMAGE_MULTIPLIER * this.research.airstrikeDamageMul)}`,
        `BLAST  ${Math.round(95 * this.research.airstrikeRadiusMul)}px`,
        this.research.airstrikeFireDuration > 0
          ? `EFFECT  FIRE PATCH ${this.research.airstrikeFireDuration.toFixed(0)}s  ·  ${this.research.airstrikeFireDps}/s`
          : 'EFFECT  delayed orbital impact',
      );
    } else if (id === 'barrier') {
      lines.push(
        'DAMAGE  NONE',
        `EFFECT  HARDLIGHT STOP  ·  ${Math.round(450 * this.research.barrierHpMul)} HP`,
        `DURATION  ${(18 * this.research.barrierDurationMul).toFixed(1)}s`,
      );
    } else if (id === 'rollingdeath') {
      lines.push(
        `DAMAGE  EXPLOSIVE  ${Math.round(420 * ROLLING_DEATH_DAMAGE_MULTIPLIER * WEAPON_DAMAGE_MULTIPLIER)} / BOMB`,
        `SALVO  ${Math.round(this.research.rollingBombCount)} BOMBS`,
        this.research.rollingBombSnowUnlocked ? `SNOW  CRYO SLOW 58%  ·  ${(5 + this.research.rollingBombSlowDuration).toFixed(1)}s` : 'EFFECT  explosive mode only',
      );
    } else {
      lines.push(
        'DAMAGE  NONE',
        `EFFECT  EMP STUN  ·  ${(3.5 * this.research.empDurationMul).toFixed(1)}s`,
        'AREA  ALL HOSTILES',
      );
    }
    const width = 282;
    const height = 18 + lines.length * 15;
    const bg = this.add.graphics().setDepth(120);
    bg.fillStyle(0x071017, 0.96).fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1, COLORS.accent, 0.9).strokeRoundedRect(0, 0, width, height, 6);
    const label = this.add.text(10, 8, lines.join('\n'), {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#c8d4e0', lineSpacing: 2,
    }).setDepth(121);
    const container = this.add.container(0, 0, [bg, label]).setDepth(120);
    container.setPosition(
      Phaser.Math.Clamp(x - width / 2, 8, GAME_WIDTH - width - 8),
      Phaser.Math.Clamp(y - height, UI_TOP_H + 8, GAME_HEIGHT - UI_BOTTOM_H - height - 8),
    );
    this.cameras.main.ignore(container);
    this.abilityTooltip = container;
  }

  private hideAbilityTooltip(): void {
    this.abilityTooltip?.destroy(true);
    this.abilityTooltip = undefined;
  }

  private showBaseTooltip(): void {
    this.hideBaseTooltip();
    const width = 280;
    const height = 92;
    const c = this.mapDef.command;
    const upgraded = this.baseLevel >= 1;
    const text = upgraded
      ? `COMMAND BASTION  ·  BASE MK II\nROOF HARDPOINTS  ${this.getBaseHardpointCount()}/${this.getBaseHardpointCount()} ONLINE\nSelect a tower, then click a roof pad.`
      : `COMMAND BASTION  ·  BASE MK I\nROOF HARDPOINTS  0/${this.getBaseHardpointCount()} LOCKED\nRIGHT-CLICK FOR UPGRADES  ·  $${this.getBaseUpgradeCost()}`;
    const bg = this.add.graphics().setDepth(120);
    bg.fillStyle(0x071017, 0.78);
    bg.fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1, this.theme.commandColour, 0.9);
    bg.strokeRoundedRect(0, 0, width, height, 6);
    const label = this.add.text(10, 9, text, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#c8d4e0',
      wordWrap: { width: width - 20 },
      lineSpacing: 4,
    }).setDepth(121);
    const container = this.add.container(0, 0, [bg, label]).setDepth(120);
    const x = Phaser.Math.Clamp(c.x - width - 24, 8, BATTLE_W - width - 8);
    const y = Phaser.Math.Clamp(c.y - height / 2, UI_TOP_H + 8, GAME_HEIGHT - UI_BOTTOM_H - height - 8);
    container.setPosition(x, y);
    this.cameras.main.ignore(container);
    this.baseTooltip = container;
  }

  private hideBaseTooltip(): void {
    this.baseTooltip?.destroy(true);
    this.baseTooltip = undefined;
  }

  private hideBaseContextMenu(): void {
    this.baseContextMenu?.destroy(true);
    this.baseContextMenu = undefined;
  }

  private showBaseUpgradeMenu(): void {
    this.hideBaseContextMenu();
    const width = 250;
    const height = 126;
    const c = this.mapDef.command;
    const bg = this.add.graphics();
    bg.fillStyle(0x071017, 0.96);
    bg.fillRoundedRect(0, 0, width, height, 7);
    bg.lineStyle(1.5, this.theme.commandColour, 0.95);
    bg.strokeRoundedRect(0, 0, width, height, 7);
    const title = this.add.text(12, 10, 'COMMAND BASTION  ·  UPGRADES', {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d8f6ff',
    });
    const state = this.baseLevel >= 1 ? 'ONLINE  ·  ROOF HARDPOINTS READY' : `LOCKED  ·  ${this.getBaseHardpointCount()} ROOF SLOTS`;
    const status = this.add.text(12, 31, state, {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: this.baseLevel >= 1 ? '#7dce5a' : '#a7b7bf',
    });
    const row = this.add.rectangle(10, 55, width - 20, 30, 0x132630, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, this.baseLevel >= 1 ? COLORS.good : this.theme.commandColour, 0.9)
      .setInteractive({ useHandCursor: this.baseLevel < 1 });
    const rowText = this.add.text(20, 64, this.baseLevel >= 1 ? 'BASTION MK II  ·  INSTALLED' : `BASTION MK II  ·  $${this.getBaseUpgradeCost()}`, {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: this.baseLevel >= 1 ? '#7dce5a' : '#d8f6ff',
    });
    row.on('pointerdown', () => {
      if (this.baseLevel < 1) this.upgradeBase();
      this.showBaseUpgradeMenu();
    });
    const hint = this.add.text(12, 96, 'Left-click a tower card, then choose a roof pad.', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#7da5b8',
    });
    const container = this.add.container(0, 0, [bg, title, status, row, rowText, hint]).setDepth(125);
    const x = Phaser.Math.Clamp(c.x + 78, 8, BATTLE_W - width - 8);
    const y = Phaser.Math.Clamp(c.y - height - 18, UI_TOP_H + 8, MAP_FIELD_BOTTOM - height - 8);
    container.setPosition(x, y);
    this.cameras.main.ignore(container);
    this.baseContextMenu = container;
  }

  private selectBuild(tid: string): void {
    if (this.gameOver) return;
    if (!this.isTowerUnlocked(tid)) {
      audio.deny();
      this.showMessage('TOWER LICENCE LOCKED — advance the campaign to requisition it.');
      return;
    }
    // Build cards are toggles: clicking the active card again drops the
    // placement ghost and returns the cursor to normal selection mode.
    if (this.placeMode === 'build' && this.pendingTowerId === tid) {
      this.placeMode = 'none';
      this.hideBuildTooltip();
      audio.click();
      this.redrawPads();
      return;
    }
    this.placeMode = 'build';
    this.pendingTowerId = tid;
    this.selectedTower = null;
    this.selectedEnemy = null;
    this.baseSelected = false;
    this.hideBaseTooltip();
    audio.click();
  }

  /** Keep placement previews and constructed towers on one effective-stat path. */
  private towerBonuses(towerId: string, baseMount: boolean): TowerBonuses {
    return {
      damageMul: (this.research.damageMul[towerId] ?? 1) * (baseMount ? this.research.baseDamageMul : 1) * this.rankPerks.towerDamageMul,
      rangeMul: (this.research.rangeMul[towerId] ?? 1) * (baseMount ? this.research.baseRangeMul : 1) * this.rankPerks.towerRangeMul,
      rateMul: this.research.rateMul[towerId] ?? 1,
      armourPenAdd: this.research.armourPenAdd[towerId] ?? 0,
      symbiosisDamageMul: this.research.symbiosisDamageMul,
      swapCreditBonus: this.rankPerks.swapCreditBonus,
    };
  }

  private pendingTowerRange(baseMount: boolean): number {
    const def = TOWERS[this.pendingTowerId];
    return def.stages[0].range * this.towerBonuses(this.pendingTowerId, baseMount).rangeMul;
  }

  private tryBuildAt(x: number, y: number): boolean {
    const pad = this.pads.find(
      (p) =>
        !p.occupied &&
        (p.kind === 'field' || this.baseLevel >= 1) &&
        Phaser.Math.Distance.Between(x, y, p.x, p.y) < 22,
    );
    if (!pad) {
      const lockedBase = this.pads.some(
        (p) => p.kind === 'base' && Phaser.Math.Distance.Between(x, y, p.x, p.y) < 22,
      );
      if (lockedBase) {
        audio.deny();
        this.showMessage(`COMMAND BASTION LOCKED — RIGHT-CLICK TO UPGRADE ($${this.getBaseUpgradeCost()})`);
        return lockedBase;
      }
      const occupiedNear = this.pads.some(
        (p) => p.occupied && Phaser.Math.Distance.Between(x, y, p.x, p.y) < 22,
      );
      audio.deny();
      this.showMessage(
        occupiedNear
          ? 'FOUNDATION OCCUPIED — CLICK AN EMPTY CYAN PAD'
          : 'NO FOUNDATION HERE — AIM FOR A CYAN PAD RING',
      );
      return occupiedNear;
    }
    if (pad.kind === 'field' && !this.isFieldPadSafe(pad.x, pad.y)) {
      audio.deny();
      this.showMessage('TURRETS MUST BE PLACED OFF THE MOB ROUTE');
      return true;
    }
    const def = TOWERS[this.pendingTowerId];
    const cost = scaleSupplyCost(def.stages[0].cost);
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`INSUFFICIENT SUPPLY (need ${cost})`);
      return true;
    }
    const baseMount = pad.kind === 'base';
    this.spend(cost);
    const tower = new Tower(this.pendingTowerId, pad.id, pad.x, pad.y, this.towerBonuses(this.pendingTowerId, baseMount));
    this.towers.push(tower);
    if (tower.def.id === 'dronehive') this.swarmDroneCounts.set(tower.id, 3);
    pad.occupied = true;
    pad.towerId = tower.id;
    this.placeMode = 'none';
    this.selectedTower = tower;
    this.baseSelected = false;
    this.hideBaseTooltip();
    audio.place();
    voice.playCategory(this, 'build', { cooldown: 14 });
    this.spawnBurst(pad.x, pad.y, def.colour, 10);
    this.claimNearbyTreasures(tower.x, tower.y, tower.padId);
    bus.emit(Events.TowerBuilt, tower.def.id);
    this.refreshHud();
    return true;
  }

  private claimNearbyTreasures(x: number, y: number, padId: number): void {
    const claimRadius = 62;
    for (const site of this.treasureSites) {
      if (site.claimed || site.padId !== padId || Phaser.Math.Distance.Between(x, y, site.x, site.y) > claimRadius) continue;
      site.claimed = true;
      const drop: LootDrop = {
        id: nextEntityId(),
        x: site.x,
        y: site.y,
        amount: site.amount,
        kind: site.kind,
        source: 'treasure',
        life: 1,
        bob: 0,
      };
      this.loot.push(drop);
      this.collectLoot(this.loot.length - 1);
      const reward = site.kind === 'research' ? `+${site.amount} DATA` : `+${site.amount} SUPPLY`;
      this.showNotificationToast('TREASURE SECURED', site.kind === 'research' ? 'FIELD RESEARCH CACHE' : 'SUPPLY CACHE', reward, site.kind === 'research' ? 0xd8a8ff : 0xffd36a);
    }
  }

  /** Shared guard for build, redraw and any future rebuild path. */
  private isFieldPadSafe(x: number, y: number): boolean {
    return isFieldTurretPlacementSafe({ x, y }, getMapRoutes(this.mapDef));
  }

  private upgradeBase(): void {
    if (this.gameOver || this.baseLevel >= 1) return;
    const cost = this.getBaseUpgradeCost();
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`NEED ${cost} SUPPLY TO UNLOCK BASTION HARDPOINTS`);
      return;
    }
    this.spend(cost);
    this.baseLevel = 1;
    audio.upgrade();
    voice.playCategory(this, 'upgrade', { cooldown: 0 });
    this.showMessage(`COMMAND BASTION ONLINE — ${this.getBaseHardpointCount()} ROOF HARDPOINTS UNLOCKED`);
    this.redrawPads();
    this.refreshHud();
    if (this.baseSelected) this.showBaseTooltip();
  }

  private upgradeSelected(): void {
    if (this.selectedTower) this.upgradeTower(this.selectedTower);
  }

  private upgradeTower(t: Tower): void {
    const cost = t.upgradeCost;
    if (cost == null) {
      audio.deny();
      return;
    }
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`NEED ${cost} SUPPLY FOR ${t.def.stages[t.stageIndex + 1].name}`);
      return;
    }
    this.spend(cost);
    t.upgrade();
    if (t.def.id === 'dronehive') {
      this.swarmDroneCounts.set(t.id, Math.min(6, this.getSwarmDroneCount(t) + 1));
    }
    audio.upgrade();
    if (t.isMax) {
      if (!voice.play(this, topTierVoiceId(t.def.id), { priority: true, cooldown: 0 })) {
        voice.playCategory(this, 'maxed', { cooldown: 0 });
      }
      this.showMessage(`${t.def.short}  ·  ${TOP_TIER_CALLOUTS[t.def.id] ?? 'MAXIMUM OUTPUT'} ONLINE`, false);
    } else voice.playCategory(this, 'upgrade', { cooldown: 14 });
    this.vfx.burst(t.x, t.y, t.def.colour, 16, { speed: 120, life: 0.4, r: 2.5 });
    this.vfx.ring(t.x, t.y, 48, t.def.colour, 3, 0.5);
    this.vfx.flash(t.x, t.y, 22, 0xffffff, 'charge', 0, 0.2);
    this.refreshHud();
    this.towerMenu.refresh();
  }

  private repairTower(t: Tower): void {
    const cost = 10;
    if (t.hp >= t.maxHp) return;
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`NEED ${cost} SUPPLY FOR FIELD REPAIR`);
      return;
    }
    this.spend(cost);
    t.repair(30);
    audio.upgrade();
    this.vfx.ring(t.x, t.y, 30, 0x7dce5a, 2, 0.35);
    this.showMessage(`${t.def.short} REPAIRED  ·  ${Math.round(t.hp)}/${t.maxHp} HP`, false);
    this.refreshHud();
    this.towerMenu.refresh();
  }

  private sellSelected(): void {
    if (this.selectedTower) this.sellTower(this.selectedTower);
  }

  private buySwarmDrone(t: Tower): void {
    if (t.def.id !== 'dronehive') return;
    const cost = this.getSwarmDroneCost(t);
    if (cost == null) {
      this.showMessage('DRONE WING FULL — SIX UNITS MAX');
      return;
    }
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`NEED ${cost} SUPPLY FOR ADDITIONAL DRONE`);
      return;
    }
    this.spend(cost);
    this.swarmDroneCounts.set(t.id, this.getSwarmDroneCount(t) + 1);
    audio.upgrade();
    this.showMessage(`DRONE WING EXPANDED — ${this.getSwarmDroneCount(t)}/6 UNITS`);
    this.refreshHud();
    this.towerMenu.refresh();
  }

  private sellTower(t: Tower): void {
    this.supply += Math.floor(t.sellValue * COIN_REWARD_MULTIPLIER);
    const pad = this.pads.find((p) => p.id === t.padId);
    if (pad) {
      pad.occupied = false;
      pad.towerId = null;
    }
    this.towers = this.towers.filter((x) => x.id !== t.id);
    this.swarmDrones.delete(t.id);
    this.swarmDroneCounts.delete(t.id);
    if (this.selectedTower?.id === t.id) this.selectedTower = null;
    audio.click();
    this.spawnBurst(t.x, t.y, 0x666666, 8);
    this.towerMenu.close();
    this.refreshHud();
  }

  private cyclePriority(): void {
    if (this.selectedTower) this.cyclePriorityTower(this.selectedTower);
  }

  private cyclePriorityTower(t: Tower): void {
    const i = PRIORITY.indexOf(t.priority);
    t.priority = PRIORITY[(i + 1) % PRIORITY.length] as TargetPriority;
    audio.click();
    this.refreshHud();
    this.towerMenu.refresh();
  }

  private evolveTower(t: Tower, toId: string): void {
    const def = TOWERS[toId];
    const evo = t.def.evolvesTo?.find((e) => e.to === toId);
    if (!def || !evo) return;
    if (!researchManager.isTowerResearchUnlocked(toId)) {
      audio.deny();
      this.showMessage('EVOLUTION LOCKED  ·  RESEARCH THE PLATFORM FIRST', false);
      return;
    }
    const cost = scaleUpgradeCost(evo.cost);
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`NEED ${cost} SUPPLY TO EVOLVE`);
      return;
    }
    this.spend(cost);
    t.evolveTo(toId);
    audio.upgrade();
    if (t.isMax && TOP_TIER_CALLOUTS[t.def.id]) {
      if (!voice.play(this, topTierVoiceId(t.def.id), { priority: true, cooldown: 0 })) {
        voice.playCategory(this, 'evolve', { cooldown: 0 });
      }
    } else {
      voice.playCategory(this, 'evolve', { cooldown: 0 });
    }
    this.vfx.burst(t.x, t.y, def.colour, 22, { speed: 150, life: 0.5, r: 3 });
    this.vfx.ring(t.x, t.y, 56, def.colour, 4, 0.5);
    this.vfx.flash(t.x, t.y, 26, 0xffffff, 'charge', 0, 0.25);
    this.showMessage(`${def.short} ONLINE`);
    this.refreshHud();
    this.towerMenu.refresh();
  }

  private attachSymbiosis(t: Tower, moduleId: string): void {
    const mod = SYMBIOSIS[moduleId];
    if (!mod) return;
    if (!researchManager.isSymbiosisUnlocked(moduleId) && t.secondary !== moduleId) {
      audio.deny();
      this.showMessage('SYMBIOSIS MODULE LOCKED  ·  RESEARCH THE ADDON FIRST', false);
      return;
    }
    const isUpgrade = t.secondary === moduleId;
    const swapCredit = isUpgrade ? 0 : t.secondarySwapCredit(moduleId);
    const cost = isUpgrade ? t.secondaryUpgradeCost : t.secondarySwapCost(moduleId);
    if (cost == null || !this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`NEED ${cost ?? 0} SUPPLY FOR ${isUpgrade ? `${mod.name} TUNING` : mod.name}`);
      return;
    }
    this.spend(cost);
    if (isUpgrade) t.upgradeSecondary();
    else t.attachSecondary(moduleId);
    audio.upgrade();
    const comboName = SYMBIOSIS_COMBO_NAMES[comboKey(t.def.id, moduleId)];
    if (!isUpgrade && comboName) {
      if (!voice.play(this, comboVoiceId(t.def.id, moduleId), { priority: true, cooldown: 0 })) {
        voice.playCategory(this, 'symbiosis', { cooldown: 0 });
      }
    } else {
      voice.playCategory(this, 'symbiosis', { cooldown: 0 });
    }
    this.vfx.burst(t.x, t.y, mod.colour, 16, { speed: 120, life: 0.4, r: 2.5 });
    this.vfx.ring(t.x, t.y, 44, mod.colour, 3, 0.45);
    this.showMessage(
      isUpgrade
        ? `${mod.name} TUNED TO T${t.secondaryLevel}/3`
        : comboName
          ? `${comboName.toUpperCase()}  ·  ${mod.name} BOLTED ON`
        : swapCredit > 0
          ? `${mod.name} SWAPPED — 75% CREDIT -$${swapCredit}`
          : `${mod.name} BOLTED ON`,
    );
    this.refreshHud();
    this.towerMenu.refresh();
  }

  private tryTowerSpecial(t: Tower): void {
    if (!t.canSpecial()) {
      if (t.isMax) {
        audio.deny();
        this.showMessage(`SPECIAL ON COOLDOWN ${t.specialCooldown.toFixed(0)}s`);
      }
      return;
    }
    const id = t.def.id;
    if (id === 'artillery' || id === 'energy') {
      this.placeMode = id === 'artillery' ? 'special-artillery' : 'special-energy';
      this.showMessage(
        id === 'artillery'
          ? 'SATURATION BOMBARDMENT — click target zone'
          : 'ENERGY OVERLOAD — click target',
      );
      return;
    }
    // Instant actives
    t.activateSpecial();
    audio.special();
    if (id === 'thermal') {
      this.thermalPurge(t);
    }
    this.vfx.ring(t.x, t.y, t.stage.specialRange ?? 100, t.def.colour, 4, 0.45);
    this.showMessage(`${t.stage.specialName} ACTIVE`);
    this.refreshHud();
  }

  private thermalPurge(t: Tower): void {
    const range = t.stage.specialRange ?? 100;
    const dps = t.stage.specialDamageMul ? t.damage * 2.5 : t.damage * 2;
    for (const e of this.enemies) {
      if (!e.alive) continue;
      if (Phaser.Math.Distance.Between(t.x, t.y, e.x, e.y) <= range) {
        const dealt = e.applyHit(dps, 'THERMAL', 0.2, 0, String(t.id), 1);
        this.recordTowerDamage(String(t.id), dealt);
        e.applyStatuses([{ kind: 'BURN', duration: 3, magnitude: 12, sourceId: String(t.id) }]);
      }
    }
    this.addDecal({ x: t.x, y: t.y, r: range * 0.6, a: 0.35, scorch: true });
    this.vfx.burst(t.x, t.y, COLORS.thermal, 28, { speed: 140, life: 0.5, r: 4 });
    this.vfx.flash(t.x, t.y, 50, 0xffaa44, 'blast', 0, 0.25);
    this.shake = 8;
  }

  // ---------- ABILITIES ----------

  private tryAbility(id: string): void {
    if (this.gameOver) return;
    const def = ABILITIES.find((a) => a.id === id);
    if (!def) return;
    if (this.abilityCd[id] > 0) {
      audio.deny();
      return;
    }
    if (id === 'airstrike') {
      this.placeMode = 'airstrike';
      this.showMessage('ORBITAL SPEAR — click target');
      audio.click();
      return;
    }
    if (id === 'barrier') {
      this.placeMode = 'barrier';
      this.showMessage('HARDLIGHT BARRIER — click the path; enemies stop and attack its HP');
      audio.click();
      return;
    }
    if (id === 'rollingdeath') {
      this.deployRollingDeath();
      return;
    }
    if (id === 'emp') {
      this.abilityCd.emp = this.abilityCooldown('emp', def.cooldown);
      const duration = 3.5 * this.research.empDurationMul;
      const cx = this.mapDef.command.x;
      const cy = this.mapDef.command.y;
      for (const e of this.enemies) {
        if (!e.alive) continue;
        e.stunT = Math.max(e.stunT, duration);
        e.applyStatuses([{ kind: 'STUN', duration, magnitude: 1, sourceId: 'emp' }]);
      }
      // EMP reads as a field pulse: a bright launch flash, a large expanding
      // wave, then a visible residual field while the stun wears off.
      this.markers.push({ x: cx, y: cy, t: 4.8, kind: 'emp', max: 4.8 });
      this.vfx.ring(cx, cy, 700, COLORS.energy, 7, 1.25);
      this.vfx.ring(cx, cy, 560, 0xd19cff, 3, 2.6);
      this.vfx.flash(cx, cy, 92, 0xc477ff, 'blast', 0, 0.55);
      this.vfx.burst(cx, cy, 0xb978ff, 32, { speed: 230, life: 0.85, r: 2.4, kind: 'plasma' });
      audio.special();
      this.showMessage('EMP LANCE — all hostiles stunned');
      voice.play(this, 'ability_emp', { cooldown: 6 });
      achievements.noteAbility();
      this.shake = 10;
      this.refreshHud();
    }
  }

  private cycleDroneMove(): void {
    this.repositionDrone();
  }

  /** Drone movement is a real tactical ability: it now honours its researchable cooldown. */
  private repositionDrone(): void {
    if (this.abilityCd.drone > 0) {
      audio.deny();
      return;
    }
    const p = this.input.activePointer;
    const wp = this.screenToWorld(p.x, p.y);
    if (p.x < BATTLE_W && p.y > UI_TOP_H && p.y < GAME_HEIGHT - UI_BOTTOM_H) {
      this.drone.moveTo(wp.x, wp.y);
      const def = ABILITIES.find((ability) => ability.id === 'drone');
      this.abilityCd.drone = this.abilityCooldown('drone', def?.cooldown ?? 8);
      audio.click();
      voice.play(this, 'ability_drone', { cooldown: 12 });
      this.showMessage(`Kite-3 redeployed — ${this.drone.mode}`);
      achievements.noteAbility();
      this.refreshHud();
    } else {
      this.showMessage('Kite-3: move the cursor over the battlefield, then press E.');
    }
  }

  private deployAirstrike(x: number, y: number): void {
    this.abilityCd.airstrike = this.abilityCooldown('airstrike', ABILITIES[0].cooldown);
    this.placeMode = 'none';
    this.markers.push({ x, y, t: 1.2, kind: 'airstrike', max: 1.2 });
    this.showMessage('SPEAR INBOUND');
    voice.play(this, 'ability_spear', { cooldown: 6 });
    achievements.noteAbility();
    this.time.delayedCall(1200, () => {
      this.doAirstrike(x, y);
    });
    this.refreshHud();
  }

  private doAirstrike(x: number, y: number): void {
    const radius = 95 * this.research.airstrikeRadiusMul;
    const dmg = 560 * this.research.airstrikeDamageMul;
    let killed = 0;
    for (const e of this.enemies) {
      if (!e.alive) continue;
      if (Phaser.Math.Distance.Between(x, y, e.x, e.y) <= radius) {
        e.applyHit(dmg, 'EXPLOSIVE', 0.5, 0.2, 'airstrike', 1);
        if (!e.alive) {
          killed++;
          this.onEnemyKilled(e, false);
        }
      }
    }
    // Orbital Spear has its own violet ground signature so the impact is
    // readable against the warm map palette and its target reticle.
    this.vfx.artilleryImpact(x, y, radius, 0, false);
    this.vfx.flash(x, y, radius * 0.95, 0xc477ff, 'blast', 0, 0.5);
    this.vfx.flash(x, y, radius * 0.42, 0xffffff, 'blast', 0, 0.22);
    this.vfx.ring(x, y, radius * 1.05, 0xd7a5ff, 7, 0.9);
    this.vfx.ring(x, y, radius * 0.62, 0xffffff, 2.5, 0.65);
    this.vfx.burst(x, y, 0xd8a4ff, 26, { speed: 210, life: 0.8, r: 2.8, kind: 'plasma' });
    this.vfx.burst(x, y, 0x8c6aaf, 14, { speed: 125, life: 0.65, r: 2.6, kind: 'debris', gravity: 55 });
    this.addDecal({ x, y, r: radius * 0.7, a: 0.45, scorch: true });
    if (this.research.airstrikeFireDuration > 0) {
      const fireRadius = radius * 0.78;
      this.markers.push({
        x,
        y,
        t: this.research.airstrikeFireDuration,
        max: this.research.airstrikeFireDuration,
        kind: 'airstrike-fire',
        radius: fireRadius,
      });
      this.showMessage(`THERMAL WAKE  ·  ${this.research.airstrikeFireDuration.toFixed(0)}s FIRE ZONE`, false);
    }
    this.shake = 16;
    audio.explosion();
    if (killed >= 8) this.showMessage(`SPEAR IMPACT — ${killed} KILLS`);
  }

  /** Refresh a low-cost burn status while hostiles remain inside a Spear fire patch. */
  private updateSpearFire(dt: number): void {
    const patches = this.markers.filter((marker) => marker.kind === 'airstrike-fire');
    if (!patches.length) return;
    for (const patch of patches) {
      const radius = patch.radius ?? 74;
      for (const enemy of this.enemies) {
        if (!enemy.alive || Phaser.Math.Distance.Between(patch.x, patch.y, enemy.x, enemy.y) > radius) continue;
        enemy.applyStatuses([{
          kind: 'BURN',
          duration: Math.max(0.2, dt * 2.5),
          magnitude: this.research.airstrikeFireDps,
          sourceId: 'airstrike-fire',
        }]);
      }
    }
  }

  private deployBarrier(x: number, y: number): void {
    const cost = scaleSupplyCost((ABILITIES[1].cost ?? 30) * this.research.barrierCostMul);
    if (!this.canSpend(cost)) {
      audio.deny();
      this.showMessage(`BARRIER NEEDS ${cost} SUPPLY`);
      return;
    }
    const barrierArgs = [
      Math.round(450 * this.research.barrierHpMul),
      18 * this.research.barrierDurationMul,
    ] as const;
    const candidates = this.routeCaches
      .map((route) => placeBarrierOnPath(x, y, route, ...barrierArgs))
      .filter((candidate): candidate is Barrier => !!candidate);
    const b = candidates.sort(
      (a, z) => Phaser.Math.Distance.Between(x, y, a.x, a.y) - Phaser.Math.Distance.Between(x, y, z.x, z.y),
    )[0];
    if (!b) {
      audio.deny();
      this.showMessage('BARRIER MUST BE ON THE PATH');
      return;
    }
    this.spend(cost);
    this.barriers.push(b);
    this.abilityCd.barrier = this.abilityCooldown('barrier', ABILITIES[1].cooldown);
    this.placeMode = 'none';
    audio.place();
    this.showMessage('HARDLIGHT DEPLOYED');
    voice.play(this, 'ability_barrier', { cooldown: 6 });
    achievements.noteAbility();
    this.refreshHud();
  }

  private toggleRollingDeathMode(): void {
    if (!this.research.rollingBombSnowUnlocked) {
      this.showMessage('ROLLING DEATH: RESEARCH CRYO CONVERSION TO UNLOCK SNOW MODE');
      audio.deny();
      return;
    }
    this.rollingBombMode = this.rollingBombMode === 'explosive' ? 'snow' : 'explosive';
    audio.click();
    this.showMessage(`ROLLING DEATH MODE → ${this.rollingBombMode === 'snow' ? 'SNOW / 5s SLOW' : 'EXPLOSIVE'}`);
    this.refreshHud();
  }

  private deployRollingDeath(): void {
    if (this.abilityCd.rollingdeath > 0) {
      audio.deny();
      return;
    }
    const lanes = Math.max(1, this.routeCaches.length);
    const total = Math.max(1, Math.round(this.research.rollingBombCount));
    const base = this.mapDef.command;
    let launched = 0;
    for (let lane = 0; lane < lanes; lane++) {
      const laneCount = Math.floor(total / lanes) + (lane < total % lanes ? 1 : 0);
      for (let i = 0; i < laneCount; i++) {
        this.rollingBombQueue.push({ routeIndex: lane, delay: launched * 0.22 });
        launched++;
      }
    }
    this.rollingBombQueue.sort((a, b) => a.delay - b.delay);
    this.abilityCd.rollingdeath = this.abilityCooldown('rollingdeath', 75);
    this.vfx.flash(base.x, base.y, 32, 0xff8b32, 'blast', 0, 0.22);
    this.vfx.ring(base.x, base.y, 54, 0xff8b32, 3, 0.35);
    this.showMessage(`ROLLING DEATH — ${total} ${this.rollingBombMode === 'snow' ? 'SNOW' : 'EXPLOSIVE'} BOMBS INBOUND`);
    audio.special();
    achievements.noteAbility();
    this.refreshHud();
  }

  private updateRollingDeath(dt: number): void {
    for (let i = this.rollingBombQueue.length - 1; i >= 0; i--) {
      const item = this.rollingBombQueue[i];
      item.delay -= dt;
      if (item.delay > 0) continue;
      this.rollingBombQueue.splice(i, 1);
      const cache = this.routeCaches[item.routeIndex] ?? this.path;
      const atBase = samplePath(cache, cache.total);
      const bomb: RollingBombState = {
        id: nextEntityId(), routeIndex: item.routeIndex, travel: 0, speed: 230,
        x: atBase.x, y: atBase.y, angle: atBase.angle + Math.PI,
        mode: this.rollingBombMode, active: true,
      };
      this.rollingBombs.push(bomb);
      this.vfx.flash(bomb.x, bomb.y, 18, bomb.mode === 'snow' ? 0x8fdcff : 0xff9b38, 'muzzle', bomb.angle, 0.18);
      this.vfx.burst(bomb.x, bomb.y, bomb.mode === 'snow' ? 0x9eeaff : 0xff8b32, 8, { speed: 75, life: 0.35, r: 2.5 });
    }

    for (let i = this.rollingBombs.length - 1; i >= 0; i--) {
      const bomb = this.rollingBombs[i];
      if (!bomb.active) {
        this.rollingBombs.splice(i, 1);
        continue;
      }
      const cache = this.routeCaches[bomb.routeIndex] ?? this.path;
      bomb.travel += bomb.speed * dt;
      if (bomb.travel >= cache.total - 2) {
        bomb.active = false;
        continue;
      }
      const point = samplePath(cache, cache.total - bomb.travel);
      bomb.x = point.x;
      bomb.y = point.y;
      bomb.angle = point.angle + Math.PI;
      this.vfx.dust(bomb.x, bomb.y + 4, bomb.mode === 'snow' ? 0x9edfff : 0xd07a36, 1);
      const hitRadius = bomb.mode === 'snow' ? 30 : 34;
      const hit = this.enemies.find((enemy) => enemy.alive && Phaser.Math.Distance.Between(bomb.x, bomb.y, enemy.x, enemy.y) <= enemy.def.radius + hitRadius);
      if (hit) this.detonateRollingBomb(bomb);
    }
  }

  private detonateRollingBomb(bomb: RollingBombState): void {
    const radius = bomb.mode === 'snow' ? 54 : 72;
    const colour = bomb.mode === 'snow' ? 0x8fdcff : 0xff8b32;
    let kills = 0;
    for (const enemy of this.enemies) {
      if (!enemy.alive || Phaser.Math.Distance.Between(bomb.x, bomb.y, enemy.x, enemy.y) > radius) continue;
      if (bomb.mode === 'snow') {
        enemy.applyHit(45 * ROLLING_DEATH_DAMAGE_MULTIPLIER, 'ENERGY', 0.1, 0, `rolling-death-${bomb.id}`, 1);
        enemy.applyStatuses([{ kind: 'SLOW', duration: Math.max(5, this.research.rollingBombSlowDuration), magnitude: 0.58, sourceId: `rolling-death-${bomb.id}` }]);
      } else {
        enemy.applyHit(420 * ROLLING_DEATH_DAMAGE_MULTIPLIER, 'EXPLOSIVE', 0.35, 0.1, `rolling-death-${bomb.id}`, 1);
        if (!enemy.alive) { kills++; this.onEnemyKilled(enemy, false); }
      }
    }
    // Rolling Death should leave a compact mechanical blast and dust wake,
    // rather than a tall smoke plume that reads like a ghost on the route.
    this.vfx.artilleryImpact(bomb.x, bomb.y, radius, 0);
    this.vfx.ring(bomb.x, bomb.y, radius, colour, 4, 0.45);
    this.vfx.flash(bomb.x, bomb.y, radius * 0.75, colour, 'blast', 0, 0.28);
    this.vfx.burst(bomb.x, bomb.y, colour, 18, { speed: 180, life: 0.55, r: 3.5 });
    this.addDecal({ x: bomb.x, y: bomb.y, r: radius * 0.58, a: 0.36, scorch: bomb.mode === 'explosive' });
    if (kills > 0) this.showMessage(`ROLLING DEATH IMPACT — ${kills} KILL${kills === 1 ? '' : 'S'}`);
    audio.explosion();
    bomb.active = false;
  }

  private fireArtillerySpecial(x: number, y: number): void {
    const t = this.selectedTower;
    if (!t) {
      this.placeMode = 'none';
      return;
    }
    t.activateSpecial();
    this.placeMode = 'none';
    audio.special();
    this.markers.push({ x, y, t: 0.8, kind: 'artillery', max: 0.8 });
    // 5 shells
    for (let i = 0; i < 5; i++) {
      this.time.delayedCall(i * 180, () => {
        const ox = x + (Math.random() - 0.5) * 70;
        const oy = y + (Math.random() - 0.5) * 70;
        this.projectiles.fire({
          x: t.x,
          y: t.y,
          tx: ox,
          ty: oy,
          damage: t.damage * (t.stage.specialDamageMul ?? 1.5),
          colour: COLORS.artillery,
          speed: 420,
          splash: t.stage.splash ?? 70,
          kind: 'shell',
          damageType: 'EXPLOSIVE',
          sourceId: String(t.id),
          armourPen: t.stage.armourPen ?? 0,
        });
      });
    }
    this.showMessage('SATURATION BOMBARDMENT');
  }

  private fireEnergySpecial(x: number, y: number): void {
    const t = this.selectedTower;
    if (!t) {
      this.placeMode = 'none';
      return;
    }
    t.activateSpecial();
    this.placeMode = 'none';
    audio.special();
    // Hit nearest enemy to click in range
    let best: Enemy | null = null;
    let bestD = 40;
    for (const e of this.enemies) {
      if (!e.alive) continue;
      const d = Phaser.Math.Distance.Between(x, y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    if (!best) {
      // hit ground
      this.projectiles.fire({
        x: t.x,
        y: t.y,
        tx: x,
        ty: y,
        damage: t.damage * (t.stage.specialDamageMul ?? 5),
        colour: COLORS.energy,
        speed: 900,
        splash: 50,
        kind: 'beam',
        damageType: 'ENERGY',
        sourceId: String(t.id),
        armourPen: 0.6,
      });
    } else {
      this.projectiles.fire({
        x: t.x,
        y: t.y,
        tx: best.x,
        ty: best.y,
        damage: t.damage * (t.stage.specialDamageMul ?? 5),
        colour: COLORS.energy,
        speed: 900,
        splash: 60,
        kind: 'beam',
        damageType: 'ENERGY',
        sourceId: String(t.id),
        armourPen: 0.6,
      });
    }
    this.shake = 10;
    this.showMessage('ENERGY OVERLOAD');
  }

  // ---------- INPUT ----------

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.gameOver) return;
    audio.unlock();
    // Screen coords for HUD/region tests, world coords for map interactions.
    const sx = pointer.x;
    const sy = pointer.y;
    const wp = this.screenToWorld(sx, sy);
    const x = wp.x;
    const y = wp.y;
    const overBattle = sx >= 0 && sx < BATTLE_W && sy >= UI_TOP_H && sy < GAME_HEIGHT - UI_BOTTOM_H;

    // Right-click is a fast tactical command: use it to cycle a tower's
    // targeting priority, or cancel any active placement/selection on empty
    // ground. This keeps the left click flow focused on building and actions.
    if (pointer.button === 2) {
      this.handleRightClick(x, y, overBattle);
      return;
    }

    // Floating tower menu takes precedence over map clicks.
    if (this.towerMenu?.isOpen) {
      if (this.towerMenu.contains(sx, sy)) return;
      this.towerMenu.close();
    }

    // Loot is deliberately checked before build/select interactions. A cache
    // on the battlefield should always be collectible with a direct click.
    if (overBattle) {
      const lootIndex = this.loot.findIndex(
        (drop) => Phaser.Math.Distance.Between(x, y, drop.x, drop.y) < 22,
      );
      if (lootIndex >= 0) {
        this.collectLoot(lootIndex);
        return;
      }
    }

    if (this.placeMode === 'build' && overBattle) {
      const built = this.tryBuildAt(x, y);
      if (!built) {
        const hit = this.towers.find((t) => Phaser.Math.Distance.Between(x, y, t.x, t.y) < 18);
        if (hit) {
          this.selectedTower = hit;
          this.selectedEnemy = null;
          this.baseSelected = false;
          this.hideBaseTooltip();
          this.placeMode = 'none';
          audio.click();
          this.refreshHud();
        }
      }
      return;
    }
    if (this.placeMode === 'airstrike' && overBattle) {
      this.deployAirstrike(x, y);
      return;
    }
    if (this.placeMode === 'barrier' && overBattle) {
      this.deployBarrier(x, y);
      return;
    }
    if (this.placeMode === 'special-artillery' && overBattle) {
      this.fireArtillerySpecial(x, y);
      return;
    }
    if (this.placeMode === 'special-energy' && overBattle) {
      this.fireEnergySpecial(x, y);
      return;
    }

    // Select tower
    if (overBattle) {
      let hit: Tower | null = null;
      for (const t of this.towers) {
        if (Phaser.Math.Distance.Between(x, y, t.x, t.y) < 18) {
          hit = t;
          break;
        }
      }
      if (hit) {
        this.hideBaseContextMenu();
        this.selectedTower = hit;
        this.selectedEnemy = null;
        this.baseSelected = false;
        this.hideBaseTooltip();
        audio.click();
        this.refreshHud();
        this.towerMenu.open(hit);
        return;
      }
      const command = this.mapDef.command;
      if (Phaser.Math.Distance.Between(x, y, command.x, command.y) < 66) {
        this.hideBaseContextMenu();
        this.selectedTower = null;
        this.selectedEnemy = null;
        this.baseSelected = true;
        this.placeMode = 'none';
        this.towerMenu.close();
        this.showBaseTooltip();
        audio.click();
        this.refreshHud();
        return;
      }
      let eh: Enemy | null = null;
      for (const e of this.enemies) {
        if (!e.alive) continue;
        if (Phaser.Math.Distance.Between(x, y, e.x, e.y) < e.def.radius + 6) {
          eh = e;
          break;
        }
      }
      this.selectedEnemy = eh;
      this.hideBaseContextMenu();
      this.selectedTower = null;
      this.baseSelected = false;
      this.hideBaseTooltip();
      this.refreshHud();
      return;
    }

    // Right panel actions via dedicated buttons (see refreshPanelButtons)
    if (x >= BATTLE_W) {
      // buttons handle themselves
    }
  }

  private handleRightClick(x: number, y: number, overBattle: boolean): void {
    if (!overBattle) return;

    const hit = this.towers.find((tower) => Phaser.Math.Distance.Between(x, y, tower.x, tower.y) < 18);
    if (hit) {
      this.hideBaseContextMenu();
      this.placeMode = 'none';
      this.selectedTower = hit;
      this.selectedEnemy = null;
      this.baseSelected = false;
      this.hideBaseTooltip();
      this.cyclePriorityTower(hit);
      this.towerMenu.open(hit);
      this.refreshHud();
      return;
    }

    const command = this.mapDef.command;
    if (Phaser.Math.Distance.Between(x, y, command.x, command.y) < 66) {
      this.placeMode = 'none';
      this.selectedTower = null;
      this.selectedEnemy = null;
      this.baseSelected = true;
      this.towerMenu?.close();
      this.hideBaseTooltip();
      this.showBaseUpgradeMenu();
      this.refreshHud();
      return;
    }

    this.placeMode = 'none';
    this.selectedTower = null;
    this.selectedEnemy = null;
    this.baseSelected = false;
    this.hideBaseTooltip();
    this.hideBaseContextMenu();
    this.towerMenu?.close();
    this.hideBuildTooltip();
    this.redrawPads();
    this.refreshHud();
  }

  private adjustGameSpeed(delta: -1 | 1): void {
    const current = BattleScene.SIM_SPEEDS.findIndex((speed) => speed === this.gameSpeed);
    const index = current >= 0 ? current : 1;
    const next = Phaser.Math.Clamp(index + delta, 0, BattleScene.SIM_SPEEDS.length - 1);
    this.gameSpeed = BattleScene.SIM_SPEEDS[next];
    this.time.timeScale = this.gameSpeed;
    this.txtGameSpeed.setText(`SIM ${BattleScene.formatSpeed(this.gameSpeed)}`);
    audio.click();
    this.showMessage(`SIMULATION SPEED ${BattleScene.formatSpeed(this.gameSpeed)}`);
  }

  // ---------- UPDATE ----------

  update(time: number, delta: number): void {
    // Keep each simulation step at or below 50ms. At 4x/5x this runs two or
    // three bounded substeps per rendered frame instead of one unstable leap.
    const simDelta = Math.min(0.4, Math.max(0, delta / 1000) * this.gameSpeed);
    const substeps = Math.max(1, Math.ceil(simDelta / 0.05));
    let dt = simDelta / substeps;
    if (this.missionExitOpen) return;
    if (this.gameOver) {
      this.syncSprites();
      this.drawEntities();
      return;
    }
    this.realElapsed += Math.max(0, delta) / 1000;
    this.simElapsed += simDelta;
    this.autosaveClock -= Math.max(0, delta) / 1000;
    if (this.autosaveClock <= 0) {
      this.saveActiveRun();
      this.autosaveClock = 4;
    }

    // Hit-stop juice on heavy impacts (toggleable in the field manual)
    if (this.hitstop > 0) {
      this.hitstop -= delta / 1000;
      if (saveManager.data.settings.slowmo) dt *= 0.15;
    }

    if (this.survival) this.survivalTime += simDelta;

    // Music intensity from threat
    const hostiles = this.enemies.filter((e) => e.alive).length;
    const elites = this.enemies.filter((e) => e.alive && (e.isElite || e.isBoss)).length;
    const pressure = this.waveActive
      ? 0.35 + Math.min(0.45, hostiles / 28) + Math.min(0.2, elites * 0.08)
      : 0.12 + this.waveIndex * 0.02;
    music.setIntensity(Math.min(1, pressure));

    for (let step = 0; step < substeps; step++) {
      this.updateWaves(dt);
      this.updateSpearFire(dt);
      this.updateEnemies(dt);
      this.updateEnemyShots(dt);
      this.updateTowers(dt);
      this.updateProjectiles(dt);
      this.updateRollingDeath(dt);
      this.updateBarriers(dt);
      this.updateAbilityCds(dt);
      this.updateVfx(dt);
      this.updateDamageNums(dt);
      this.updateLoot(dt);
      this.updateDecals(dt);
    }
    this.drawWeather(simDelta);
    this.animateWarpGates(simDelta);
    this.drawZones(time);
    this.syncSprites();
    this.drawEntities();
    this.drawOverlay();
    this.drawHudMotion(time);
    this.refreshHudSoft(simDelta);

    if (this.bannerT > 0) {
      this.bannerT -= simDelta;
      const elapsed = 2.2 - this.bannerT;
      const appear = Math.min(1, elapsed * 4);
      const fade = Math.min(1, Math.max(0, this.bannerT / 0.5));
      this.txtBanner.setAlpha(Math.min(appear, fade));
      this.txtBanner.setScale(1.22 - 0.22 * appear);
      if (this.bannerT <= 0) this.txtBanner.setAlpha(0);
    }

    if (this.cinematic > 0) this.cinematic -= simDelta;
    if (this.shake > 0 && saveManager.data.settings.shake) {
      this.shake = Math.max(0, this.shake - simDelta * 55);
    } else {
      this.shake = 0;
    }
    this.applyCamera();

    // Victory
    if (
      this.allWavesDone &&
      !this.gameOver &&
      this.waveIndex >= this.waveList.length &&
      !this.waveActive &&
      this.enemies.every((e) => !e.alive)
    ) {
      this.finish(true);
    }
  }

  private updateWaves(dt: number): void {
    if (!this.waveActive) return;
    this.spawnClock += dt;
    while (this.spawnQueue.length && this.spawnQueue[0].t <= this.spawnClock) {
      const item = this.spawnQueue.shift()!;
      this.spawnEnemy(item.def, 0, item.lane, undefined, item.formationSlot, item.formationSize);
    }
    if (!this.spawnQueue.length && this.enemies.every((e) => !e.alive)) {
      this.waveActive = false;
      this.rushesUsed = 0;
      if (this.survival) achievements.noteSurvivalWaveCleared(this.waveIndex, this.difficulty);
      const clearBonus = Math.floor((25 + this.waveIndex * 5) * this.research.earlyWaveBonusMul * CREDIT_REWARD_MULTIPLIER * COIN_REWARD_MULTIPLIER);
      const tunedClearBonus = Math.floor(clearBonus * this.devCreditMul);
      this.supply += tunedClearBonus;
      this.score += tunedClearBonus * 2;
      bus.emit(Events.WaveClear, this.waveIndex);
      this.showMessage(`WAVE ${this.waveIndex} CLEAR  +${tunedClearBonus} SUPPLY`);
      announcer.say('waveClear');
      if (this.integrity >= this.waveStartIntegrity && this.integrity > 0) {
        // Flawless-wave praise is rationed so it stays special.
        if (this.perfectSaid < 2 && voice.playCategory(this, 'perfect', { cooldown: 8 })) {
          this.perfectSaid++;
        }
      } else {
        voice.playCategory(this, 'waveclear', { cooldown: 8 });
      }
      if (!this.survival && this.waveIndex >= this.waveList.length) {
        this.allWavesDone = true;
      } else {
        this.scheduleAutoWave();
      }
      // Wave state changes happen inside the simulation tick, so refresh the
      // command deck immediately.  Without this the READY/NEXT WAVE affordance
      // waited for an unrelated pointer interaction to repaint.
      this.refreshHud();
    }
  }

  private updateEnemies(dt: number): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      if (!e.alive) {
        // Damage-over-time can finish a unit between projectile impacts. Make
        // that death count for score, bounty and the weapon that applied it.
        if (!e.killRecorded) {
          const source = e.lastDamageSourceId;
          // The only scene-side damage that can finish a unit between hits is
          // the burn tick, so its achievement category is thermal even when
          // the burn came from a symbiosis module (`sec-*`).
          this.onEnemyKilled(e, true, 'THERMAL', source);
        }
        this.enemies.splice(i, 1);
        continue;
      }
      // Barrier block
      let blocked = false;
      for (const b of this.barriers) {
        if (!b.alive) continue;
        const d = Phaser.Math.Distance.Between(e.x, e.y, b.x, b.y);
        if (d < b.radius + e.def.radius) {
          blocked = true;
          b.takeDamage((e.armour > 10 ? 18 : 10) * e.powerMul * dt * (e.isBoss ? 4 : 1));
          break;
        }
      }
      if (!blocked) {
        const route = e.route ?? this.path;
        e.update(dt, route.total, (parent) => {
          this.spawnEnemy(ENEMIES[parent.def.spawnChild!], Math.max(0, parent.dist - 12), parent.lane, parent.route);
        });
      } else {
        // A barrier physically obstructs the route; contact enemies still
        // animate and tick statuses, but make almost no route progress while
        // they chew through its HP.
        e.update(dt * 0.08, (e.route ?? this.path).total);
      }

      // Boss phase message
      if (e.pendingPhase) {
        if (e.pendingPhase.message) this.showMessage(e.pendingPhase.message);
        if (e.pendingPhase.summon) {
          const n = e.pendingPhase.summonCount ?? 3;
          for (let k = 0; k < n; k++) {
            this.spawnEnemy(ENEMIES[e.pendingPhase.summon], Math.max(0, e.dist - 20 - k * 8), e.lane, e.route);
          }
        }
        if (e.pendingPhase.armourMul) e.def = { ...e.def, armour: e.def.armour * e.pendingPhase.armourMul };
        e.pendingPhase = null;
        this.shake = 8;
        audio.special();
      }

      // Nulltick jam
      if (e.def.stunTowers && e.stunT <= 0) {
        const r = e.def.stunRadius ?? 70;
        for (const t of this.towers) {
          if (Phaser.Math.Distance.Between(e.x, e.y, t.x, t.y) < r) t.jammed = 0.3;
        }
      }

      const p = samplePath(e.route ?? this.path, e.dist);
      const lateral = e.formationOffset;
      e.x = p.x - Math.sin(p.angle) * lateral;
      e.y = p.y + Math.cos(p.angle) * lateral;
      e.angle = p.angle;

      // Ranged raiders/machines suppress the nearest turret in reach. A small,
      // explicit subset launches visible counter-fire; the rest keep the
      // lighter instant suppression effect so the mechanic remains readable
      // and does not turn every wave into turret harassment.
      if (e.def.ranged) {
        e.fireTimer -= dt;
        if (e.fireTimer <= 0) {
          const reach = e.def.range ?? 140;
          let bestT: Tower | null = null;
          let bestD = reach;
          for (const t of this.towers) {
            const d = Phaser.Math.Distance.Between(e.x, e.y, t.x, t.y);
            if (d < bestD) {
              bestD = d;
              bestT = t;
            }
          }
          if (bestT) {
            e.fireTimer = e.def.fireInterval ?? 2.2;
            if (e.def.returnFire) {
              if (this.enemyShotClock <= 0) {
                this.enemyShotClock = 0.58;
                this.enemyShots.push({
                  id: nextEntityId(),
                  x: e.x,
                  y: e.y,
                  sx: e.x,
                  sy: e.y,
                  tx: bestT.x,
                  ty: bestT.y,
                  towerId: bestT.id,
                  age: 0,
                  life: 0.24,
                  colour: e.def.colour,
                });
                // A short muzzle flash sells the attack before the projectile
                // crosses the lane.
                this.vfx.flash(e.x, e.y, 7, e.def.colour, 'muzzle', e.angle, 0.08);
                audio.teslaFire();
              } else {
                // Hold this unit's next shot until the shared spacing window
                // opens rather than stacking several impacts in one frame.
                e.fireTimer = 0.28;
              }
            } else {
              bestT.jammed = Math.max(bestT.jammed, 0.6);
              this.vfx.beam(e.x, e.y, bestT.x, bestT.y, e.def.colour, 0.14, 2);
              this.vfx.flash(bestT.x, bestT.y, 9, e.def.colour, 'impact', 0, 0.1);
              audio.teslaFire();
            }
          } else {
            e.fireTimer = 0.4;
          }
        }
      }

      // Wounded units vent smoke — thicker as they get closer to dying.
      const hpFrac = e.maxHp > 0 ? e.hp / e.maxHp : 1;
      if (hpFrac < 0.66) {
        e.smokeTimer -= dt;
        if (e.smokeTimer <= 0) {
          e.smokeTimer = hpFrac < 0.33 ? 0.13 : 0.38;
          this.vfx.spawn({
            x: e.x + (Math.random() - 0.5) * e.def.radius,
            y: e.y - e.def.radius * 0.4,
            vx: (Math.random() - 0.5) * 10,
            vy: -18 - Math.random() * 14,
            kind: 'smoke',
            c: hpFrac < 0.33 ? 0x3a2a26 : 0x33383a,
            maxLife: 0.7 + Math.random() * 0.5,
            r: 3 + Math.random() * 3,
            drag: 0.97,
            alpha0: 0.34,
          });
        }
      }

      if (e.dist >= (e.route ?? this.path).total - 1) {
        e.markBreached();
        this.onBreach(e);
        this.enemies.splice(i, 1);
      }
    }
    this.resolveEnemyFormationCollisions();
  }

  /** Keep same-lane units from occupying the same road space in a formation. */
  private resolveEnemyFormationCollisions(): void {
    const active = this.enemies.filter((enemy) => enemy.alive && enemy.route).sort((a, b) => b.dist - a.dist);
    for (let i = 0; i < active.length; i++) {
      const leader = active[i];
      for (let j = i + 1; j < active.length; j++) {
        const follower = active[j];
        if (leader.route !== follower.route) continue;
        const gap = leader.dist - follower.dist;
        if (gap < 0 || gap > 90) continue;
        const lateralGap = Math.abs(leader.formationOffset - follower.formationOffset);
        const requiredLateral = leader.def.radius + follower.def.radius + 3;
        if (lateralGap >= requiredLateral) continue;
        const requiredGap = Math.max(8, leader.def.radius + follower.def.radius - lateralGap * 0.35);
        if (gap >= requiredGap) continue;
        follower.dist = Math.max(0, leader.dist - requiredGap);
        const p = samplePath(follower.route ?? this.path, follower.dist);
        follower.x = p.x - Math.sin(p.angle) * follower.formationOffset;
        follower.y = p.y + Math.cos(p.angle) * follower.formationOffset;
        follower.angle = p.angle;
      }
    }
  }

  private updateEnemyShots(dt: number): void {
    this.enemyShotClock = Math.max(0, this.enemyShotClock - dt);
    for (let i = this.enemyShots.length - 1; i >= 0; i--) {
      const shot = this.enemyShots[i];
      shot.age += dt;
      const progress = Phaser.Math.Clamp(shot.age / shot.life, 0, 1);
      shot.x = Phaser.Math.Linear(shot.sx, shot.tx, progress);
      shot.y = Phaser.Math.Linear(shot.sy, shot.ty, progress);
      if (shot.age < shot.life) continue;

      const tower = this.towers.find((candidate) => candidate.id === shot.towerId);
      if (tower) {
        // Counter-fire now creates persistent pressure. The tower's repair
        // state is visible in-world, while the jam keeps the hit readable.
        tower.takeDamage(10);
        tower.jammed = Math.max(tower.jammed, 1.05);
        tower.flash = Math.max(tower.flash, 0.16);
        this.vfx.flash(tower.x, tower.y, 12, shot.colour, 'impact', 0, 0.16);
        this.vfx.burst(tower.x, tower.y, shot.colour, 5, { speed: 52, life: 0.22, r: 1.8 });
      }
      this.enemyShots.splice(i, 1);
    }
  }

  private updateTowers(dt: number): void {
    // Recompute symbiosis aura buffs (Shield Emitter).
    for (const t of this.towers) t.auraRateBonus = 0;
    for (const t of this.towers) {
      const sd = t.secondaryDef;
      if (sd?.auraRange && sd.auraRate) {
        for (const o of this.towers) {
          if (o.id === t.id) continue;
          if (Phaser.Math.Distance.Between(t.x, t.y, o.x, o.y) <= sd.auraRange) {
            o.auraRateBonus = Math.max(o.auraRateBonus, sd.auraRate);
          }
        }
      }
    }

    for (const t of this.towers) {
      t.update(dt);
      if (t.isDisabled) {
        this.updateSecondary(t);
        continue;
      }
      if (t.def.id === 'dronehive') {
        this.updateSwarmTower(t, dt);
        this.updateSecondary(t);
        continue;
      }
      const target = t.pickTarget(this.enemies);
      if (target) {
        t.targetAngle = Math.atan2(target.y - t.y, target.x - t.x);
        if (t.cooldown <= 0) {
          t.cooldown = t.fireInterval;
          this.fireTower(t, target);
        }
      }
      this.updateSecondary(t);
    }
  }

  private updateSwarmTower(t: Tower, dt: number): void {
    const wanted = Math.min(6, this.getSwarmDroneCount(t) + (this.rankPerks?.droneWingBonus ?? 0));
    let swarm = this.swarmDrones.get(t.id);
    if (!swarm) {
      swarm = [];
      this.swarmDrones.set(t.id, swarm);
    }
    while (swarm.length < wanted) {
      const index = swarm.length;
      swarm.push({
        id: nextEntityId(),
        x: t.x,
        y: t.y,
        orbit: index * (Math.PI * 2 / Math.max(1, wanted)),
        phase: 'docked',
        weapon: (['machine-gun', 'bomb', 'micro-missile'] as SwarmDroneWeapon[])[index % 3],
        targetId: null,
        fireCooldown: 0,
        sortieTime: 0,
        fireFlash: 0,
        angle: 0,
        cohesion: false,
      });
    }
    if (swarm.length > wanted) swarm.splice(wanted);

    const assigned = new Set<number>();
    const nearbyWingmates = [...this.swarmDrones.entries()]
      .filter(([towerId]) => towerId !== t.id)
      .flatMap(([, drones]) => drones.filter((other) => other.phase !== 'docked'));
    // A ready hive launches its available wing together; each drone then
    // owns its target and flight path independently until it returns home.
    const launchWindow = t.cooldown <= 0 || t.specialActive > 0;
    for (const drone of swarm) {
      drone.fireFlash = Math.max(0, drone.fireFlash - dt);
      drone.orbit += dt * (1.5 + (drone.id % 3) * 0.18);
      drone.cohesion = false;
      if (drone.phase !== 'docked') {
        const wingmates = nearbyWingmatePositions(drone, nearbyWingmates);
        if (wingmates.length) {
          drone.cohesion = true;
          const cx = wingmates.reduce((sum, mate) => sum + mate.x, 0) / wingmates.length;
          const cy = wingmates.reduce((sum, mate) => sum + mate.y, 0) / wingmates.length;
          const pull = Math.min(1, dt * 1.35);
          drone.x += (cx - drone.x) * pull;
          drone.y += (cy - drone.y) * pull;
        }
      }
      const target = drone.targetId == null ? null : this.enemies.find((enemy) => enemy.id === drone.targetId) ?? null;

      if (drone.phase === 'docked') {
        const ring = 14 + (drone.id % 2) * 4;
        const dockX = t.x + Math.cos(drone.orbit) * ring;
        const dockY = t.y + Math.sin(drone.orbit) * ring * 0.62;
        this.moveSwarmDrone(drone, dockX, dockY, dt, 120);
        if (launchWindow) {
          const sortieTarget = this.pickSwarmTarget(t, assigned);
          if (sortieTarget) {
            drone.phase = 'sortie';
            drone.targetId = sortieTarget.id;
            drone.sortieTime = 0;
            drone.fireCooldown = 0.12;
            assigned.add(sortieTarget.id);
            t.cooldown = t.fireInterval;
            this.vfx.flash(t.x, t.y, 12, t.def.colour, 'charge', drone.orbit, 0.1);
          }
        }
        continue;
      }

      if (drone.phase === 'sortie') {
        drone.sortieTime += dt;
        if (!target || !target.alive || Phaser.Math.Distance.Between(t.x, t.y, target.x, target.y) > t.range * 1.15 || drone.sortieTime > 3.2) {
          drone.phase = 'returning';
          drone.targetId = null;
          continue;
        }
        drone.angle = Phaser.Math.Angle.Between(drone.x, drone.y, target.x, target.y);
        this.moveSwarmDrone(drone, target.x, target.y, dt, 210);
        drone.fireCooldown -= dt;
        if (drone.fireCooldown <= 0 && Phaser.Math.Distance.Between(drone.x, drone.y, target.x, target.y) < 34) {
          this.fireSwarmWeapon(t, drone, target);
          drone.fireCooldown = drone.weapon === 'machine-gun' ? 0.2 : drone.weapon === 'bomb' ? 1.15 : 0.82;
        }
        continue;
      }

      const homeAngle = drone.orbit + Math.PI;
      const homeX = t.x + Math.cos(homeAngle) * 12;
      const homeY = t.y + Math.sin(homeAngle) * 12 * 0.6;
      drone.angle = Phaser.Math.Angle.Between(drone.x, drone.y, homeX, homeY);
      this.moveSwarmDrone(drone, homeX, homeY, dt, 230);
      if (Phaser.Math.Distance.Between(drone.x, drone.y, t.x, t.y) < 16) {
        drone.phase = 'docked';
        drone.targetId = null;
      }
    }
  }

  private pickSwarmTarget(t: Tower, assigned: Set<number>): Enemy | null {
    const range2 = t.range * t.range;
    return this.enemies
      .filter((enemy) => {
        if (!enemy.alive || enemy.distDone || assigned.has(enemy.id)) return false;
        const dx = enemy.x - t.x;
        const dy = enemy.y - t.y;
        return dx * dx + dy * dy <= range2;
      })
      .sort((a, b) => b.dist - a.dist)[0] ?? null;
  }

  private moveSwarmDrone(drone: SwarmDroneState, tx: number, ty: number, dt: number, speed: number): void {
    const dx = tx - drone.x;
    const dy = ty - drone.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 0.5) return;
    const step = Math.min(distance, speed * dt);
    drone.x += (dx / distance) * step;
    drone.y += (dy / distance) * step;
  }

  private fireSwarmWeapon(t: Tower, drone: SwarmDroneState, target: Enemy): void {
    drone.fireFlash = 0.14;
    const sourceId = `swarm-${t.id}-${drone.id}`;
    const splashMul = this.research.splashMul.dronehive ?? 1;
    const critChance = this.research.critChanceAdd.drone ?? 0;
    if (drone.weapon === 'machine-gun') {
      this.projectiles.fire({
        x: drone.x,
        y: drone.y,
        tx: target.x,
        ty: target.y,
        damage: t.damage * 0.48 * this.research.droneDamageMul * (drone.cohesion ? 1.15 : 1),
        colour: 0xffd36a,
        speed: 680,
        splash: 0,
        kind: 'bullet',
        damageType: 'BALLISTIC',
        sourceId,
        armourPen: t.armourPen,
        critChance,
      });
      audio.autocannon();
      this.vfx.casing(drone.x, drone.y, drone.angle);
      return;
    }
    if (drone.weapon === 'bomb') {
      this.projectiles.fire({
        x: drone.x,
        y: drone.y,
        tx: target.x,
        ty: target.y,
        damage: t.damage * 1.15 * this.research.droneDamageMul * (drone.cohesion ? 1.15 : 1),
        colour: 0xff9c4d,
        speed: 300,
        splash: (t.stage.splash ?? 34) * splashMul,
        kind: 'cluster',
        damageType: 'EXPLOSIVE',
        sourceId,
        armourPen: t.armourPen,
        critChance,
      });
      audio.clusterLaunch();
      this.vfx.flash(drone.x, drone.y, 10, 0xff9c4d, 'muzzle', drone.angle, 0.1);
      return;
    }
    this.projectiles.fire({
      x: drone.x,
      y: drone.y,
      tx: target.x,
      ty: target.y,
      damage: t.damage * 0.92 * this.research.droneDamageMul * (drone.cohesion ? 1.15 : 1),
      colour: 0xff5e8c,
      speed: 390,
       splash: 18 * splashMul,
      kind: 'missile',
      damageType: 'EXPLOSIVE',
       sourceId,
       homing: true,
       targetId: target.id,
       armourPen: t.armourPen,
       critChance,
    });
    audio.missileLaunch();
    this.vfx.flash(drone.x, drone.y, 11, 0xff5e8c, 'muzzle', drone.angle, 0.1);
  }

  private updateSecondary(t: Tower): void {
    const sd = t.secondaryDef;
    if (!sd || sd.kind === 'support') return;
    const target = this.pickTargetInRange(t, t.secondaryRange);
    if (sd.kind === 'laser') {
      // The laser holds a continuous beam on its target between damage ticks.
      t.secondaryBeam = target ? { x: target.x, y: target.y } : null;
    }
    if (target) {
      t.secondaryTargetAngle = Math.atan2(target.y - t.y, target.x - t.x);
      if (t.secondaryCooldown <= 0) {
        t.secondaryCooldown = t.secondaryFireInterval;
        this.fireSecondary(t, target, sd);
      }
    }
  }

  private pickTargetInRange(t: Tower, range: number): Enemy | null {
    let best: Enemy | null = null;
    let bestDist = -1;
    const r2 = range * range;
    for (const e of this.enemies) {
      if (!e.alive || e.distDone) continue;
      const dx = e.x - t.x;
      const dy = e.y - t.y;
      if (dx * dx + dy * dy > r2) continue;
      if (e.dist > bestDist) {
        bestDist = e.dist;
        best = e;
      }
    }
    return best;
  }

  private fireSecondary(t: Tower, target: Enemy, sd: SymbiosisDef): void {
    t.secondaryFlash = 0.12;
    const off = 18;
    const perp = t.secondaryAngle + Math.PI / 2;
    const sx = t.x + Math.cos(perp) * off;
    const sy = t.y + Math.sin(perp) * off;

    if (sd.kind === 'arc') {
      this.fireChain(t, target, t.secondaryDamage, sd.chain ?? 1, sd.armourPen ?? 0, sd.colour, `sec-${t.id}`, sd.slowPct);
      this.vfx.flash(sx, sy, 12, 0xbbffff, 'charge', t.secondaryAngle, 0.1);
      audio.teslaFire();
      return;
    }

    if (sd.kind === 'laser') {
      this.fireLaser(t, target, sd);
      this.vfx.flash(sx, sy, 10, sd.colour, 'charge', t.secondaryAngle, 0.1);
      audio.laserFire();
      return;
    }

    const kind: 'bullet' | 'missile' | 'flame' | 'frost' | 'plasma' | 'cluster' =
      sd.kind === 'missile' || sd.kind === 'salvo'
        ? 'missile'
        : sd.kind === 'cryo'
          ? 'flame'
          : sd.kind === 'frost'
            ? 'frost'
            : sd.kind === 'plasma'
              ? 'plasma'
              : sd.kind === 'cluster'
                ? 'cluster'
                : 'bullet';
    const shots = sd.kind === 'salvo' ? 4 : 1;
    const speed =
      sd.kind === 'missile' || sd.kind === 'salvo'
        ? 300
        : sd.kind === 'frost' || sd.kind === 'cluster'
          ? 340
          : sd.kind === 'plasma'
            ? 470
            : 520;
    for (let s = 0; s < shots; s++) {
      const spread = sd.kind === 'salvo' ? (s - 1.5) * 10 : 0;
      this.projectiles.fire({
        x: sx,
        y: sy,
        tx: target.x + spread,
        ty: target.y + (s % 2 ? 6 : -6),
        damage: t.secondaryDamage,
        colour: sd.colour,
        speed,
        splash: sd.splash ?? 0,
        kind,
        burnDps: sd.burnDps,
        slowPct: sd.kind === 'cryo' ? Math.min(0.95, (sd.slowPct ?? 0) * 2) : sd.slowPct,
        armourPen: sd.armourPen ?? 0,
        damageType: sd.damageType,
        sourceId: `sec-${t.id}`,
        homing: sd.kind === 'missile' || sd.kind === 'salvo',
        targetId: target.id,
      });
    }
    this.vfx.flash(sx, sy, sd.kind === 'salvo' ? 18 : 10, sd.colour, 'muzzle', t.secondaryAngle, 0.1);
    if (sd.kind === 'cluster') {
      audio.clusterLaunch();
      this.shake = Math.max(this.shake, 2);
    } else if (sd.kind === 'missile' || sd.kind === 'salvo') {
      audio.missileLaunch();
      this.shake = Math.max(this.shake, 2);
    } else if (sd.kind === 'plasma') {
      audio.plasmaFire();
      this.vfx.burst(sx, sy, sd.colour, 4, { speed: 40, life: 0.2, r: 2, kind: 'plasma' });
    } else if (sd.kind === 'frost') {
      audio.frostFire();
      this.vfx.burst(sx, sy, 0xbfe9ff, 3, { speed: 40, life: 0.25, r: 2 });
    } else if (sd.kind === 'bullet') {
      audio.autocannon();
      this.vfx.casing(sx, sy, t.secondaryAngle);
    } else audio.shoot();
  }

  private fireLaser(t: Tower, target: Enemy, sd: SymbiosisDef): void {
    const dmg = t.secondaryDamage;
    const wasAlive = target.alive;
    const dealt = target.applyHit(dmg, 'ENERGY', sd.armourPen ?? 0.3, 0, `sec-${t.id}`, 1);
    this.recordTowerDamage(`sec-${t.id}`, dealt);
    target.hitFlash = 0.12;
    this.spawnDamageNum(target.x, target.y, Math.round(dmg), '#ff9ab0', false);
    this.vfx.beam(t.x, t.y, target.x, target.y, sd.colour, 0.13, 3);
    this.vfx.flash(target.x, target.y, 10, sd.colour, 'impact', 0, 0.1);
    if (wasAlive && !target.alive) this.onEnemyKilled(target, true, 'ENERGY', `sec-${t.id}`);
  }

  private clusterDetonate(p: import('../entities/Projectile').Projectile): void {
    const x = p.x;
    const y = p.y;
    const r = p.splash;
    const dmg = p.damage;
    const colour = p.colour;
    const sourceId = p.sourceId;
    const damageType = p.damageType;
    const armourPen = p.armourPen;

    this.vfx.artilleryImpact(x, y, r);
    this.addDecal({ x, y, r: r * 0.5, a: 0.4, scorch: true });
    this.shake = Math.max(this.shake, 6);
    for (const e of this.enemies) {
      if (!e.alive) continue;
      if (Phaser.Math.Distance.Between(x, y, e.x, e.y) <= r) {
        this.applyClusterHit(e, dmg, damageType, armourPen, sourceId, colour);
      }
    }
    const n = 5;
    for (let i = 0; i < n; i++) {
      const bx = x + (Math.random() - 0.5) * r * 1.6;
      const by = y + (Math.random() - 0.5) * r * 1.6;
      this.time.delayedCall(70 + i * 55, () => {
        this.vfx.explosion(bx, by, 0.7, true);
        this.vfx.ring(bx, by, 26, colour, 2, 0.25);
        for (const e of this.enemies) {
          if (!e.alive) continue;
          if (Phaser.Math.Distance.Between(bx, by, e.x, e.y) <= 32) {
            this.applyClusterHit(e, dmg * 0.5, damageType, armourPen, sourceId, colour);
          }
        }
        audio.explosion();
      });
    }
    audio.explosion();
  }

  private applyClusterHit(
    e: Enemy,
    dmg: number,
    damageType: import('../entities/Projectile').ProjectileInit['damageType'],
    armourPen: number,
    sourceId: string,
    _colour: number,
  ): void {
    const wasAlive = e.alive;
    const dealt = e.applyHit(dmg, damageType, armourPen, 0, sourceId, 1);
    this.recordTowerDamage(sourceId, dealt);
    e.hitFlash = 0.1;
    this.spawnDamageNum(e.x, e.y, Math.round(dmg), '#ffcf88', false);
    if (wasAlive && !e.alive) this.onEnemyKilled(e, true, damageType, sourceId);
  }

  /**
   * Sprite aim calibration.
   * Art barrels/nozzles point in different local directions; rotation maps that local
   * direction onto `t.angle`. lenFactor is fraction of display half-size to barrel tip.
   */
  private static readonly TOWER_AIM_ROTATION = -2.84;

  private static readonly AIM: Record<string, { rot: number; len: number }> = {
    // Legacy v3 plates were authored with different local muzzle axes. Keep
    // each chassis aligned to its own visible barrel so the shared world-space
    // aim vector also passes through the muzzle flash and projectile spawn.
    ballistic: { rot: -2.42, len: 0.52 },
    thermal: { rot: -2.9, len: 0.46 },
    artillery: { rot: 1.8, len: 0.58 },
    energy: { rot: Math.PI * 0.5, len: 0.42 },
    vulcan: { rot: -2.9, len: 0.5 },
    sabre: { rot: -1.8, len: 0.5 },
    tesla: { rot: 0, len: 0.38 },
    cryo: { rot: -2.9, len: 0.5 },
    dronehive: { rot: 0, len: 0.32 },
  };

  /** Complete level plates rotate as a single unit; each value maps its painted barrel to aim. */
  private static readonly TOP_DOWN_PLATE_AIM: Record<string, number> = {
    ballistic: 1.05,
    thermal: 0.9,
    sabre: 0.9,
    artillery: Math.PI * 0.5,
    tesla: Math.PI * 0.5,
    cryo: 0.9,
    // Complete Cyclone plates paint the twin gatlings slightly down-right.
    // Without this entry bodyRotation stayed 0 and the cannon never tracked.
    vulcan: 0.25,
  };

  /** Muzzle world position — tip of painted barrel after sprite rotation. */
  private getMuzzle(t: Tower): { x: number; y: number; len: number } {
    const stage = t.stageIndex;
    const visual = this.towerSprites.get(t.id);
    const sprite = visual?.weapon ?? visual?.body;
    const cal = BattleScene.AIM[t.def.id] ?? { rot: BattleScene.TOWER_AIM_ROTATION, len: 0.45 };
    let len: number;
    if (sprite) {
      const half = Math.max(sprite.displayWidth, sprite.displayHeight) * 0.5;
      // Keep the family calibration as a small nudge, but place the flash
      // beyond the visible turret silhouette instead of inside its body.
      len = half * (1.02 + cal.len * 0.12) + 2;
    } else {
      len = 19 + stage * 3 - (t.flash > 0 ? t.flash * 4 : 0);
    }
    // Dual-barrel lateral offset (visual only for spawn)
    let ox = 0;
    let oy = 0;
    if (t.def.id === 'ballistic' && stage >= 2) {
      const perp = t.angle + Math.PI / 2;
      const side = stage >= 3 ? 7 : 5;
      ox = Math.cos(perp) * side;
      oy = Math.sin(perp) * side;
    }
    const terrainScale = this.terrainDepthScale(t.x, t.y);
    const art = TOWER_ART[t.def.id];
    const weaponLift = (art?.weaponLift ?? 0) * terrainScale;
    // The art is seated on the ring in perspective, then receives a fixed
    // screen-space drop so every chassis visibly contacts its mounting point.
    const mountOffset = (art?.mountOffset ?? 0) * terrainScale + 5;
    return {
      x: t.x + Math.cos(t.angle) * len + ox,
      y: t.y + mountOffset - weaponLift + Math.sin(t.angle) * len + oy,
      len,
    };
  }

  private fireTower(t: Tower, target: Enemy): void {
    const stage = t.stage;
    const colour = t.def.colour;
    const id = t.def.id;
    t.flash = 0.12;

    // Tesla arcs directly — no travelling projectile.
    if (id === 'tesla') {
      this.fireChain(t, target, t.damage, (stage.chain ?? 2) + (this.research.chainAdd[id] ?? 0), t.armourPen, colour, String(t.id), stage.slowPct);
      this.vfx.flash(t.x, t.y, 16, 0xbbffff, 'charge', t.angle, 0.12);
      audio.teslaFire();
      return;
    }

    const shots = stage.projectiles ?? 1;
    const dmg = t.damage;
    const splash = (stage.splash ?? 0) * (this.research.splashMul[id] ?? 1);
    const critChance = (stage.critChance ?? 0) + (this.research.critChanceAdd[id] ?? 0);
    const muzzle = this.getMuzzle(t);
    const mx = muzzle.x;
    const my = muzzle.y;
    const muzzleFlashAngle = t.angle;

    // Muzzle flash per family.
    if (id === 'ballistic' || id === 'vulcan') {
      this.vfx.flash(mx, my, 10 + t.stageIndex * 2, 0xffee88, 'muzzle', muzzleFlashAngle, 0.08);
      this.vfx.burst(mx, my, 0xffcc66, 3, { speed: 40, life: 0.12, r: 1.2 });
    } else if (id === 'artillery' || id === 'sabre') {
      this.vfx.flash(mx, my, 22, 0xffaa44, 'muzzle', t.angle, 0.15);
      this.vfx.smoke(mx, my, 0x554433, id === 'artillery' ? 4 : 2);
      this.vfx.burst(mx, my, 0xffaa44, 8, { speed: 80, life: 0.2, r: 2 });
    } else if (id === 'energy') {
      this.vfx.flash(mx, my, 14, 0xbb88ff, 'charge', t.angle, 0.12);
      this.vfx.burst(t.x, t.y, colour, 4, { speed: 30, life: 0.2, r: 2, kind: 'plasma' });
    } else {
      this.vfx.burst(mx, my, colour, 4, { speed: 50, life: 0.15, r: 2 });
    }

    const kind: 'bullet' | 'flame' | 'shell' | 'missile' | 'beam' =
      id === 'sabre' ? 'missile'
        : id === 'ballistic' || id === 'vulcan' ? 'bullet'
          : id === 'thermal' || id === 'cryo' ? 'flame'
            : id === 'artillery' ? 'shell'
              : 'beam';
    const speed = id === 'artillery' ? 380 : id === 'sabre' ? 320 : id === 'energy' ? 700 : 520;

    for (let s = 0; s < shots; s++) {
      const spread = s === 0 ? 0 : (s % 2 === 0 ? 6 : -6);
      const tx = target.x + spread;
      const ty = target.y + (s === 0 ? 0 : 4 * (s % 2 === 0 ? 1 : -1));
      let sx = mx;
      let sy = my;
      if ((id === 'ballistic' || id === 'vulcan') && shots > 1 && s > 0) {
        const perp = t.angle + Math.PI / 2;
        const side = s === 1 ? -8 : 8;
        sx = mx + Math.cos(perp) * side;
        sy = my + Math.sin(perp) * side;
        this.vfx.flash(sx, sy, 6, 0xffee88, 'muzzle', muzzleFlashAngle, 0.06);
      }
      this.projectiles.fire({
        x: sx,
        y: sy,
        tx,
        ty,
        damage: dmg,
        colour,
        speed,
        splash,
        kind,
        burnDps: stage.burnDps,
        slowPct: id === 'cryo' ? Math.min(0.95, (stage.slowPct ?? 0) * 2) : stage.slowPct,
        armourPen: t.armourPen,
        critChance,
        damageType: stage.damageType,
        sourceId: String(t.id),
        homing: stage.homing,
        targetId: target.id,
        maxTravel: id === 'thermal' || id === 'cryo' ? Phaser.Math.Distance.Between(mx, my, tx, ty) + 12 : undefined,
      });
    }

    // SFX — distinct ammunition per weapon family.
    if (id === 'vulcan') {
      audio.gatling();
      this.vfx.casing(mx, my, t.angle);
    } else if (id === 'ballistic') {
      audio.autocannon();
      this.vfx.casing(mx, my, t.angle);
    } else if (id === 'thermal') {
      audio.flameFire();
    } else if (id === 'cryo') {
      audio.frostFire();
    } else if (id === 'artillery') {
      audio.clusterLaunch();
      this.shake = Math.max(this.shake, 3);
    } else if (id === 'sabre') {
      audio.missileLaunch();
      this.shake = Math.max(this.shake, 3);
    } else audio.energy();
  }

  /** Instant chaining arc used by Tesla coils and Arc Node modules. */
  private fireChain(
    t: Tower,
    first: Enemy,
    damage: number,
    chain: number,
    armourPen: number,
    colour: number,
    sourceId: string,
    slowPct = 0,
  ): void {
    const hitSet = new Set<number>([first.id]);
    const points: { x: number; y: number }[] = [
      { x: t.x, y: t.y },
      { x: first.x, y: first.y },
    ];
    let dmg = damage;
    this.applyChainHit(first, dmg, armourPen, sourceId, slowPct);
    let current = first;
    for (let i = 0; i < chain; i++) {
      let next: Enemy | null = null;
      let best = 115;
      for (const e of this.enemies) {
        if (!e.alive || hitSet.has(e.id)) continue;
        const d = Phaser.Math.Distance.Between(current.x, current.y, e.x, e.y);
        if (d < best) {
          best = d;
          next = e;
        }
      }
      if (!next) break;
      hitSet.add(next.id);
      dmg *= 0.82;
      this.applyChainHit(next, dmg, armourPen, sourceId, slowPct);
      points.push({ x: next.x, y: next.y });
      current = next;
    }
    for (let i = 0; i < points.length - 1; i++) {
      this.vfx.arc(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y, colour, 0.16);
    }
    this.vfx.flash(first.x, first.y, 12, 0xffffff, 'impact', 0, 0.1);
  }

  private applyChainHit(e: Enemy, dmg: number, armourPen: number, sourceId: string, slowPct: number): void {
    const wasAlive = e.alive;
    const dealt = e.applyHit(dmg, 'ENERGY', armourPen, 0, sourceId, 1);
    this.recordTowerDamage(sourceId, dealt);
    e.hitFlash = 0.1;
    this.spawnDamageNum(e.x, e.y, Math.round(dmg), '#9fe8ff', false);
    if (slowPct) e.applyStatuses([{ kind: 'SLOW', duration: 1.2, magnitude: slowPct, sourceId }]);
    if (wasAlive && !e.alive) this.onEnemyKilled(e, true, 'ENERGY', sourceId);
  }

  private updateProjectiles(dt: number): void {
    const list = this.projectiles.getActive();
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i];
      // Hit test
      let hit = false;
      if (p.kind === 'flame') {
        // flame hits continuously along path - check all
        for (const e of this.enemies) {
          if (!e.alive) continue;
          if (Phaser.Math.Distance.Between(p.x, p.y, e.x, e.y) < e.def.radius + 10) {
            this.applyProjectileHit(p, e);
            hit = true;
          }
        }
      } else {
        for (const e of this.enemies) {
          if (!e.alive) continue;
          if (Phaser.Math.Distance.Between(p.x, p.y, e.x, e.y) < e.def.radius + 4) {
            this.applyProjectileHit(p, e);
            hit = true;
            break;
          }
        }
      }

      if (hit && p.kind === 'cluster') {
        this.clusterDetonate(p);
        this.projectiles.recycle(p);
        continue;
      }

      // Ballistic rounds that miss bury themselves in the ground, kicking dust.
      if (!hit && p.kind === 'bullet' && Phaser.Math.Distance.Between(p.x, p.y, p.tx, p.ty) < 10) {
        this.vfx.dust(p.x, p.y, 0x7a6a4a, 2);
        this.projectiles.recycle(p);
        continue;
      }

      if (hit && p.kind !== 'flame') {
        if (p.splash > 0) {
          for (const e of this.enemies) {
            if (!e.alive) continue;
            const d = Phaser.Math.Distance.Between(p.x, p.y, e.x, e.y);
            if (d < p.splash && d > e.def.radius * 0.3) {
              this.applyProjectileHit(p, e, 0.55);
            }
          }
          if (p.kind === 'shell' || p.kind === 'missile') {
            this.vfx.artilleryImpact(p.x, p.y, p.splash);
            this.addDecal({ x: p.x, y: p.y, r: p.splash * 0.55, a: 0.4, scorch: true });
            this.shake = Math.max(this.shake, 5);
          } else {
            this.vfx.explosion(p.x, p.y, 0.7, p.damageType !== 'ENERGY');
            this.vfx.ring(p.x, p.y, p.splash, p.colour, 3, 0.28);
          }
        } else {
          this.vfx.flash(p.x, p.y, 8, 0xffffff, 'impact', 0, 0.1);
          this.vfx.burst(p.x, p.y, p.colour, 4, { speed: 60, life: 0.2, r: 1.5 });
        }
        this.projectiles.recycle(p);
        continue;
      }

      // Out of bounds / expired
      if (p.x < -20 || p.x > BATTLE_W + 20 || p.y < UI_TOP_H - 20 || p.y > GAME_HEIGHT) {
        this.projectiles.recycle(p);
        continue;
      }
      const previous = { x: p.x, y: p.y };
      const alive = p.update(dt, this.enemies);
      // Expired rounds must leave the active pool. If the return value is
      // ignored, drawProjectile keeps painting the final position forever,
      // which appears as stationary yellow bullet marks on the map.
      if (!alive || p.age >= 5) {
        this.projectiles.recycle(p);
        continue;
      }
      const lowFlight = p.kind === 'bullet' || p.kind === 'flame' || p.kind === 'beam' || p.kind === 'frost' || p.kind === 'plasma';
      if (lowFlight && this.terrainBlocksSegment(previous, p)) {
        this.vfx.dust(p.x, p.y, 0x6c6256, 1);
        this.projectiles.recycle(p);
      }
    }
  }

  private applyProjectileHit(p: import('../entities/Projectile').Projectile, e: Enemy, mul = 1): void {
    if (!e.alive) return;
    const wasAlive = e.alive;
    const dealt = e.applyHit(p.damage * mul, p.damageType, p.armourPen, p.critChance, p.sourceId, 1);
    this.recordTowerDamage(p.sourceId, dealt);
    e.hitFlash = 0.12;
    this.spawnDamageNum(e.x, e.y, Math.round(p.damage * mul), '#ffffff', p.critChance > 0 && Math.random() < p.critChance);
    if (saveManager.data.settings.slowmo && (p.kind === 'shell' || mul >= 1)) {
      this.hitstop = Math.max(this.hitstop, p.kind === 'shell' ? 0.08 : 0.03);
    }
    this.vfx.flash(e.x, e.y, e.def.radius * 0.7, 0xffffff, 'impact', 0, 0.08);
    this.vfx.burst(e.x, e.y, p.colour, 3, { speed: 50, life: 0.15, r: 1.5 });
    if (p.kind === 'bullet') {
      this.vfx.dust(e.x, e.y + e.def.radius * 0.5, 0x7a6a4a, 1);
      if (Math.random() < 0.65) audio.projectileImpact(p.kind);
    } else if (p.kind !== 'flame' && p.kind !== 'beam') {
      audio.projectileImpact(p.kind);
    }
    if (p.burnDps) {
      e.applyStatuses([{ kind: 'BURN', duration: 2.5, magnitude: p.burnDps, sourceId: p.sourceId }]);
    }
    if (p.slowPct) {
      e.applyStatuses([{ kind: 'SLOW', duration: 1.5, magnitude: p.slowPct, sourceId: p.sourceId }]);
    }
    if (wasAlive && !e.alive) {
      this.onEnemyKilled(e, true, p.damageType, p.sourceId);
    } else if (e.isElite || e.isBoss) {
      // small hit spark
    }
  }

  private recordTowerDamage(sourceId: string, damage: number): void {
    const towerId = sourceId.startsWith('sec-') ? sourceId.slice(4) : sourceId;
    const tower = this.towers.find((candidate) => String(candidate.id) === towerId);
    if (tower) tower.damageDealt += Math.max(0, damage);
  }

  private onEnemyKilled(e: Enemy, byTower: boolean, damageType?: string, sourceId = ''): void {
    if (e.killRecorded) return;
    e.killRecorded = true;
    const normalKillMultiplier = this.difficulty === 'normal' ? NORMAL_KILL_REWARD_MULTIPLIER : 1;
    const bounty = Math.max(1, Math.round(e.def.bounty * this.bountyMul * normalKillMultiplier * EARNED_INCOME_MULTIPLIER * this.nightmareIncomeMultiplier() * CREDIT_REWARD_MULTIPLIER * COIN_REWARD_MULTIPLIER * this.devCreditMul));
    this.score += bounty * 3 + (e.isBoss ? 500 : e.isElite ? 80 : 0);
    // Standard bounties are harvested automatically on death. Battlefield
    // caches stay special: bosses always drop one, while elites/minibosses
    // have a chance to drop a bonus cache.
    this.supply += bounty;
    const rareDrop = e.isBoss || (e.isElite && Math.random() < 0.35);
    let droppedCache: LootDrop | undefined;
    if (rareDrop) {
      const kind: LootDrop['kind'] = e.isBoss && this.bossResearchDrops < 2 && Math.random() < 0.5 ? 'research' : 'supply';
      if (kind === 'research') this.bossResearchDrops++;
      const bonus = kind === 'research' ? scaleDataReward(1) : e.isBoss ? Math.max(20, Math.round(bounty * 0.75)) : Math.max(8, Math.round(bounty * 0.5));
      droppedCache = {
        id: nextEntityId(),
        x: e.x,
        y: e.y - 5,
        amount: bonus,
        kind,
        source: e.isBoss ? 'boss' : 'elite',
        life: 30,
        bob: Math.random() * Math.PI * 2,
      };
      this.loot.push(droppedCache);
    }
    this.vfx.spawn({
      x: e.x, y: e.y - 12,
      vx: 0, vy: -30,
      kind: 'spark', c: 0xd4a24a, maxLife: 0.7, r: 2, drag: 1, alpha0: 0.9,
    });
    if (e.isBoss) {
      const bossScale = Phaser.Math.Clamp(1.8 + Math.sqrt(e.maxHp / 420), 2.2, 4.8);
      this.vfx.explosion(e.x, e.y, bossScale, true);
      this.vfx.ring(e.x, e.y, 58 * bossScale, 0xff4444, Math.min(8, 3 + bossScale), 0.7);
      this.vfx.ring(e.x, e.y, 38 * bossScale, 0xffd36a, 2.5, 0.5);
      this.vfx.burst(e.x, e.y, 0xff2222, Math.round(18 * bossScale), { speed: 180 * Math.min(1.4, bossScale / 2.2), life: 0.7, r: 4 });
      this.addDecal({ x: e.x, y: e.y, r: e.def.radius * 1.5, a: 0.55, scorch: true });
      this.shake = 18;
      voice.playCategory(this, 'bossdown', { priority: true, cooldown: 0 });
    } else if (e.isElite) {
      this.vfx.explosion(e.x, e.y, 1.2, true);
      this.vfx.ring(e.x, e.y, 50, e.def.colour, 3, 0.4);
      this.addDecal({ x: e.x, y: e.y, r: e.def.radius, a: 0.45, scorch: true });
      voice.playCategory(this, 'elitedown', { cooldown: 14 });
    } else {
      this.vfx.explosion(e.x, e.y, 0.45, true);
      this.vfx.burst(e.x, e.y, e.def.colour, 8, { speed: 90, life: 0.3, r: 2 });
    }
    if (!this.sawFirstBlood) {
      this.sawFirstBlood = true;
      voice.playCategory(this, 'firstblood', { cooldown: 0 });
    }
    const now = this.time.now;
    this.killTimes.push(now);
    while (this.killTimes.length && this.killTimes[0] < now - 4000) this.killTimes.shift();
    if (this.killTimes.length >= 15) {
      this.killTimes.length = 0;
      voice.playCategory(this, 'streak', { cooldown: 20 });
    }
    this.killStreak++;
    if (this.killStreak % BattleScene.KILL_STREAK_STEP === 0) {
      const streakReward = Math.min(
        BattleScene.KILL_STREAK_MAX_REWARD,
        BattleScene.KILL_STREAK_BASE_REWARD + Math.floor(this.killStreak / 10) * 2,
      );
      const tunedReward = Math.floor(streakReward * this.research.earlyWaveBonusMul * this.rankPerks.waveRewardMul * EARNED_INCOME_MULTIPLIER * CREDIT_REWARD_MULTIPLIER * COIN_REWARD_MULTIPLIER * this.devCreditMul);
      this.supply += tunedReward;
      this.score += tunedReward * 2;
      this.showMessage(`KILL STREAK ${this.killStreak}  +${tunedReward} SUPPLY`, false);
    }
    audio.explosion();
    achievements.noteKill(damageType ?? 'BALLISTIC', this.difficulty);
    if (e.isBoss) achievements.noteBossKill();
    this.refreshHud();
    if (rareDrop) {
      this.showMessage(`${e.isBoss ? 'BOSS' : 'ELITE'} CACHE DROPPED  —  ${droppedCache?.kind === 'research' ? `RECOVER +${droppedCache.amount} DATA` : `RECOVER +${droppedCache?.amount ?? 0} SUPPLY`}`, false);
      if (e.isBoss) {
        const reward = droppedCache?.kind === 'research' ? `CACHE DROP  ·  +${droppedCache.amount} DATA` : `CACHE DROP  ·  +${droppedCache?.amount ?? 0} SUPPLY`;
        this.showNotificationToast('BOSS ELIMINATED', e.def.name, reward, 0xff6b6b);
      }
    }
    bus.emit(Events.EnemyKilled, e.def.id);
    // Credit the tower that fired the killing weapon. Nearest-tower fallback
    // keeps special cases such as legacy saves and direct abilities visible.
    if (byTower) {
      const sourceTowerId = sourceId.startsWith('sec-') ? sourceId.slice(4) : sourceId;
      let best = this.towers.find((tower) => String(tower.id) === sourceTowerId);
      if (!best) {
        let bestD = Infinity;
        for (const t of this.towers) {
          const d = Phaser.Math.Distance.Between(e.x, e.y, t.x, t.y);
          if (d < bestD) {
            bestD = d;
            best = t;
          }
        }
      }
      if (best) best.kills++;
    }
  }

  private collectLoot(index: number): void {
    const drop = this.loot[index];
    if (!drop) return;
    this.loot.splice(index, 1);
    if (drop.kind === 'research') {
      saveManager.adjustResearchPoints(drop.amount);
      saveManager.save();
    } else {
      this.supply += drop.amount;
    }

    const cam = this.cameras.main;
    this.lootFlights.push({
      sx: cam.x + (drop.x - cam.scrollX) * cam.zoom,
      sy: cam.y + (drop.y - cam.scrollY) * cam.zoom,
      amount: drop.amount,
      kind: drop.kind,
      life: 0,
      maxLife: 0.68,
    });
    const lootColour = drop.kind === 'research' ? COLORS.energy : 0xffd36a;
    this.vfx.burst(drop.x, drop.y, lootColour, 10, { speed: 75, life: 0.35, r: 2.2 });
    this.vfx.ring(drop.x, drop.y, 24, lootColour, 2, 0.3);
    audio.upgrade();
    const flash = this.add.text(
      96,
      UI_TOP_H / 2,
      drop.kind === 'research' ? `+${drop.amount} DATA` : `+${drop.amount} CREDITS`,
      {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '18px',
        color: drop.kind === 'research' ? '#d8a8ff' : '#ffd36a',
        fontStyle: 'bold',
        stroke: '#071017',
        strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 6, fill: true },
      },
    ).setOrigin(0.5).setDepth(60).setScale(0.7);
    // Keep pickup feedback on the fixed HUD while the field camera moves.
    this.cameras.main.ignore(flash);
    this.tweens.add({
      targets: flash,
      y: 15,
      alpha: 0,
      scale: 1.15,
      // Keep the arcade reward flash readable long enough to register the
      // amount before it flies into the supply meter.
      duration: 1900,
      ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    this.showMessage(drop.kind === 'research' ? `RESEARCH CACHE RECOVERED  +${drop.amount} DATA` : `SUPPLY CACHE RECOVERED  +${drop.amount}`);
    this.refreshHud();
  }

  /** Boss caches are guaranteed rewards, so settle them before final scoring. */
  private collectBossLootBeforeFinish(): void {
    for (let index = this.loot.length - 1; index >= 0; index--) {
      if (this.loot[index].source === 'boss') this.collectLoot(index);
    }
  }

  private updateLoot(dt: number): void {
    for (let i = this.loot.length - 1; i >= 0; i--) {
      const drop = this.loot[i];
      drop.life -= dt;
      drop.bob += dt * 4;
      if (drop.life <= 0) this.loot.splice(i, 1);
    }
    for (let i = this.lootFlights.length - 1; i >= 0; i--) {
      const flight = this.lootFlights[i];
      flight.life += dt;
      if (flight.life >= flight.maxLife) this.lootFlights.splice(i, 1);
    }
  }

  /** Battlefield dirt/oil is temporary feedback, never permanent map clutter. */
  private updateDecals(dt: number): void {
    for (let i = this.decals.length - 1; i >= 0; i--) {
      const decal = this.decals[i];
      decal.life -= dt;
      if (decal.life <= 0) this.decals.splice(i, 1);
    }
  }

  private addDecal(decal: Omit<BattleScene['decals'][number], 'life'>): void {
    this.decals.push({ ...decal, life: BattleScene.DECAL_LIFETIME_SEC });
  }

  private onBreach(e: Enemy): void {
    const dev = saveManager.data.settings;
    const breachDamageMul = dev.devMode ? Phaser.Math.Clamp(dev.devBreachDamageMultiplier ?? 1, 0, 2) : 1;
    const dmg = Math.max(0, Math.round(e.def.breachDamage * e.powerMul * breachDamageMul));
    this.killStreak = 0;
    const integrityBefore = this.integrity;
    this.integrity = Math.max(0, this.integrity - dmg);
    const integrityChanged = this.integrity < integrityBefore;
    this.score = Math.max(0, this.score - dmg * 20);
    this.shake = 18;
    audio.breach();
    this.cameras.main.flash(280, 255, 48, 48);
    this.cameras.main.shake(220, 0.012);
    announcer.say('breach');
    this.showMessage(announcer.dequeue() ?? `BREACH — ${e.def.name}`);
    this.showMessage(`LEAK −${dmg}  ·  INTEGRITY ${this.integrity}/${this.maxIntegrity}`, false);
    // Always-visible leak callout at the bastion (independent of damage-number setting).
    const leak = this.worldAdd(
      this.add.text(this.mapDef.command.x, this.mapDef.command.y - 36, `LEAK −${dmg}`, {
        fontFamily: 'Consolas, monospace', fontSize: '16px', fontStyle: 'bold',
        color: '#ff6a5a', stroke: '#1a0505', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(60),
    );
    this.tweens.add({ targets: leak, y: leak.y - 28, alpha: 0, duration: 900, onComplete: () => leak.destroy() });
    this.vfx.explosion(e.x, e.y, 1.3, false);
    this.vfx.ring(e.x, e.y, 60, COLORS.danger, 4, 0.5);
    this.vfx.flash(this.mapDef.command.x, this.mapDef.command.y, 40, 0xff2222, 'blast', 0, 0.3);
    // Screen-edge breach wash
    const edge = this.add.graphics().setDepth(55);
    edge.fillStyle(0xff2a22, 0.22);
    edge.fillRect(0, 0, GAME_WIDTH, 10);
    edge.fillRect(0, GAME_HEIGHT - 10, GAME_WIDTH, 10);
    edge.fillRect(0, 0, 10, GAME_HEIGHT);
    edge.fillRect(GAME_WIDTH - 10, 0, 10, GAME_HEIGHT);
    this.tweens.add({ targets: edge, alpha: 0, duration: 420, onComplete: () => edge.destroy() });
    if (this.integrity <= 0) {
      this.finish(false);
      return;
    }
    const band =
      this.integrity <= this.maxIntegrity * 0.2 ? 2 : this.integrity <= this.maxIntegrity * 0.4 ? 1 : 0;
    if (band === 2 && this.integrityBand < 2) {
      voice.playCategory(this, 'neardeath', { priority: true, cooldown: 0 });
    } else if (band === 1 && this.integrityBand < 1) {
      voice.playCategory(this, 'integrity', { priority: true, cooldown: 0 });
    } else if (integrityChanged) {
      voice.playCategory(this, 'breach', { priority: true, cooldown: 4 });
    }
    this.integrityBand = band;
  }

  private updateDrone(dt: number): void {
    this.drone.update(dt);
    const d = this.drone;
    if (d.mode === 'ATTACK' && d.fireCooldown <= 0) {
      // Find enemy in 90px
      let best: Enemy | null = null;
      let bestD = 95;
      for (const e of this.enemies) {
        if (!e.alive) continue;
        const dist = Phaser.Math.Distance.Between(d.x, d.y, e.x, e.y);
        if (dist < bestD) {
          bestD = dist;
          best = e;
        }
      }
      if (best) {
        d.fireCooldown = 0.55;
        this.projectiles.fire({
          x: d.x,
          y: d.y,
          tx: best.x,
          ty: best.y,
          damage: 14 * this.research.droneDamageMul,
          colour: 0x9ad4ff,
          speed: 400,
          splash: 0,
          kind: 'bullet',
          damageType: 'ENERGY',
          sourceId: 'drone',
          armourPen: 0.15,
        });
        audio.shoot();
      }
    }
    if (d.mode === 'BOOST') {
      // Boost nearest tower within 50
      for (const t of this.towers) {
        if (Phaser.Math.Distance.Between(d.x, d.y, t.x, t.y) < 50) {
          // slightly faster via jam negative? use specialActive light boost
          t.cooldown = Math.max(0, t.cooldown - dt * 0.5);
        }
      }
    }
    if (d.mode === 'SHIELD') {
      if (d.shieldT <= 0) {
        // shield command if near
        if (Phaser.Math.Distance.Between(d.x, d.y, this.mapDef.command.x, this.mapDef.command.y) < 70) {
          this.integrity = Math.min(this.maxIntegrity, this.integrity + dt * 0.15);
        }
      }
    }
  }

  private updateBarriers(dt: number): void {
    for (let i = this.barriers.length - 1; i >= 0; i--) {
      const b = this.barriers[i];
      b.update(dt);
      if (!b.alive) this.barriers.splice(i, 1);
    }
  }

  private updateAbilityCds(dt: number): void {
    for (const k of Object.keys(this.abilityReadyFlash)) {
      this.abilityReadyFlash[k] = Math.max(0, this.abilityReadyFlash[k] - dt);
    }
    for (const k of Object.keys(this.abilityCd)) {
      if (this.abilityCd[k] > 0) {
        this.abilityCd[k] = Math.max(0, this.abilityCd[k] - dt);
        if (this.abilityCd[k] === 0) {
          this.abilityReadyFlash[k] = 0.7;
          bus.emit(Events.AbilityReady, k);
        }
      }
    }
  }

  private updateVfx(dt: number): void {
    this.vfx.update(dt);
    // Ambient weather
    this.ambientT += dt;
    if (this.ambientT > 0.08 && this.theme.ambient !== 'none') {
      this.ambientT = 0;
      const kind = this.theme.ambient;
      const c = this.theme.ambientColour;
      const x = Math.random() * BATTLE_W;
      if (kind === 'snow') {
        this.vfx.spawn({
          x, y: UI_TOP_H - 4,
          vx: (Math.random() - 0.5) * 15,
          vy: 25 + Math.random() * 30,
          kind: 'smoke', c, maxLife: 4, r: 1.2 + Math.random() * 1.5, drag: 1, alpha0: 0.55,
        });
      } else if (kind === 'ember') {
        this.vfx.spawn({
          x, y: GAME_HEIGHT - UI_BOTTOM_H - Math.random() * 40,
          vx: (Math.random() - 0.5) * 20,
          vy: -20 - Math.random() * 35,
          kind: 'ember', c, maxLife: 1.5 + Math.random(), r: 1.5, gravity: -10, alpha0: 0.7,
        });
      } else if (kind === 'spore') {
        this.vfx.spawn({
          x, y: UI_TOP_H + Math.random() * (GAME_HEIGHT - UI_TOP_H - UI_BOTTOM_H),
          vx: (Math.random() - 0.5) * 12,
          vy: (Math.random() - 0.5) * 8,
          kind: 'vapour', c, maxLife: 2.5, r: 1.5, drag: 1, alpha0: 0.35,
        });
      } else {
        this.vfx.spawn({
          x, y: UI_TOP_H + Math.random() * 80,
          vx: 8 + Math.random() * 18,
          vy: 10 + Math.random() * 14,
          kind: 'vapour', c, maxLife: 2.2, r: 1, drag: 1, alpha0: 0.25,
        });
      }
    }
    for (let i = this.markers.length - 1; i >= 0; i--) {
      const m = this.markers[i];
      m.t -= dt;
      if (m.t <= 0) this.markers.splice(i, 1);
    }
    if (this.msgTimer > 0) {
      this.msgTimer -= dt;
      if (this.msgTimer <= 0) this.txtMsg.setText('');
    }
    if (this.achievementToastTimer > 0) {
      this.achievementToastTimer -= dt;
      if (this.achievementToastTimer <= 0 && this.achievementToast) {
        const toast = this.achievementToast;
        this.achievementToast = undefined;
        this.tweens.add({
          targets: toast,
          alpha: 0,
          y: toast.y - 8,
          duration: 220,
          ease: 'Quad.In',
          onComplete: () => toast.destroy(),
        });
      }
    }
  }

  private spawnBurst(x: number, y: number, c: number, n: number): void {
    this.vfx.burst(x, y, c, n, { speed: 110, life: 0.35, r: 2.2 });
  }

  private spawnDamageNum(x: number, y: number, amount: number, colour: string, crit = false): void {
    if (!saveManager.data.settings.damageNumbers) return;
    if (this.damageNums.length > 40) {
      const old = this.damageNums.shift();
      old?.obj?.destroy();
    }
    const obj = this.worldAdd(
      this.add
        .text(x, y - 8, crit ? `${amount}!` : String(amount), {
          fontFamily: 'Consolas, monospace',
          fontSize: `${crit ? 14 : 11}px`,
          color: crit ? '#ffdd44' : colour,
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(60),
    );
    this.damageNums.push({
      x, y: y - 8, vy: -40, life: crit ? 0.7 : 0.45,
      text: '', colour, size: 11, obj,
    });
  }

  private updateDamageNums(dt: number): void {
    for (let i = this.damageNums.length - 1; i >= 0; i--) {
      const d = this.damageNums[i];
      d.life -= dt;
      d.y += d.vy * dt;
      d.vy *= 0.92;
      if (d.obj) {
        d.obj.setPosition(d.x, d.y);
        d.obj.setAlpha(Math.max(0, d.life * 2));
      }
      if (d.life <= 0) {
        d.obj?.destroy();
        this.damageNums.splice(i, 1);
      }
    }
  }

  private syncSprites(): void {
    // Ensure tower visuals exist. New art is composed from a fixed chassis and
    // an independently rotating weapon; legacy art remains a single fallback.
    for (const t of this.towers) {
      if (this.towerSprites.has(t.id)) continue;
      const resolved = this.resolveTowerArt(t);
      if (!resolved.bodyKey || !this.textures.exists(resolved.bodyKey)) continue;
      const body = this.worldAdd(this.add.image(t.x, t.y, resolved.bodyKey)
        .setDepth(this.towerWorldDepth(t.x, t.y))
        .setAlpha(0.92));
      body.setOrigin(resolved.spec?.bodyOrigin.x ?? 0.5, resolved.spec?.bodyOrigin.y ?? 0.5);
      const weapon = resolved.weaponKey && this.textures.exists(resolved.weaponKey)
        ? this.worldAdd(this.add.image(t.x, t.y, resolved.weaponKey)
          .setDepth(this.towerWorldDepth(t.x, t.y) + 0.01)
          .setAlpha(0.96))
        : undefined;
      weapon?.setOrigin(resolved.spec?.weaponOrigin.x ?? 0.5, resolved.spec?.weaponOrigin.y ?? 0.5);
      this.towerSprites.set(t.id, { body, weapon });
    }
    // Remove dead towers
    for (const [id, visual] of this.towerSprites) {
      if (!this.towers.find((t) => t.id === id)) {
        visual.body.destroy();
        visual.weapon?.destroy();
        this.towerSprites.delete(id);
      }
    }
    // Position + stage scale + layer-specific rotation.
    for (const t of this.towers) {
      const visual = this.towerSprites.get(t.id);
      if (!visual) continue;
      const resolved = this.resolveTowerArt(t);
      if (!resolved.bodyKey || !this.textures.exists(resolved.bodyKey)) continue;
      const spec = resolved.spec;
      const depth = this.towerWorldDepth(t.x, t.y);
      if (visual.body.texture.key !== resolved.bodyKey) visual.body.setTexture(resolved.bodyKey);
      visual.body.setOrigin(spec?.bodyOrigin.x ?? 0.5, spec?.bodyOrigin.y ?? 0.5);
      // Keep the entire assembly together and add the same fixed 5px pad-seat
      // correction used by the muzzle calculation above.
      const mountOffset = (spec?.mountOffset ?? 0) * this.terrainDepthScale(t.x, t.y) + 5;
      const plateAimOffset = BattleScene.TOP_DOWN_PLATE_AIM[t.def.id];
      const bodyRotation = spec ? 0 : plateAimOffset == null ? 0 : t.angle + plateAimOffset;
      visual.body.setPosition(t.x, t.y + mountOffset).setDepth(depth)
        // A split rig keeps its chassis fixed. A complete top-down plate must
        // rotate as one unit or its painted barrel cannot match projectiles.
        .setRotation(bodyRotation);
      const bodyTarget = spec?.bodyTarget ?? (54 + t.stageIndex * 7);
      const bodyScale = this.towerDisplayScale(t, Math.min(bodyTarget / visual.body.width, bodyTarget / visual.body.height));
      visual.body.setScale(bodyScale.x, bodyScale.y).setAlpha(t.specialActive > 0 ? 1 : 0.95);

      if (spec && resolved.weaponKey && this.textures.exists(resolved.weaponKey)) {
        if (!visual.weapon) {
          visual.weapon = this.worldAdd(this.add.image(t.x, t.y, resolved.weaponKey).setAlpha(0.96));
          visual.weapon.setOrigin(spec.weaponOrigin.x, spec.weaponOrigin.y);
        }
        if (visual.weapon.texture.key !== resolved.weaponKey) visual.weapon.setTexture(resolved.weaponKey);
        visual.weapon.setOrigin(spec.weaponOrigin.x, spec.weaponOrigin.y);
        const weaponLift = (spec.weaponLift ?? 0) * this.terrainDepthScale(t.x, t.y);
        visual.weapon.setPosition(t.x, t.y + mountOffset - weaponLift).setDepth(depth + 0.02);
        const weaponTarget = spec.weaponTarget + t.stageIndex * 4;
        const weaponScale = this.towerDisplayScale(t, Math.min(weaponTarget / visual.weapon.width, weaponTarget / visual.weapon.height));
        visual.weapon.setScale(weaponScale.x, weaponScale.y)
          .setFlipY(false)
          .setRotation(t.angle + spec.weaponRotationOffset)
          .setAlpha(t.specialActive > 0 ? 1 : 0.96);
        if (t.flash > 0) visual.weapon.setTint(0xffffcc);
        else visual.weapon.clearTint();
      } else if (visual.weapon) {
        visual.weapon.destroy();
        visual.weapon = undefined;
      }
      if (t.flash > 0) visual.body.setTint(0xffffcc);
      else visual.body.clearTint();
    }

    // Legacy single-sprite art uses the same level-plane aim rotation as the
    // generated plates; terrain depth only affects uniform display scale.

    // Swarm units use the same authored drone sprite as the tower icon. The
    // sprite is animated in flight (rotation, pulse, tint) while trails and
    // muzzle flashes remain lightweight vector FX underneath it.
    const liveDroneIds = new Set<number>();
    for (const swarm of this.swarmDrones.values()) {
      for (const drone of swarm) {
        liveDroneIds.add(drone.id);
        if (!this.textures.exists('swarm-drone')) continue;
        let img = this.swarmDroneSprites.get(drone.id);
        if (!img) {
          img = this.worldAdd(this.add.image(drone.x, drone.y, 'swarm-drone').setDepth(16));
          this.swarmDroneSprites.set(drone.id, img);
        }
        const weaponCol = drone.weapon === 'machine-gun' ? 0xffd36a : drone.weapon === 'bomb' ? 0xff9c4d : 0xff5e8c;
        const target = 18 * this.terrainDepthScale(drone.x, drone.y);
        const scale = Math.min(target / img.width, target / img.height);
        img.setPosition(drone.x, drone.y)
          .setScale(scale * (1 + Math.sin(this.time.now / 110 + drone.id) * 0.05))
          .setRotation(drone.angle + Math.PI / 2)
          .setAlpha(drone.phase === 'docked' ? 0.7 : 1)
          .setTint(drone.cohesion ? 0x9dfff0 : weaponCol);
      }
    }
    for (const [id, img] of this.swarmDroneSprites) {
      if (!liveDroneIds.has(id)) {
        img.destroy();
        this.swarmDroneSprites.delete(id);
      }
    }

    // Rolling Death ordnance is an authored sprite with a live tactical trail.
    // Keep it above the route and below the overlay VFX so the launch and
    // detonation rings remain readable.
    const liveBombIds = new Set<number>();
    for (const bomb of this.rollingBombs) {
      if (!bomb.active || !this.textures.exists('rolling-death-bomb-v1')) continue;
      liveBombIds.add(bomb.id);
      let img = this.rollingBombSprites.get(bomb.id);
      if (!img) {
        img = this.worldAdd(this.add.image(bomb.x, bomb.y, 'rolling-death-bomb-v1').setDepth(17));
        this.rollingBombSprites.set(bomb.id, img);
      }
          const target = 38 * this.terrainDepthScale(bomb.x, bomb.y);
      const scale = Math.min(target / img.width, target / img.height);
      const pulse = 1 + Math.sin(this.time.now / 70 + bomb.id) * 0.06;
      img.setPosition(bomb.x, bomb.y)
        .setScale(scale * pulse)
        .setRotation(bomb.angle + bomb.travel * 0.025)
        .setTint(bomb.mode === 'snow' ? 0x8fdcff : 0xffb05a)
        .setAlpha(0.98);
    }
    for (const [id, img] of this.rollingBombSprites) {
      if (!liveBombIds.has(id)) {
        img.destroy();
        this.rollingBombSprites.delete(id);
      }
    }

    // Enemy sprites for art-backed types. These overrides are deliberately
    // explicit so the authored/generated art is used in live combat instead
    // of silently falling back to the old procedural blob roster.
    const artOverrides: Record<string, string> = {
      scuttler: 'enemy-scuttler-v2',
      husk: 'enemy-husk-v2',
      dashling: 'enemy-dashling-v2',
      plateback: 'enemy-plateback-v2',
      mender: 'enemy-mender-v2',
      broodnode: 'enemy-broodnode-v2',
      veilguard: 'enemy-veilguard-v2',
      sporethrower: 'enemy-sporethrower-v2',
      archon: 'enemy-archon-v2',
      colossus: 'enemy-colossus-v2',
      wasp: 'enemy-wasp-v2',
      hivecrawler: 'enemy-hivecrawler-v2',
      dronebot: 'enemy-dronebot-v2',
      sentrybot: 'enemy-sentrybot-v2',
      haulerbot: 'enemy-haulerbot-v2',
      raider: 'enemy-raider-v2',
      sharpshooter: 'enemy-sharpshooter-v2',
      mechpilot: 'enemy-mechpilot-v2',
      hivequeen: 'enemy-hivequeen-v2',
      reaperbot: 'enemy-reaperbot-v2',
      gunner: 'enemy-gunner-v2',
      goliath: 'enemy-goliath-v2',
      nulltick: 'enemy-nulltick-v2',
      venomspitter: 'enemy-venomspitter-v2',
      locust: 'enemy-locust-v2',
    siegebreaker: 'enemy-siegebreaker-v3',
      warlord: 'enemy-warlord-v2',
      matriarch: 'enemy-matriarch-v2',
      leviathan: 'enemy-leviathan-v2',
    };
    const familyDefaults: Record<NonNullable<Enemy['def']['family']>, string> = {
      insect: 'enemy-locust-v2',
      robot: 'enemy-reaperbot-v2',
      human: 'enemy-gunner-v2',
      mesh: 'enemy-goliath-v2',
    };
    const artEnemies: Record<string, string> = {};
    for (const id of Object.keys(ENEMIES)) {
      const authoredKey = artOverrides[id] ?? familyDefaults[ENEMIES[id].family ?? 'mesh'];
      const sourceKey = authoredKey && this.textures.exists(authoredKey) ? authoredKey : `enemy-${id}`;
      const key = ENEMIES[id].boss ? this.cleanBossTexture(sourceKey) : sourceKey;
      if (this.textures.exists(key)) artEnemies[id] = key;
    }
    for (const e of this.enemies) {
      if (!e.alive) {
        const old = this.enemySprites.get(e.id);
        if (old) {
          old.destroy();
          this.enemySprites.delete(e.id);
        }
        continue;
      }
      const key = artEnemies[e.def.id];
      if (!key || !this.textures.exists(key)) continue;
      let img = this.enemySprites.get(e.id);
      if (!img) {
        // Boss silhouettes sit above transient impact FX so a volley can read
        // as an effect around the target instead of erasing the target.
        img = this.worldAdd(this.add.image(e.x, e.y, key).setDepth(e.isBoss ? 22 : 14));
        const s = Math.max(26, e.def.radius * 3.2) / img.width;
        img.setScale(s * this.terrainDepthScale(e.x, e.y));
        this.enemySprites.set(e.id, img);
      }
      // Keep a live enemy bound to the current authored art. This matters
      // when a scene is restarted after an art update: the old image must not
      // survive behind the replacement boss texture.
      if (img.texture.key !== key) img.setTexture(key);
      img.setPosition(e.x, e.y);
      const s = Math.max(26, e.def.radius * 3.2) / img.width;
        img.setScale(s * this.terrainDepthScale(e.x, e.y));
      // The generated boss plates are authored front-facing. Rotating them
      // with the path angle makes the silhouette tumble at corners and reads
      // like a broken sprite sheet. Regular units retain lane-facing motion.
      img.setRotation(e.isBoss ? 0 : e.angle + Math.PI / 2);
      img.setDepth(e.isBoss ? 22 : 14);
      img.setAlpha(e.hitFlash > 0 ? 0.75 : 1);
      if (e.hitFlash > 0) img.setTint(0xffffff);
      else if (e.stunT > 0) img.setTint(0xffff66);
      else img.clearTint();
    }
    for (const [id, img] of this.enemySprites) {
      if (!this.enemies.find((e) => e.id === id && e.alive)) {
        img.destroy();
        this.enemySprites.delete(id);
      }
    }
  }

  private resolveTowerArt(t: Tower): { bodyKey: string; weaponKey?: string; spec?: TowerArtSpec } {
    const separated = TOWER_ART[t.def.id];
    if (separated && this.textures.exists(separated.bodyKey)) {
      return { bodyKey: separated.bodyKey, weaponKey: separated.weaponKey, spec: separated };
    }
    const v4Key = `tower-${t.def.id}-v4`;
    if (this.textures.exists(v4Key)) {
      // v4 is the complete level top-down plate used by both the ghost and
      // the planted turret. It must not be reinterpreted as a rotating rig.
      return { bodyKey: v4Key };
    }
    // Legacy complete plates are still useful as a fallback, but they are
    // mounted level and are never tilted with the ground plane or aim angle.
    const v3Key = `top-${t.def.id}-v3`;
    const bodyKey = this.textures.exists(v3Key)
      ? v3Key
      : t.def.id === 'dronehive' && this.textures.exists('top-dronehive-v2')
        ? 'top-dronehive-v2'
        : t.def.id === 'vulcan' && this.textures.exists('top-vulcan-v2')
          ? 'top-vulcan-v2'
          : `top-${t.def.id}`;
    return { bodyKey };
  }

  /** Remove checkerboard pixels that slipped into an authored boss plate. */
  private cleanBossTexture(sourceKey: string): string {
    const cached = this.bossTextureAliases.get(sourceKey);
    if (cached) return cached;
    if (typeof document === 'undefined' || !this.textures.exists(sourceKey)) return sourceKey;
    const source = this.textures.get(sourceKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    if (!source?.width || !source?.height) return sourceKey;
    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return sourceKey;
    ctx.drawImage(source, 0, 0);
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = image.data;
    const w = canvas.width;
    const h = canvas.height;
    const candidate = (index: number) => {
      const r = data[index]; const g = data[index + 1]; const b = data[index + 2];
      return data[index + 3] > 0 && Math.max(r, g, b) - Math.min(r, g, b) <= 24 && Math.min(r, g, b) >= 160;
    };
    const seen = new Uint8Array(w * h);
    const queue: number[] = [];
    const enqueue = (x: number, y: number) => {
      const p = y * w + x;
      if (!seen[p] && candidate(p * 4)) { seen[p] = 1; queue.push(p); }
    };
    for (let x = 0; x < w; x++) { enqueue(x, 0); enqueue(x, h - 1); }
    for (let y = 1; y < h - 1; y++) { enqueue(0, y); enqueue(w - 1, y); }
    for (let i = 0; i < queue.length; i++) {
      const p = queue[i];
      data[p * 4 + 3] = 0;
      const x = p % w; const y = Math.floor(p / w);
      if (x > 0) enqueue(x - 1, y); if (x + 1 < w) enqueue(x + 1, y);
      if (y > 0) enqueue(x, y - 1); if (y + 1 < h) enqueue(x, y + 1);
    }
    ctx.putImageData(image, 0, 0);
    const cleanKey = `${sourceKey}-clean`;
    if (!this.textures.exists(cleanKey)) this.textures.addCanvas(cleanKey, canvas);
    this.bossTextureAliases.set(sourceKey, cleanKey);
    return cleanKey;
  }

  private showMessage(msg: string, radio = true): void {
    const text = radio ? `[ARC]  ${msg}` : msg;
    this.txtMsg.setText(text);
    // Give commanders enough time to read battlefield callouts and reward
    // feedback before the next message replaces it.
    this.msgTimer = 4.2;
    bus.emit(Events.Message, text);
  }

  private showAchievementToast(def: { name: string; desc: string }): void {
    this.showNotificationToast('ACHIEVEMENT UNLOCKED', def.name, def.desc, 0xd4a24a);
  }

  /** Keep the mobile display dock from masking the toast on landscape phones. */
  private notificationToastX(width: number): number {
    const dock = typeof document === 'undefined' ? undefined : document.getElementById('mobileDisplayDock');
    const canvas = this.game.canvas;
    if (!dock || !canvas || !dock.getClientRects().length) return BATTLE_W / 2;
    const canvasBounds = canvas.getBoundingClientRect();
    const dockBounds = dock.getBoundingClientRect();
    if (!canvasBounds.width || dockBounds.left <= canvasBounds.left) return BATTLE_W / 2;
    const scale = canvasBounds.width / BATTLE_W;
    const dockLeft = (dockBounds.left - canvasBounds.left) / scale;
    const minX = width / 2 + 12;
    return Phaser.Math.Clamp(Math.min(BATTLE_W / 2, dockLeft - width / 2 - 12), minX, BATTLE_W - width / 2 - 12);
  }

  private showNotificationToast(title: string, nameText: string, descText: string, accent = 0xd4a24a): void {
    if (this.achievementToast) this.achievementToast.destroy();
    const width = 520;
    const height = 76;
    const bg = this.add.graphics();
    bg.fillStyle(0x08131c, NOTIFICATION_TOAST_BACKGROUND_ALPHA);
    bg.fillRoundedRect(-width / 2, 0, width, height, 8);
    bg.lineStyle(2, accent, 0.95);
    bg.strokeRoundedRect(-width / 2, 0, width, height, 8);
    bg.lineStyle(1, 0x48c3e6, 0.45);
    bg.lineBetween(-width / 2 + 12, 28, width / 2 - 12, 28);
    const accentHex = `#${accent.toString(16).padStart(6, '0')}`;
    const label = this.add.text(0, 8, title, {
      fontFamily: 'Consolas, monospace', fontSize: '11px', fontStyle: 'bold', color: accentHex,
      align: 'center',
    }).setOrigin(0.5, 0);
    const name = this.add.text(0, 32, nameText, {
      fontFamily: 'Consolas, monospace', fontSize: '15px', fontStyle: 'bold', color: '#e9f7ff',
      align: 'center',
    }).setOrigin(0.5, 0);
    const desc = this.add.text(0, 54, descText, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8',
      align: 'center', wordWrap: { width: width - 28, useAdvancedWrap: true },
    }).setOrigin(0.5, 0);
    const toast = this.add.container(this.notificationToastX(width), UI_TOP_H + 48, [bg, label, name, desc])
      .setDepth(100)
      .setAlpha(0);
    this.achievementToast = toast;
    this.achievementToastTimer = NOTIFICATION_TOAST_DURATION_SECONDS;
    this.tweens.add({ targets: toast, alpha: 1, y: toast.y + 6, duration: 180, ease: 'Quad.Out' });
  }

  private showBanner(text: string, colour = '#cfeaff'): void {
    this.txtBanner.setText(text).setColor(colour).setAlpha(1).setScale(1.22);
    this.bannerT = 2.2;
  }

  private finish(win: boolean): void {
    if (this.gameOver) return;
    this.gameOver = true;
    this.placeMode = 'none';
    this.autoTimer?.remove();
    this.autoTimer = null;
    music.stopBattleOpening();
    this.collectBossLootBeforeFinish();
    saveManager.clearActiveRun();

    const bannerTxt = this.survival ? 'SURVIVAL ENDED' : win ? 'INSTALLATION HELD' : 'COMMAND OVERRUN';
    this.showBanner(bannerTxt, this.survival ? '#d4a24a' : win ? '#7dce5a' : '#ff4a4a');
    this.cameras.main.fadeOut(650, 0, 0, 0);

    const unspentCredits = win ? Math.max(0, Math.floor(this.supply)) : 0;
    const cappedCredits = Math.min(20_000, unspentCredits);
    const integrityScoreMultiplier = this.difficulty === 'nightmare' ? 30 : this.difficulty === 'hard' ? 20 : 10;
    // 1,000 reserve credits have the same score weight as one remaining
    // integrity point. The 20k soft cap keeps hoarding from dominating a run.
    const integrityBonus = win && !this.survival
      ? Math.floor((this.integrity + cappedCredits / 1_000) * integrityScoreMultiplier)
      : 0;
    if (win && !this.survival) this.score += integrityBonus + 500;
    const creditMultiplier = this.difficulty === 'nightmare' ? 1 : this.difficulty === 'hard' ? 1 / 3 : 1 / 4;
    const creditBonus = Math.floor(unspentCredits * creditMultiplier);
    if (creditBonus > 0) this.score += creditBonus;
    const averageSpeed = this.averageSpeedMultiplier();
    const tempoMultiplier = 1 + Math.max(0, averageSpeed - 1) * 0.35;
    const tempoBonus = win ? Math.floor(this.score * (tempoMultiplier - 1)) : 0;
    if (tempoBonus > 0) this.score += tempoBonus;
    const xpEarned = Math.floor((this.score / 10) * tempoMultiplier * XP_REWARD_MULTIPLIER);
    const mapDataAward = win && !this.survival
      ? MAP_CLEAR_DATA_REWARD_BASE
      : 0;

    if (this.survival) {
      // Survival always ends in defeat; record bests
      const waves = this.waveIndex;
      const time = this.survivalTime;
      audio.defeat();
      saveManager.addXp(xpEarned);
      achievements.noteRank();
      const prev = saveManager.data.survivalBest[this.missionId];
      if (!prev || waves > prev.waves || (waves === prev.waves && this.score > prev.score)) {
        saveManager.data.survivalBest[this.missionId] = {
          waves,
          score: Math.floor(this.score),
          time: Math.floor(time),
        };
        saveManager.save();
      }
      this.time.delayedCall(800, () => {
        this.scene.start('Results', {
          win: false,
          score: this.score,
          integrity: this.integrity,
          maxIntegrity: this.maxIntegrity,
          waves,
          survival: true,
          time: Math.floor(time),
          missionName: this.mapDef.name,
          missionId: this.missionId,
          difficulty: this.difficulty,
          kills: this.towers.reduce((sum, tower) => sum + tower.kills, 0),
          averageSpeed,
          tempoBonus: 0,
          unspentCredits,
          creditBonus,
          integrityBonus,
          mapDataAward,
          xpEarned,
          towerPlacements: this.towers.map((tower) => ({ id: tower.def.id, x: tower.x, y: tower.y, stage: tower.stageIndex })),
        });
      });
      return;
    }

    let nextMissionId: string | undefined;
    if (win) {
      audio.victory();
      announcer.say('victory');
      music.setIntensity(0.2);
      saveManager.completeMission(this.missionId);
      nextMissionId = getNextUnbeatenMissionId(saveManager.data.missionsComplete, saveManager.data.settings.devMode);
      if (mapDataAward > 0) saveManager.adjustResearchPoints(mapDataAward);
      saveManager.addXp(xpEarned);
      this.finalizeMapResearchBudget();
      achievements.noteMissionComplete(
        this.missionId,
        this.integrity,
        this.maxIntegrity,
        this.difficulty,
      );
      achievements.noteRank();
      saveManager.recordScore(this.missionId, this.score);
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('apogee-command-wing-update'));
    } else {
      audio.defeat();
      announcer.say('defeat');
      music.setIntensity(0.1);
      saveManager.recordMissionFailure(this.missionId);
    }
    this.time.delayedCall(800, () => {
      this.scene.start('Results', {
        win,
        score: this.score,
        integrity: this.integrity,
        maxIntegrity: this.maxIntegrity,
        waves: this.waveIndex,
        survival: false,
        missionName: this.mapDef.name,
        missionId: this.missionId,
        nextMissionId,
        difficulty: this.difficulty,
        kills: this.towers.reduce((sum, tower) => sum + tower.kills, 0),
        averageSpeed,
        tempoBonus,
        unspentCredits,
        creditBonus,
        integrityBonus,
        mapDataAward,
        xpEarned,
        towerPlacements: this.towers.map((tower) => ({ id: tower.def.id, x: tower.x, y: tower.y, stage: tower.stageIndex })),
      });
    });
  }

  private averageSpeedMultiplier(): number {
    if (this.realElapsed <= 0) return 1;
    return Phaser.Math.Clamp(this.simElapsed / this.realElapsed, 1, 3);
  }

  /** Keep rank and cache DATA from stacking beyond the map's 15-point budget. */
  private finalizeMapResearchBudget(): void {
    const gained = saveManager.data.researchPoints - this.researchPointsAtSortieStart;
    const adjustment = MAP_CLEAR_DATA_REWARD_BASE - gained;
    if (adjustment !== 0) {
      saveManager.adjustResearchPoints(adjustment);
      saveManager.save();
    }
  }

  // ---------- DRAW ----------

  private drawEntities(): void {
    const g = this.gEntities;
    g.clear();

    // Barriers: the long axis is rotated to the route normal so the hardlight
    // wall crosses the entire lane instead of pointing along it.
    const wallPoints = (x: number, y: number, angle: number, length: number, width: number) => {
      const ux = Math.cos(angle) * length * 0.5;
      const uy = Math.sin(angle) * length * 0.5;
      const vx = -Math.sin(angle) * width * 0.5;
      const vy = Math.cos(angle) * width * 0.5;
      return [
        { x: x - ux - vx, y: y - uy - vy },
        { x: x + ux - vx, y: y + uy - vy },
        { x: x + ux + vx, y: y + uy + vy },
        { x: x - ux + vx, y: y - uy + vy },
      ];
    };
    const drawWall = (x: number, y: number, angle: number, colour: number, alpha: number, length = 48, width = 14) => {
      const pts = wallPoints(x, y, angle, length, width);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.fillStyle(colour, alpha);
      g.fillPath();
      g.lineStyle(1.5, colour, Math.min(1, alpha + 0.3));
      g.strokePath();
    };
    for (const b of this.barriers) {
      const pulse = 0.7 + Math.sin(this.time.now / 100) * 0.2;
      // glow field
      g.fillStyle(COLORS.barrier, 0.12 * pulse);
      const glow = wallPoints(b.x, b.y, b.angle, 58, 24);
      g.beginPath();
      g.moveTo(glow[0].x, glow[0].y);
      for (let i = 1; i < glow.length; i++) g.lineTo(glow[i].x, glow[i].y);
      g.closePath();
      g.fillPath();
      // posts
      g.fillStyle(b.hitFlash > 0 ? 0xffffff : 0x6ab0d0, 1);
      const ux = Math.cos(b.angle) * 20;
      const uy = Math.sin(b.angle) * 20;
      g.fillCircle(b.x - ux, b.y - uy, 3);
      g.fillCircle(b.x + ux, b.y + uy, 3);
      // energy wall
      drawWall(b.x, b.y, b.angle, b.hitFlash > 0 ? 0xffffff : COLORS.barrier, 0.55 * pulse);
      // scan lines
      for (let i = 0; i < 3; i++) {
        g.lineStyle(1, 0xffffff, 0.15);
        const off = (i - 1) * 4;
        const px = b.x - Math.sin(b.angle) * off;
        const py = b.y + Math.cos(b.angle) * off;
        g.lineBetween(px - Math.cos(b.angle) * 18, py - Math.sin(b.angle) * 18, px + Math.cos(b.angle) * 18, py + Math.sin(b.angle) * 18);
      }
      const pct = b.hp / b.maxHp;
      g.fillStyle(0x000000, 0.5);
      g.fillRect(b.x - 16, b.y - 14, 32, 3);
      g.fillStyle(pct > 0.4 ? COLORS.barrier : COLORS.warn, 1);
      g.fillRect(b.x - 16, b.y - 14, 32 * pct, 3);
    }

    // Towers
    for (const t of this.towers) {
      this.drawTower(g, t);
    }
    this.drawTowerHealthBars(g);
    this.drawSwarmDrones(g);

    // Enemies
    for (const e of this.enemies) {
      this.drawEnemy(g, e);
    }

  }

  private drawTowerHealthBars(g: Phaser.GameObjects.Graphics): void {
    for (const t of this.towers) {
      const damaged = t.hp < t.maxHp;
      const selected = this.selectedTower?.id === t.id;
      if (!damaged && !selected) continue;
      const scale = this.terrainDepthScale(t.x, t.y);
      const width = 34 * scale;
      const y = t.y - 27 * scale;
      const pct = Phaser.Math.Clamp(t.healthPercent, 0, 1);
      g.fillStyle(0x05080a, 0.82);
      g.fillRect(t.x - width / 2, y, width, 4);
      g.fillStyle(t.isDisabled ? 0xff4f66 : pct > 0.55 ? 0x7dce5a : pct > 0.25 ? 0xe0a744 : 0xff4f66, 1);
      g.fillRect(t.x - width / 2, y, width * pct, 4);
      g.lineStyle(1, 0x9ad4e8, 0.65);
      g.strokeRect(t.x - width / 2, y, width, 4);
      if (t.isDisabled) {
        g.fillStyle(0xff6a6a, 0.9);
        g.fillCircle(t.x, y - 5, 2.2);
      }
    }
  }

  private drawSwarmDrones(g: Phaser.GameObjects.Graphics): void {
    const now = this.time.now;
    for (const swarm of this.swarmDrones.values()) {
      for (const drone of swarm) {
        const weaponCol = drone.weapon === 'machine-gun' ? 0xffd36a : drone.weapon === 'bomb' ? 0xff9c4d : 0xff5e8c;
        const pulse = 0.65 + Math.sin(now / 80 + drone.id) * 0.2;
        if (drone.phase !== 'docked') {
          g.lineStyle(1, weaponCol, 0.18);
          g.lineBetween(drone.x - Math.cos(drone.angle) * 13, drone.y - Math.sin(drone.angle) * 13, drone.x, drone.y);
        }
        if (drone.cohesion) {
          g.lineStyle(1.2, 0x9dfff0, 0.35 + pulse * 0.25);
          g.strokeCircle(drone.x, drone.y, 12 + Math.sin(now / 120 + drone.id) * 2);
        }
        if (drone.fireFlash > 0) {
          g.fillStyle(0xffffff, drone.fireFlash / 0.14);
          g.fillCircle(drone.x + Math.cos(drone.angle) * 8, drone.y + Math.sin(drone.angle) * 8, 3);
        }
      }
    }
  }

  private drawTower(g: Phaser.GameObjects.Graphics, t: Tower): void {
    const col = t.def.colour;
    const stage = t.stageIndex;
    const now = this.time.now;
    const hasSprite = this.towerSprites.has(t.id);
    const recoil = t.flash > 0 ? t.flash * 4 : 0;
    const barrelLen = 14 + stage * 3 - recoil;
    const bx = t.x + Math.cos(t.angle) * barrelLen;
    const by = t.y + Math.sin(t.angle) * barrelLen;
    const glow = 0.55 + Math.sin(now / 400 + t.id) * 0.1;
    const depthScale = this.terrainDepthScale(t.x, t.y);
    g.save();
    g.translateCanvas(t.x, t.y);
    g.scaleCanvas(depthScale, depthScale);
    g.translateCanvas(-t.x, -t.y);

    // Shadow under sprite towers
    if (hasSprite) {
      g.fillStyle(0x000000, 0.42);
      g.fillEllipse(t.x + 2, t.y + 6, 38 + stage * 5, 19 + stage * 2);
    } else {
    g.fillStyle(0x000000, 0.35);
    g.fillEllipse(t.x + 2, t.y + 4, (14 + stage) * 2.1, (14 + stage) * 1.2);

    // Base plate
    g.fillStyle(0x121a20, 1);
    g.fillCircle(t.x, t.y, 15 + stage);
    g.fillStyle(0x1a242c, 1);
    g.fillCircle(t.x, t.y, 12 + stage);
    g.lineStyle(1.5, col, 0.85);
    g.strokeCircle(t.x, t.y, 12 + stage);
    // stage notches
    for (let i = 0; i <= stage; i++) {
      const a = (i / 4) * Math.PI * 2 - Math.PI / 2;
      g.fillStyle(col, 0.7);
      g.fillCircle(t.x + Math.cos(a) * (14 + stage), t.y + Math.sin(a) * (14 + stage), 1.8);
    }

    if (t.def.id === 'ballistic') {
      // housing
      g.fillStyle(0x0e1418, 1);
      g.fillCircle(t.x, t.y, 7);
      g.lineStyle(3.5, col, 1);
      g.lineBetween(t.x, t.y, bx, by);
      g.lineStyle(1.5, 0xffffff, 0.35);
      g.lineBetween(t.x, t.y, bx - Math.cos(t.angle) * 2, by - Math.sin(t.angle) * 2);
      if (stage >= 1) {
        const a2 = t.angle + 0.2;
        const a3 = t.angle - 0.2;
        g.lineStyle(2.5, col, 0.9);
        g.lineBetween(t.x, t.y, t.x + Math.cos(a2) * barrelLen * 0.92, t.y + Math.sin(a2) * barrelLen * 0.92);
        g.lineBetween(t.x, t.y, t.x + Math.cos(a3) * barrelLen * 0.92, t.y + Math.sin(a3) * barrelLen * 0.92);
      }
      if (stage >= 3) {
        // rotary hint
        g.lineStyle(1, 0xffffff, 0.25);
        g.strokeCircle(bx, by, 3);
      }
    } else if (t.def.id === 'thermal') {
      // fuel tanks
      g.fillStyle(0x2a1810, 1);
      g.fillRoundedRect(t.x - 10, t.y - 6, 6, 12, 2);
      g.fillRoundedRect(t.x + 4, t.y - 6, 6, 12, 2);
      g.lineStyle(1, col, 0.6);
      g.strokeRoundedRect(t.x - 10, t.y - 6, 6, 12, 2);
      g.strokeRoundedRect(t.x + 4, t.y - 6, 6, 12, 2);
      // nozzle cone
      g.fillStyle(col, 0.9);
      g.fillTriangle(
        t.x + Math.cos(t.angle) * 6,
        t.y + Math.sin(t.angle) * 6,
        t.x + Math.cos(t.angle + 2.4) * 11,
        t.y + Math.sin(t.angle + 2.4) * 11,
        t.x + Math.cos(t.angle - 2.4) * 11,
        t.y + Math.sin(t.angle - 2.4) * 11,
      );
      // idle pilot flame
      g.fillStyle(0xffaa44, glow * 0.5);
      g.fillCircle(bx, by, 3 + stage + Math.sin(now / 80) * 1);
      g.fillStyle(0xffee88, 0.6);
      g.fillCircle(bx, by, 2);
    } else if (t.def.id === 'artillery') {
      // carriage
      g.fillStyle(0x1a1814, 1);
      g.fillRoundedRect(t.x - 10, t.y - 8, 20, 16, 3);
      g.lineStyle(1.5, col, 0.7);
      g.strokeRoundedRect(t.x - 10, t.y - 8, 20, 16, 3);
      // long barrel
      g.lineStyle(6, 0x2a2218, 1);
      g.lineBetween(t.x, t.y, bx, by);
      g.lineStyle(4, col, 1);
      g.lineBetween(t.x + Math.cos(t.angle) * 2, t.y + Math.sin(t.angle) * 2, bx, by);
      g.lineStyle(1, 0xffeeaa, 0.3);
      g.lineBetween(t.x, t.y - 1, bx, by - 1);
      // muzzle brake
      g.fillStyle(0x1a1410, 1);
      g.fillCircle(bx, by, 3.5);
    } else if (t.def.id === 'dronehive') {
      const pulse = 0.65 + Math.sin(now / 240) * 0.25;
      g.fillStyle(col, 0.12 * pulse);
      g.fillCircle(t.x, t.y, 22 + stage * 2);
      g.fillStyle(0x0a161e, 1);
      g.fillRoundedRect(t.x - 12 - stage, t.y - 9 - stage, 24 + stage * 2, 18 + stage * 2, 5);
      g.lineStyle(1.5, col, 0.9);
      g.strokeRoundedRect(t.x - 12 - stage, t.y - 9 - stage, 24 + stage * 2, 18 + stage * 2, 5);
      g.fillStyle(col, 0.85);
      g.fillCircle(t.x, t.y, 4 + stage * 0.5);
      g.fillStyle(0xffffff, 0.75);
      g.fillCircle(t.x, t.y, 1.5);
      for (let i = 0; i < Math.min(6, 3 + stage); i++) {
        const a = now / 900 + i * (Math.PI * 2 / Math.min(6, 3 + stage));
        const hx = t.x + Math.cos(a) * (14 + stage);
        const hy = t.y + Math.sin(a) * (8 + stage * 0.8);
        g.lineStyle(1, col, 0.65);
        g.strokeCircle(hx, hy, 2.5);
      }
      g.lineStyle(1.5, col, 0.38 + pulse * 0.2);
      g.strokeCircle(t.x, t.y, 19 + stage * 2);
    } else {
      // energy — capacitors + core
      const pulse = 0.7 + Math.sin(now / 200) * 0.3;
      g.fillStyle(col, 0.15 * pulse);
      g.fillCircle(t.x, t.y, 16 + stage * 2);
      g.fillStyle(col, t.flash > 0 ? 1 : 0.75);
      g.fillCircle(t.x, t.y, 6 + stage);
      g.fillStyle(0xffffff, 0.5 + pulse * 0.3);
      g.fillCircle(t.x, t.y, 3 + stage * 0.5);
      g.lineStyle(2, col, 0.9);
      g.strokeCircle(t.x, t.y, 10 + stage * 2);
      if (stage >= 2) {
        g.lineStyle(1, col, 0.45);
        g.strokeCircle(t.x, t.y, 13 + stage * 2);
      }
      g.lineStyle(2, col, 0.75);
      g.lineBetween(t.x, t.y, bx, by);
      g.lineStyle(1, 0xffffff, 0.4);
      g.lineBetween(t.x, t.y, bx, by);
    }
    } // end !hasSprite procedural body

    // Bolt-on symbiosis turret
    if (t.hasSecondary) this.drawSecondaryTurret(g, t);

    // Overdrive aura
    if (t.specialActive > 0) {
      const pulse = 1 + Math.sin(now / 60) * 0.15;
      g.lineStyle(2.5, 0xffdd44, 0.75);
      g.strokeCircle(t.x, t.y, (18 + stage) * pulse);
      g.fillStyle(0xffdd44, 0.08);
      g.fillCircle(t.x, t.y, (20 + stage) * pulse);
    }

    // Special ready pulse
    if (t.canSpecial()) {
      const pulse = 0.6 + Math.sin(now / 180) * 0.4;
      g.lineStyle(2, 0xffdd44, pulse);
      g.strokeCircle(t.x, t.y, 17 + stage);
    }

    // Jam
    if (t.jammed > 0) {
      g.lineStyle(2, 0xff00ff, 0.55);
      g.strokeCircle(t.x, t.y, 16);
      g.fillStyle(0xff00ff, 0.12);
      g.fillCircle(t.x, t.y, 16);
    }
    g.restore();

    // Tactical range is a world-space distance. Keep it outside the turret's
    // perspective scale so the ring exactly matches targeting distance.
    const isBaseMounted = this.pads.find((pad) => pad.id === t.padId)?.kind === 'base';
    if (this.selectedTower?.id === t.id && !isBaseMounted) {
      const r = t.range;
      g.fillStyle(col, 0.035);
      g.fillCircle(t.x, t.y, r);
      g.lineStyle(1.25, col, 0.5);
      g.strokeCircle(t.x, t.y, r);
      g.lineStyle(1, col, 0.15);
      g.strokeCircle(t.x, t.y, r * 0.66);
      // Rotating dashed outer arc.
      const segs = 48;
      const rot = now / 1600;
      for (let i = 0; i < segs; i += 2) {
        const a0 = (i / segs) * Math.PI * 2 + rot;
        const a1 = ((i + 1) / segs) * Math.PI * 2 + rot;
        g.lineStyle(2.5, col, 0.6);
        g.beginPath();
        g.arc(t.x, t.y, r, a0, a1, false);
        g.strokePath();
      }
    }
  }

  /** Procedural bolt-on symbiosis module (secondary turret). */
  private drawSecondaryTurret(g: Phaser.GameObjects.Graphics, t: Tower): void {
    const sd = t.secondaryDef;
    if (!sd) return;
    const col = sd.colour;
    const off = 18;
    const perp = t.secondaryAngle + Math.PI / 2;
    const px = t.x + Math.cos(perp) * off;
    const py = t.y + Math.sin(perp) * off;
    const a = t.secondaryAngle;
    const flash = t.secondaryFlash > 0;

    if (sd.kind === 'support') {
      const ar = sd.auraRange ?? 70;
      g.fillStyle(col, 0.05);
      g.fillCircle(t.x, t.y, ar);
      g.lineStyle(1, col, 0.24 + Math.sin(this.time.now / 400) * 0.08);
      g.strokeCircle(t.x, t.y, ar);
      g.fillStyle(0x0e1418, 1);
      g.fillCircle(px, py, 7);
      g.lineStyle(1.5, col, 0.9);
      g.strokeCircle(px, py, 7);
      g.fillStyle(col, 0.8);
      g.fillCircle(px, py, 3);
      return;
    }

    g.fillStyle(0x101820, 1);
    g.fillCircle(px, py, 8);
    g.lineStyle(1.5, col, 0.85);
    g.strokeCircle(px, py, 8);

    if (sd.kind === 'missile' || sd.kind === 'salvo') {
      const tubes = sd.kind === 'salvo' ? 4 : 2;
      for (let i = 0; i < tubes; i++) {
        const spread = (i - (tubes - 1) / 2) * 4;
        const ox = Math.cos(a) * 7 + Math.cos(a + Math.PI / 2) * spread;
        const oy = Math.sin(a) * 7 + Math.sin(a + Math.PI / 2) * spread;
        g.fillStyle(flash ? 0xffffff : col, 0.95);
        g.fillRect(px + ox - 2, py + oy - 2, 4, 4);
      }
    } else if (sd.kind === 'arc') {
      g.lineStyle(1.5, flash ? 0xffffff : col, 0.9);
      g.strokeCircle(px, py, 5);
      g.lineStyle(1, col, 0.6);
      g.lineBetween(px, py, px + Math.cos(a) * 9, py + Math.sin(a) * 9);
    } else {
      g.lineStyle(3, 0x2a333b, 1);
      g.lineBetween(px, py, px + Math.cos(a) * 10, py + Math.sin(a) * 10);
      g.lineStyle(2, col, 1);
      g.lineBetween(px, py, px + Math.cos(a) * 9, py + Math.sin(a) * 9);
    }
    if (flash) {
      this.vfx.flash(px + Math.cos(a) * 9, py + Math.sin(a) * 9, 6, col, 'muzzle', a, 0.06);
    }
  }

  private drawEnemy(g: Phaser.GameObjects.Graphics, e: Enemy): void {
    const r = e.def.radius;
    const col = e.def.colour;
    const bob = Math.sin(e.bob) * 1.5;
    const hasSprite = this.enemySprites.has(e.id);
    const depthScale = this.terrainDepthScale(e.x, e.y);
    g.save();
    g.translateCanvas(e.x, e.y);
    g.scaleCanvas(depthScale, depthScale);
    g.translateCanvas(-e.x, -e.y);

    // Ground shadow
    g.fillStyle(0x000000, 0.32);
    g.fillEllipse(e.x, e.y + r * 0.5 + bob, r * 2.1, r * 1.1);

    // Shield (skip body ring when sprite art is used)
    if (e.shield > 0) {
      g.fillStyle(COLORS.enemyShield, 0.12);
      g.fillCircle(e.x, e.y + bob, r + (hasSprite ? 8 : 5));
      g.lineStyle(1.5, COLORS.enemyShield, 0.45);
      g.strokeCircle(e.x, e.y + bob, r + (hasSprite ? 8 : 5));
    }

    if (hasSprite) {
      // Bright rim so dark enemy plates stay readable on dark terrain.
      g.lineStyle(2, e.hitFlash > 0 ? 0xffffff : 0xffb0a0, e.hitFlash > 0 ? 0.9 : 0.45);
      g.strokeCircle(e.x, e.y + bob, r + 6);
    }
    // Body (skipped when sprite art is used). Each archetype gets a readable silhouette.
    if (!hasSprite) {
      const cy = e.y + bob;
      // Dark rim for contrast against the photographic plate.
      g.fillStyle(0x080d08, 0.9);
      g.fillCircle(e.x, cy, r + 1.5);

      const fam = e.def.family ?? 'mesh';
      if (fam === 'insect') {
        this.drawInsect(g, e, cy);
      } else if (fam === 'robot') {
        this.drawRobot(g, e, cy);
      } else if (fam === 'human') {
        this.drawHuman(g, e, cy);
      } else if (e.isBoss) {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(3, 0xff2020, 1);
        g.strokeCircle(e.x, cy, r);
        for (let i = 0; i < 6; i++) {
          const a = e.angle + (i * Math.PI) / 3;
          g.lineStyle(3, col, 1);
          g.lineBetween(e.x, cy, e.x + Math.cos(a) * (r + 6), cy + Math.sin(a) * (r + 6));
        }
      } else if (e.isElite) {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        for (let i = 0; i < 8; i++) {
          const a = e.angle + (i * Math.PI) / 4;
          g.lineStyle(2, 0xffaaaa, 0.9);
          g.lineBetween(e.x + Math.cos(a) * r * 0.8, cy + Math.sin(a) * r * 0.8, e.x + Math.cos(a) * (r + 5), cy + Math.sin(a) * (r + 5));
        }
        g.lineStyle(2, 0xff8888, 1);
        g.strokeCircle(e.x, cy, r);
      } else if (e.def.id === 'dashling') {
        g.fillStyle(col, 1);
        g.fillEllipse(e.x, cy, r * 2.3, r * 1.1);
        g.lineStyle(1.5, 0x2a3a10, 1);
        g.strokeEllipse(e.x, cy, r * 2.3, r * 1.1);
        g.lineStyle(1, 0xeaffaa, 0.4);
        g.lineBetween(e.x - r * 1.7, cy, e.x - r * 0.7, cy);
      } else if (e.def.id === 'plateback') {
        g.fillStyle(col, 1);
        g.fillRoundedRect(e.x - r, cy - r * 0.8, r * 2, r * 1.6, 4);
        g.lineStyle(2, 0x2a4a20, 1);
        g.strokeRoundedRect(e.x - r, cy - r * 0.8, r * 2, r * 1.6, 4);
        g.lineStyle(1, 0x2a4a20, 0.8);
        for (let i = -1; i <= 1; i++) g.lineBetween(e.x - r + 3, cy + i * r * 0.4, e.x + r - 3, cy + i * r * 0.4);
      } else if (e.def.id === 'veilguard') {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(2, COLORS.enemyShield, 0.85);
        g.strokeCircle(e.x, cy, r + 2);
        g.lineStyle(1, 0x9fe0ff, 0.45);
        g.strokeCircle(e.x, cy, r + 5);
      } else if (e.def.id === 'goliath') {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(3, 0x1e3a18, 1);
        g.strokeCircle(e.x, cy, r);
        g.fillStyle(0x1e3a18, 1);
        g.fillCircle(e.x, cy, r * 0.45);
        g.fillStyle(col, 0.9);
        g.fillCircle(e.x, cy, r * 0.28);
      } else if (e.def.id === 'mender') {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(3, 0x0e4a34, 1);
        g.strokeCircle(e.x, cy, r);
        g.lineStyle(2.5, 0xddfff0, 0.95);
        g.lineBetween(e.x - r * 0.5, cy, e.x + r * 0.5, cy);
        g.lineBetween(e.x, cy - r * 0.5, e.x, cy + r * 0.5);
      } else if (e.def.id === 'broodnode') {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(2, 0x3a4a18, 1);
        g.strokeCircle(e.x, cy, r);
        for (let i = 0; i < 3; i++) {
          const a = e.angle + (i * Math.PI * 2) / 3;
          g.fillStyle(0x7a9a2a, 0.9);
          g.fillCircle(e.x + Math.cos(a) * r * 0.5, cy + Math.sin(a) * r * 0.5, r * 0.28);
        }
      } else if (e.def.id === 'sporethrower') {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(2, 0x6a5a10, 1);
        g.strokeCircle(e.x, cy, r);
        for (let i = 0; i < 5; i++) {
          const a = e.angle + (i * Math.PI * 2) / 5;
          g.fillStyle(0xe8dc6a, 0.9);
          g.fillCircle(e.x + Math.cos(a) * r, cy + Math.sin(a) * r, 2);
        }
      } else if (e.def.id === 'nulltick') {
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(2, 0x5a1a5a, 1);
        g.strokeCircle(e.x, cy, r);
        g.lineStyle(1, 0xff66ff, 0.5);
        g.strokeCircle(e.x, cy, r + 4 + Math.sin(e.bob * 2) * 2);
      } else {
        // Husk and default combat forms.
        g.fillStyle(col, 1);
        g.fillCircle(e.x, cy, r);
        g.lineStyle(2, 0x1a2e12, 1);
        g.strokeCircle(e.x, cy, r);
        g.lineStyle(1, 0x1a2e12, 0.7);
        g.lineBetween(e.x - r * 0.6, cy - r * 0.3, e.x + r * 0.6, cy - r * 0.3);
      }

      if (e.hitFlash > 0) {
        g.fillStyle(0xffffff, 0.55);
        g.fillCircle(e.x, cy, r * 1.05);
      }
    }

    // HP bar — shown for elites, bosses, and any unit that has taken damage.
    if (e.isElite || e.isBoss || e.hp < e.maxHp) {
      const w = Math.max(22, r * 2.4);
      const pct = Math.max(0, e.hp / e.maxHp);
      const by = e.y - r - (hasSprite ? 12 : 10) + bob;
      g.fillStyle(0x000000, 0.62);
      g.fillRect(e.x - w / 2 - 1, by - 1, w + 2, 5);
      g.fillStyle(pct > 0.5 ? COLORS.good : pct > 0.25 ? COLORS.warn : COLORS.danger, 1);
      g.fillRect(e.x - w / 2, by, w * pct, 3);
      if (e.maxShield > 0 && e.shield > 0) {
        g.fillStyle(COLORS.enemyShield, 0.85);
        g.fillRect(e.x - w / 2, by - 4, w * (e.shield / e.maxShield), 2);
      }
    }

    // Status
    if (e.stunT > 0) {
      g.lineStyle(2, 0xffff00, 0.8);
      g.strokeCircle(e.x, e.y + bob, r + 3);
    }
    const burn = e.statuses.find((s) => s.kind === 'BURN');
    if (burn) {
      g.fillStyle(COLORS.thermal, 0.35);
      g.fillCircle(e.x, e.y + bob, r * 0.6);
    }
    if (e.statuses.find((s) => s.kind === 'SLOW')) {
      g.lineStyle(1, 0x66ccff, 0.6);
      g.strokeCircle(e.x, e.y + bob, r + 2);
    }

    if (this.selectedEnemy?.id === e.id) {
      g.lineStyle(1.5, 0xffffff, 0.8);
      g.strokeCircle(e.x, e.y + bob, r + 6);
    }
    g.restore();
  }

  /** Six-legged chitin runner with a scuttling walk cycle and flapping wings. */
  private drawInsect(g: Phaser.GameObjects.Graphics, e: Enemy, cy: number): void {
    const r = e.def.radius;
    const col = e.def.colour;
    const f = e.angle;
    const px = Math.cos(f);
    const py = Math.sin(f);
    const nx = -py;
    const ny = px;

    // Legs — three pairs, alternating tripod gait.
    g.lineStyle(1.7, 0x2a3a12, 1);
    for (let i = 0; i < 3; i++) {
      const along = (i - 1) * r * 0.55;
      const swing = Math.sin(e.bob * 3.2 + i * 2.1) * r * 0.4;
      for (const side of [-1, 1]) {
        const bx = e.x + px * along + nx * side * r * 0.3;
        const by = cy + py * along + ny * side * r * 0.3;
        const kx = bx + nx * side * r * 0.75 + px * swing;
        const ky = by + ny * side * r * 0.75 + py * swing;
        const fx = kx + nx * side * r * 0.45 - px * swing * 0.7;
        const fy = ky + ny * side * r * 0.45 - py * swing * 0.7;
        g.lineBetween(bx, by, kx, ky);
        g.lineBetween(kx, ky, fx, fy);
      }
    }

    // Abdomen segments.
    for (let i = 2; i >= 0; i--) {
      const t = i / 2;
      const ax = e.x - px * (r * 0.5 + t * r * 0.85);
      const ay = cy - py * (r * 0.5 + t * r * 0.85);
      g.fillStyle(i === 0 ? col : 0x6a7a2a, 1);
      g.fillCircle(ax, ay, r * (0.52 - t * 0.16));
    }

    // Thorax + head.
    g.fillStyle(col, 1);
    g.fillCircle(e.x, cy, r * 0.72);
    g.fillStyle(0x1e2a10, 1);
    const hx = e.x + px * r * 0.72;
    const hy = cy + py * r * 0.72;
    g.fillCircle(hx, hy, r * 0.44);
    // Mandibles + antennae.
    g.lineStyle(1.5, 0x2a3a12, 1);
    g.lineBetween(hx, hy, hx + px * r * 0.5 + nx * r * 0.2, hy + py * r * 0.5 + ny * r * 0.2);
    g.lineBetween(hx, hy, hx + px * r * 0.5 - nx * r * 0.2, hy + py * r * 0.5 - ny * r * 0.2);
    g.lineStyle(1, 0x2a3a12, 0.85);
    for (const side of [-1, 1]) {
      g.lineBetween(hx, hy, hx + px * r * 0.8 + nx * side * r * 0.7, hy + py * r * 0.8 + ny * side * r * 0.7);
    }

    // Wings on the fast fliers.
    if (e.def.speed > 120) {
      const flap = 0.5 + 0.5 * Math.abs(Math.sin(e.bob * 14));
      g.fillStyle(0xe8f0b0, 0.18 + flap * 0.3);
      for (const side of [-1, 1]) {
        const wx = e.x + px * r * 0.2 + nx * side * r * 0.9;
        const wy = cy + py * r * 0.2 + ny * side * r * 0.9;
        g.fillEllipse(wx, wy, r * 1.5, r * (0.25 + flap * 0.5));
      }
    }

    if (e.isElite || e.isBoss) {
      g.lineStyle(e.isBoss ? 3 : 2, e.isBoss ? 0xff2020 : 0xff8888, 0.9);
      g.strokeCircle(e.x, cy, r + 4);
    }
  }

  /** Tracked chassis with a sweeping turret head and a pulsing reactor. */
  private drawRobot(g: Phaser.GameObjects.Graphics, e: Enemy, cy: number): void {
    const r = e.def.radius;
    const col = e.def.colour;
    const f = e.angle;
    const nx = -Math.sin(f);
    const ny = Math.cos(f);
    const pulse = 0.5 + 0.5 * Math.sin(e.bob * 3);

    // Treads.
    g.fillStyle(0x141c24, 1);
    for (const side of [-1, 1]) {
      const tx = e.x + nx * side * r * 0.85;
      const ty = cy + ny * side * r * 0.85;
      g.fillRoundedRect(tx - r * 0.32, ty - r * 0.55, r * 0.64, r * 1.1, 2);
      g.fillStyle(0x2c3a46, 0.8);
      for (let k = -1; k <= 1; k++) {
        g.fillRect(tx - r * 0.32, ty + k * r * 0.32 - 1, r * 0.64, 2);
      }
      g.fillStyle(0x141c24, 1);
    }

    // Chassis.
    g.fillStyle(col, 1);
    g.fillRoundedRect(e.x - r * 0.92, cy - r * 0.8, r * 1.84, r * 1.6, 4);
    g.lineStyle(2, 0x0e1a22, 1);
    g.strokeRoundedRect(e.x - r * 0.92, cy - r * 0.8, r * 1.84, r * 1.6, 4);
    g.lineStyle(1, 0x0e1a22, 0.7);
    g.lineBetween(e.x - r * 0.92, cy + r * 0.2, e.x + r * 0.92, cy + r * 0.2);

    // Sweeping turret.
    const headA = f + Math.sin(e.bob * 0.9) * 0.35;
    g.fillStyle(0x2a3844, 1);
    g.fillCircle(e.x, cy - r * 0.15, r * 0.5);
    g.lineStyle(3, 0x0e1a22, 1);
    g.lineBetween(
      e.x,
      cy - r * 0.15,
      e.x + Math.cos(headA) * r * 1.45,
      cy - r * 0.15 + Math.sin(headA) * r * 1.45,
    );
    g.fillStyle(0x66e0ff, 0.7 + pulse * 0.3);
    g.fillCircle(e.x + Math.cos(headA) * r * 1.45, cy - r * 0.15 + Math.sin(headA) * r * 1.45, 2.4);

    // Reactor core.
    g.fillStyle(col, 0.3 + pulse * 0.3);
    g.fillCircle(e.x, cy + r * 0.45, r * 0.42);
    g.fillStyle(0xffffff, 0.45 + pulse * 0.5);
    g.fillCircle(e.x, cy + r * 0.45, r * 0.2);

    if (e.isElite || e.isBoss) {
      g.lineStyle(e.isBoss ? 3 : 2, e.isBoss ? 0xff2020 : 0xff8888, 0.9);
      g.strokeCircle(e.x, cy, r + 4);
    }
  }

  /** Upright raider with a walk cycle and an aimed weapon. */
  private drawHuman(g: Phaser.GameObjects.Graphics, e: Enemy, cy: number): void {
    const r = e.def.radius;
    const col = e.def.colour;
    const f = e.angle;
    const px = Math.cos(f);
    const py = Math.sin(f);
    const nx = -py;
    const ny = px;
    const walk = Math.sin(e.bob * 3.4);

    // Legs.
    g.lineStyle(2.4, 0x2a2a2a, 1);
    for (const side of [-1, 1]) {
      const hx = e.x + nx * side * r * 0.3;
      const hy = cy + ny * side * r * 0.3 + r * 0.35;
      const swing = walk * side * r * 0.5;
      g.lineBetween(hx, hy, hx + px * swing, hy + py * swing + r * 0.5);
    }

    // Torso + armour.
    g.fillStyle(col, 1);
    g.fillRoundedRect(e.x - r * 0.45, cy - r * 0.55, r * 0.9, r * 1.05, 3);
    g.lineStyle(1.5, 0x2a2a1a, 1);
    g.strokeRoundedRect(e.x - r * 0.45, cy - r * 0.55, r * 0.9, r * 1.05, 3);

    // Head with a glowing visor.
    const hx = e.x + px * r * 0.12;
    const hy = cy - r * 0.78;
    g.fillStyle(0xd8b890, 1);
    g.fillCircle(hx, hy, r * 0.34);
    g.fillStyle(0x66e0ff, 0.85);
    g.fillCircle(hx + px * r * 0.14, hy + py * r * 0.14, r * 0.14);

    // Weapon arm, aimed forward with a hot muzzle.
    const wx = e.x + px * r * 1.0;
    const wy = cy + py * r * 1.0 - r * 0.15;
    g.lineStyle(2.6, 0x1a1a1a, 1);
    g.lineBetween(e.x, cy - r * 0.2, wx, wy);
    const heat = e.fireTimer < 0.35 ? 1 : 0.45;
    g.fillStyle(0x66e0ff, heat);
    g.fillCircle(wx, wy, 2.6);

    if (e.isElite || e.isBoss) {
      g.lineStyle(e.isBoss ? 3 : 2, e.isBoss ? 0xff2020 : 0xff8888, 0.9);
      g.strokeCircle(e.x, cy, r + 4);
    }
  }

  private drawOverlay(): void {
    const g = this.gOverlay;
    g.clear();
    this.gMuzzle.clear();
    const now = this.time.now;

    // Decals under everything
    this.gDecals.clear();
    for (const d of this.decals) {
      const fade = Math.min(1, d.life / 4);
      this.gDecals.fillStyle(0x120c08, d.a * 0.7 * fade);
      this.gDecals.fillCircle(d.x, d.y, d.r);
      if (d.scorch) {
        this.gDecals.fillStyle(0x1a1008, d.a * 0.5 * fade);
        this.gDecals.fillCircle(d.x, d.y, d.r * 0.7);
        this.gDecals.lineStyle(1, 0x2a1810, d.a * 0.4 * fade);
        this.gDecals.strokeCircle(d.x, d.y, d.r * 0.85);
      }
    }

    if (this.baseSelected) {
      const c = this.mapDef.command;
      const pulse = 0.72 + Math.sin(now / 180) * 0.14;
      g.lineStyle(2, this.theme.commandColour, pulse);
      g.strokeCircle(c.x, c.y, 66);
      g.lineStyle(1, 0xd8f6ff, pulse * 0.55);
      g.strokeCircle(c.x, c.y, 72);
    }

    // Caches are hidden until a lucky pad is built. Once discovered, the
    // reward flash and toast provide the only battlefield reveal.
    for (const drop of this.loot) {
      const fade = Math.min(1, drop.life / 2.5);
      const bob = Math.sin(drop.bob) * 2.5;
      const y = drop.y + bob;
      g.fillStyle(0x000000, 0.32 * fade);
      g.fillEllipse(drop.x, drop.y + 7, 20, 7);
      const cacheColour = drop.kind === 'research' ? COLORS.energy : 0xffb83d;
      const cacheEdge = drop.kind === 'research' ? 0xd8a8ff : 0xffd36a;
      g.fillStyle(cacheColour, 0.14 * fade);
      g.fillCircle(drop.x, y, 15);
      g.fillStyle(0x13202a, 0.96 * fade);
      g.fillTriangle(drop.x, y - 10, drop.x + 10, y, drop.x, y + 10);
      g.fillTriangle(drop.x, y - 10, drop.x, y + 10, drop.x - 10, y);
      g.lineStyle(1.5, cacheEdge, 0.95 * fade);
      g.strokeTriangle(drop.x, y - 10, drop.x + 10, y, drop.x, y + 10);
      g.strokeTriangle(drop.x, y - 10, drop.x, y + 10, drop.x - 10, y);
      g.fillStyle(cacheEdge, 0.95 * fade);
      g.fillCircle(drop.x, y, 3.2);
      g.lineStyle(1, 0xffffff, 0.65 * fade);
      g.lineBetween(drop.x - 4, y, drop.x + 4, y);
      g.lineBetween(drop.x, y - 4, drop.x, y + 4);
    }

    // Projectiles with vapour trails
    for (const p of this.projectiles.getActive()) {
      this.drawProjectile(g, p, now);
    }
    this.drawEnemyShots(g, now);

    // Rolling Death guidance glow and dust wake. The sprite itself supplies
    // the mechanical silhouette; these pulses sell the heavy rolling launch.
    for (const bomb of this.rollingBombs) {
      if (!bomb.active) continue;
      const colour = bomb.mode === 'snow' ? 0x8fdcff : 0xff8b32;
      const pulse = 0.45 + Math.sin(now / 90 + bomb.id) * 0.12;
      g.fillStyle(colour, 0.12 * pulse);
      g.fillCircle(bomb.x, bomb.y, 25 + Math.sin(now / 80 + bomb.id) * 3);
      g.lineStyle(1.5, colour, 0.55 * pulse);
      g.strokeCircle(bomb.x, bomb.y, 22 + Math.sin(now / 70 + bomb.id) * 2);
      g.lineStyle(2, colour, 0.22);
      g.lineBetween(bomb.x - Math.cos(bomb.angle) * 30, bomb.y - Math.sin(bomb.angle) * 30, bomb.x, bomb.y);
    }

    // VFX particles
    for (const p of this.vfx.particles) {
      const t = p.life / p.maxLife;
      const a = Math.max(0, t) * p.alpha0;
      if (a <= 0.01) continue;
      if (p.kind === 'smoke' || p.kind === 'vapour') {
        g.fillStyle(p.c, a * 0.55);
        g.fillCircle(p.x, p.y, p.r);
        g.fillStyle(p.c, a * 0.25);
        g.fillCircle(p.x - p.r * 0.2, p.y - p.r * 0.2, p.r * 0.7);
      } else if (p.kind === 'dust') {
        g.fillStyle(p.c, a * 0.38);
        g.fillCircle(p.x, p.y, p.r * 1.6);
        g.fillStyle(p.c, a * 0.22);
        g.fillCircle(p.x, p.y, p.r * 0.8);
      } else if (p.kind === 'ember') {
        g.fillStyle(0xffaa44, a);
        g.fillCircle(p.x, p.y, p.r);
        g.fillStyle(0xffeeaa, a * 0.6);
        g.fillCircle(p.x, p.y, p.r * 0.45);
      } else if (p.kind === 'debris') {
        g.fillStyle(p.c, a);
        g.fillRect(p.x - p.r, p.y - p.r * 0.5, p.r * 2, p.r);
      } else if (p.kind === 'plasma') {
        g.fillStyle(p.c, a * 0.4);
        g.fillCircle(p.x, p.y, p.r * 2);
        g.fillStyle(0xffffff, a * 0.8);
        g.fillCircle(p.x, p.y, p.r * 0.6);
      } else if (p.kind === 'flame') {
        const speed = Math.max(1, Math.hypot(p.vx, p.vy));
        const nx = p.vx / speed;
        const ny = p.vy / speed;
        const px = -ny;
        const py = nx;
        const tail = p.r * 1.8;
        g.fillStyle(p.c, a * 0.9);
        g.fillTriangle(
          p.x + nx * p.r * 1.4, p.y + ny * p.r * 1.4,
          p.x - nx * tail + px * p.r * 0.72, p.y - ny * tail + py * p.r * 0.72,
          p.x - nx * tail - px * p.r * 0.72, p.y - ny * tail - py * p.r * 0.72,
        );
        g.fillStyle(0xfff0a0, a * 0.75);
        g.fillCircle(p.x - nx * p.r * 0.25, p.y - ny * p.r * 0.25, p.r * 0.4);
      } else {
        g.fillStyle(p.c, a);
        g.fillCircle(p.x, p.y, Math.max(0.5, p.r));
      }
    }

    // Shock rings
    for (const r of this.vfx.rings) {
      const a = Math.max(0, r.life / r.maxLife);
      g.lineStyle(r.w, r.c, a * 0.9);
      g.strokeCircle(r.x, r.y, r.r);
      g.lineStyle(r.w * 0.4, 0xffffff, a * 0.35);
      g.strokeCircle(r.x, r.y, r.r * 0.92);
    }

    // Flashes
    for (const f of this.vfx.flashes) {
      const flashG = f.kind === 'muzzle' ? this.gMuzzle : g;
      const t = f.life / f.maxLife;
      const a = Math.max(0, t);
      const scale = 1 + (1 - t) * 0.4;
      if (f.kind === 'muzzle') {
        const r = f.r * scale;
        flashG.fillStyle(f.c, a * 0.9);
        // starburst
        for (let i = 0; i < 5; i++) {
          const ang = f.angle + (i * Math.PI * 2) / 5;
          flashG.fillTriangle(
            f.x, f.y,
            f.x + Math.cos(ang - 0.35) * r,
            f.y + Math.sin(ang - 0.35) * r,
            f.x + Math.cos(ang + 0.35) * r,
            f.y + Math.sin(ang + 0.35) * r,
          );
        }
        flashG.fillStyle(0xffffff, a);
        flashG.fillCircle(f.x, f.y, r * 0.35);
      } else if (f.kind === 'blast') {
        const r = f.r * scale;
        flashG.fillStyle(f.c, a * 0.55);
        flashG.fillCircle(f.x, f.y, r);
        flashG.fillStyle(0xffffff, a * 0.7);
        flashG.fillCircle(f.x, f.y, r * 0.4);
      } else if (f.kind === 'impact') {
        const r = f.r * scale;
        flashG.fillStyle(f.c, a * 0.8);
        flashG.fillCircle(f.x, f.y, r * 0.5);
        flashG.lineStyle(2, 0xffffff, a);
        flashG.strokeCircle(f.x, f.y, r);
      } else {
        flashG.fillStyle(f.c, a * 0.6);
        flashG.fillCircle(f.x, f.y, f.r * scale);
      }
    }

    // Lightning arcs (Tesla / Lightning Node) and focused laser beams
    for (const a of this.vfx.arcs) {
      const t = Math.max(0, a.life / a.maxLife);
      if (!a.jagged) {
        g.lineStyle(a.w * 3, a.c, t * 0.18);
        g.lineBetween(a.x1, a.y1, a.x2, a.y2);
        g.lineStyle(a.w, a.c, t * 0.85);
        g.lineBetween(a.x1, a.y1, a.x2, a.y2);
        g.lineStyle(Math.max(1, a.w * 0.4), 0xffffff, t);
        g.lineBetween(a.x1, a.y1, a.x2, a.y2);
        continue;
      }
      const segs = 5;
      let px = a.x1;
      let py = a.y1;
      for (let i = 1; i <= segs; i++) {
        const f = i / segs;
        const nx = a.x1 + (a.x2 - a.x1) * f + (i < segs ? (Math.random() - 0.5) * 11 : 0);
        const ny = a.y1 + (a.y2 - a.y1) * f + (i < segs ? (Math.random() - 0.5) * 11 : 0);
        g.lineStyle(a.w + 1.5, a.c, t * 0.3);
        g.lineBetween(px, py, nx, ny);
        g.lineStyle(a.w, 0xffffff, t * 0.9);
        g.lineBetween(px, py, nx, ny);
        px = nx;
        py = ny;
      }
    }

    // Continuous laser beams — held on target, not just on the damage tick.
    for (const t of this.towers) {
      const sd = t.secondaryDef;
      if (!sd || sd.kind !== 'laser' || !t.secondaryBeam) continue;
      const b = t.secondaryBeam;
      const flicker = 0.78 + 0.22 * Math.sin(now / 55 + t.id * 1.7);
      g.lineStyle(11, sd.colour, 0.1 * flicker);
      g.lineBetween(t.x, t.y, b.x, b.y);
      g.lineStyle(5, sd.colour, 0.5 * flicker);
      g.lineBetween(t.x, t.y, b.x, b.y);
      g.lineStyle(1.6, 0xffffff, 0.9 * flicker);
      g.lineBetween(t.x, t.y, b.x, b.y);
      g.fillStyle(sd.colour, 0.32 * flicker);
      g.fillCircle(b.x, b.y, 8);
      g.fillStyle(0xffffff, 0.8 * flicker);
      g.fillCircle(b.x, b.y, 3);
    }

    // Markers (airstrike reticle, artillery zone)
    for (const m of this.markers) {
      const life = m.max ?? 1.2;
      const t = Math.max(0, m.t / life);
      const pulse = 1 + Math.sin(now / 45) * 0.12;
      if (m.kind === 'emp') {
        const elapsed = life - m.t;
        const expand = Phaser.Math.Clamp(elapsed / 1.05, 0, 1);
        const linger = Phaser.Math.Clamp((elapsed - 1.05) / 3.75, 0, 1);
        const fade = elapsed < 1.05 ? 0.9 : 0.9 * (1 - linger);
        const waveRadius = 700 * (0.12 + expand * 0.88);
        g.lineStyle(8, 0x9b56e8, fade * 0.18);
        g.strokeCircle(m.x, m.y, waveRadius);
        g.lineStyle(2.5, 0xd9adff, fade * 0.95);
        g.strokeCircle(m.x, m.y, waveRadius);
        g.lineStyle(1, 0xffffff, fade * 0.45);
        g.strokeCircle(m.x, m.y, waveRadius * 0.96);
        const innerRadius = waveRadius * 0.62;
        g.lineStyle(2, 0xb978ff, fade * 0.6);
        g.strokeCircle(m.x, m.y, innerRadius);
        for (let i = 0; i < 8; i++) {
          const ang = (i / 8) * Math.PI * 2 + now / 900;
          const r0 = innerRadius * 0.88;
          const r1 = waveRadius * 1.02;
          g.lineStyle(1.5, 0xe9d5ff, fade * 0.55);
          g.lineBetween(m.x + Math.cos(ang) * r0, m.y + Math.sin(ang) * r0, m.x + Math.cos(ang) * r1, m.y + Math.sin(ang) * r1);
        }
        continue;
      }
      if (m.kind === 'airstrike-fire') {
        const radius = m.radius ?? 74;
        const fade = Math.min(1, t * 1.5);
        g.fillStyle(0xff5b22, 0.08 * fade);
        g.fillCircle(m.x, m.y, radius * (0.94 + Math.sin(now / 120) * 0.03));
        g.lineStyle(3, 0xff7a32, 0.55 * fade);
        g.strokeCircle(m.x, m.y, radius);
        g.lineStyle(1, 0xffd36a, 0.34 * fade);
        g.strokeCircle(m.x, m.y, radius * 0.78);
        for (let i = 0; i < 7; i++) {
          const ang = now / 620 + (i / 7) * Math.PI * 2;
          const rr = radius * (0.35 + ((i * 0.17) % 0.42));
          g.fillStyle(i % 2 ? 0xff9a3c : 0xffdf76, 0.55 * fade);
          g.fillCircle(m.x + Math.cos(ang) * rr, m.y + Math.sin(ang) * rr, 2 + (i % 3));
        }
        continue;
      }
      const isAir = m.kind === 'airstrike';
      const col = isAir ? 0xffaa44 : COLORS.artillery;
      g.lineStyle(2, col, 0.55 + 0.4 * t);
      g.strokeCircle(m.x, m.y, (isAir ? 95 : 70) * pulse);
      g.lineStyle(1, col, 0.35 * t);
      g.strokeCircle(m.x, m.y, (isAir ? 95 : 70) * 0.55 * pulse);
      // crosshair
      const arm = isAir ? 18 : 14;
      g.lineStyle(2, col, 0.85);
      g.lineBetween(m.x - arm, m.y, m.x + arm, m.y);
      g.lineBetween(m.x, m.y - arm, m.x, m.y + arm);
      // countdown ticks
      if (isAir) {
        const ticks = 12;
        for (let i = 0; i < ticks; i++) {
          const ang = (i / ticks) * Math.PI * 2 - now / 200;
          const r0 = 28;
          const r1 = 34 + (1 - t) * 20;
          g.lineStyle(1, col, 0.5 * t);
          g.lineBetween(
            m.x + Math.cos(ang) * r0, m.y + Math.sin(ang) * r0,
            m.x + Math.cos(ang) * r1, m.y + Math.sin(ang) * r1,
          );
        }
      }
    }

    // Place mode previews
    const p = this.input.activePointer;
    const wp = this.screenToWorld(p.x, p.y);
    const over = p.x < BATTLE_W && p.y > UI_TOP_H && p.y < GAME_HEIGHT - UI_BOTTOM_H;
    if (this.placeMode === 'build') {
      const def = TOWERS[this.pendingTowerId];
      const separated = TOWER_ART[this.pendingTowerId];
      const bodyKey = separated?.bodyKey;
      const v4Key = `tower-${this.pendingTowerId}-v4`;
      const v3Key = `top-${this.pendingTowerId}-v3`;
      const ghostKey = bodyKey && this.textures.exists(bodyKey)
        ? bodyKey
        : this.textures.exists(v4Key)
        ? v4Key
        : this.textures.exists(v3Key)
        ? v3Key
        : this.pendingTowerId === 'dronehive' && this.textures.exists('top-dronehive-v2')
          ? 'top-dronehive-v2'
          : this.pendingTowerId === 'vulcan' && this.textures.exists('top-vulcan-v2')
            ? 'top-vulcan-v2'
            : `top-${this.pendingTowerId}`;
      let snap: PadView | undefined;
      let gx = wp.x;
      let gy = wp.y;
      if (this.textures.exists(ghostKey)) {
        if (!this.buildGhost || this.buildGhost.texture.key !== ghostKey) {
          this.buildGhost?.destroy();
          this.buildGhost = this.worldAdd(this.add.image(0, 0, ghostKey).setDepth(15).setAlpha(0.55));
        }
        snap = this.pads.find(
          (pd) =>
            !pd.occupied &&
            (pd.kind === 'field' || this.baseLevel >= 1) &&
            Phaser.Math.Distance.Between(wp.x, wp.y, pd.x, pd.y) < 24,
        );
        gx = snap ? snap.x : wp.x;
        gy = snap ? snap.y : wp.y;
        const s = Math.min(54 / this.buildGhost.width, 54 / this.buildGhost.height);
        this.buildGhost.setPosition(gx, gy).setScale(s).setVisible(over);
        this.buildGhost.setAlpha(snap ? 0.8 : 0.4);
        const cost = scaleSupplyCost(def.stages[0].cost);
        const nearPad = this.pads.find(
          (pd) => Phaser.Math.Distance.Between(wp.x, wp.y, pd.x, pd.y) < 24,
        );
        const invalid = !this.canSpend(cost)
          || Boolean(nearPad && (nearPad.occupied || (nearPad.kind === 'base' && this.baseLevel < 1) || (nearPad.kind === 'field' && !this.isFieldPadSafe(nearPad.x, nearPad.y))))
          || (!snap && over);
        if (invalid) this.buildGhost.setTint(0xff5555);
        else this.buildGhost.clearTint();
        if (over) {
          g.lineStyle(2.2, invalid ? 0xff5555 : COLORS.accent, invalid ? 0.9 : 0.75);
          g.strokeCircle(gx, gy, snap ? 24 : 14);
        }
      }
      if (over) {
        const range = this.pendingTowerRange(snap?.kind === 'base');
        const cost = scaleSupplyCost(def.stages[0].cost);
        const blocked = !this.canSpend(cost)
          || !snap
          || snap.occupied
          || (snap.kind === 'base' && this.baseLevel < 1)
          || (snap.kind === 'field' && !this.isFieldPadSafe(snap.x, snap.y));
        g.lineStyle(1.8, blocked ? 0xff6a5a : def.colour, blocked ? 0.85 : 0.7);
        g.strokeCircle(gx, gy, range);
        g.fillStyle(blocked ? 0xff4a4a : def.colour, blocked ? 0.06 : 0.05);
        g.fillCircle(gx, gy, range);
      }
    } else if (this.buildGhost?.visible) {
      this.buildGhost.setVisible(false);
    }
    if (over && this.placeMode === 'airstrike') {
      g.lineStyle(2, 0xffaa44, 0.85);
      g.strokeCircle(wp.x, wp.y, 95);
      g.lineStyle(1, 0xffaa44, 0.4);
      g.strokeCircle(wp.x, wp.y, 95 * (0.85 + Math.sin(now / 70) * 0.05));
      g.fillStyle(0xffaa44, 0.06);
      g.fillCircle(wp.x, wp.y, 95);
    }
    if (over && this.placeMode === 'barrier') {
      g.lineStyle(2, COLORS.barrier, 0.85);
      g.strokeCircle(wp.x, wp.y, 22);
      let nearest: { dist: number; x: number; y: number; angle: number } | null = null;
      for (const route of this.routeCaches) {
        const hit = distanceToPathSegment(wp.x, wp.y, route);
        if (!nearest || hit.dist < nearest.dist) nearest = { dist: hit.dist, x: hit.point.x, y: hit.point.y, angle: hit.angle };
      }
      if (nearest && nearest.dist <= 36) {
        const angle = nearest.angle + Math.PI / 2;
        const ux = Math.cos(angle) * 24;
        const uy = Math.sin(angle) * 24;
        const vx = -Math.sin(angle) * 7;
        const vy = Math.cos(angle) * 7;
        const corners = [
          { x: nearest.x - ux - vx, y: nearest.y - uy - vy },
          { x: nearest.x + ux - vx, y: nearest.y + uy - vy },
          { x: nearest.x + ux + vx, y: nearest.y + uy + vy },
          { x: nearest.x - ux + vx, y: nearest.y - uy + vy },
        ];
        g.fillStyle(COLORS.barrier, 0.22);
        g.beginPath();
        g.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < corners.length; i++) g.lineTo(corners[i].x, corners[i].y);
        g.closePath();
        g.fillPath();
        g.lineStyle(1.5, COLORS.barrier, 0.9);
        g.strokePath();
      }
    }

    // Ability cooldown overlays
    for (const ab of ABILITIES) {
      const bar = this.abilityBars[ab.id];
      bar.clear();
      const cd = this.abilityCd[ab.id];
      const i = ABILITIES.findIndex((a) => a.id === ab.id);
      const abilityConsoleX = BattleScene.WAVE_CONSOLE_X + BattleScene.WAVE_CONSOLE_W + BattleScene.ABILITY_CONSOLE_GAP;
      const cx = abilityConsoleX + BattleScene.ABILITY_FIRST_INSET + i * BattleScene.ABILITY_STEP;
      const cy = GAME_HEIGHT - UI_BOTTOM_H + 65;
      const txt = this.abilityTexts[ab.id];
      const glyph = this.abilityGlyphs[ab.id];
      const icon = this.abilityIcons[ab.id];
      if (cd > 0) {
        const pct = Math.min(1, cd / ab.cooldown);
        const start = -Math.PI / 2;
        bar.fillStyle(0x5ec8ff, 0.16);
        bar.slice(cx, cy, 22, start, start + Math.PI * 2 * (1 - pct), false);
        bar.fillPath();
        bar.fillStyle(0x020609, 0.9);
        bar.fillRect(cx - 22, cy + 23, 44, 4);
        bar.fillStyle(0x4d91ad, 0.95);
        bar.fillRect(cx - 22, cy + 23, 44 * (1 - pct), 4);
        txt.setText(`${ab.key}\n${Math.ceil(cd)}s`).setColor('#d5eaf2');
        glyph?.setColor('#7a9bb0');
        icon?.setAlpha(0.45);
      } else {
        bar.fillStyle(0x5ec8ff, 0.18);
        bar.fillRect(cx - 22, cy + 23, 44, 4);
        bar.fillStyle(0x7ee8ff, 0.95);
        bar.fillRect(cx - 22, cy + 23, 44, 4);
        const readyFlash = this.abilityReadyFlash[ab.id] ?? 0;
        if (readyFlash > 0) {
          bar.lineStyle(2, 0xd8faff, readyFlash * 1.4);
          bar.strokeCircle(cx, cy, 25 + (1 - readyFlash) * 5);
        }
        txt.setText(`${ab.key}\nREADY`).setColor('#d8faff');
        glyph?.setColor('#5ec8ff');
        icon?.setAlpha(1);
      }
    }
  }

  /** Subtle vector telemetry: animated rails and scan-lines that stay behind every HUD label. */
  private drawHudMotion(time: number): void {
    const g = this.gHudMotion;
    g.clear();
    this.drawTopBars(g);
    this.drawWavePrompt(time);
    // Recovered caches arc toward the fixed supply meter, so the pickup reads
    // correctly even while the player has the battlefield zoomed or panned.
    for (const flight of this.lootFlights) {
      const t = Phaser.Math.Clamp(flight.life / flight.maxLife, 0, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const x = Phaser.Math.Linear(flight.sx, 78, eased);
      const y = Phaser.Math.Linear(flight.sy, 29, eased) - Math.sin(t * Math.PI) * 18;
      const fade = t > 0.78 ? (1 - t) / 0.22 : 1;
      const size = 4 + Math.min(3, flight.amount / 20);
      const flightColour = flight.kind === 'research' ? COLORS.energy : 0xffb83d;
      const flightEdge = flight.kind === 'research' ? 0xd8a8ff : 0xffd36a;
      g.fillStyle(flightColour, 0.16 * fade);
      g.fillCircle(x, y, size * 2.4);
      g.fillStyle(flightEdge, 0.95 * fade);
      g.fillTriangle(x, y - size, x + size, y, x, y + size);
      g.fillTriangle(x, y - size, x, y + size, x - size, y);
      g.fillStyle(0xffffff, 0.9 * fade);
      g.fillCircle(x, y, 1.5);
    }
    if (this.reducedMotion) return;

    const fieldBottom = MAP_FIELD_BOTTOM;
    const phase = (time * 0.06) % 1;

    // A travelling signal along the command rail above the build controls.
    const railStart = 12;
    const railEnd = 542;
    const railY = fieldBottom + 8;
    const signalX = Phaser.Math.Linear(railStart, railEnd, phase);
    g.lineStyle(1, 0x2a5a70, 0.34);
    g.lineBetween(railStart, railY, railEnd, railY);
    g.fillStyle(COLORS.accent, 0.72);
    g.fillCircle(signalX, railY, 1.8);
    g.fillStyle(COLORS.accent, 0.18);
    g.fillCircle(signalX, railY, 5);

    // A low-opacity diagnostic sweep in the side console.
    const scanY = UI_TOP_H + 12 + ((time * 0.035) % (fieldBottom - UI_TOP_H - 24));
    g.lineStyle(1, COLORS.accent, 0.1);
    g.lineBetween(BATTLE_W + 8, scanY, GAME_WIDTH - 8, scanY);

    // Ready-wave control gets a small amber vector halo rather than a flashing text treatment.
    if (!this.waveActive && !this.allWavesDone && !this.gameOver) {
      const a = 0.22 + Math.sin(time / 260) * 0.1;
      g.lineStyle(1.5, COLORS.warn, a);
      g.lineBetween(BATTLE_W + 24, 568, GAME_WIDTH - 24, 568);
    }
  }

  /** Animated "call the next wave" prompt drawn over the battlefield. */
  private drawWavePrompt(time: number): void {
    if (!this.wavePrompt.visible) return;
    const g = this.gWavePrompt;
    g.clear();
    const pulse = this.reducedMotion ? 0.5 : 0.5 + 0.5 * Math.sin(time / 320);

    g.fillStyle(0x08131a, 0.74);
    g.fillRoundedRect(-140, -36, 280, 84, 12);
    g.lineStyle(2, 0xd4a24a, 0.55 + pulse * 0.45);
    g.strokeRoundedRect(-140, -36, 280, 84, 12);

    if (!this.reducedMotion) {
      const grow = pulse * 14;
      g.lineStyle(1.5, 0xffd27a, (1 - pulse) * 0.5);
      g.strokeRoundedRect(-140 - grow, -36 - grow * 0.6, 280 + grow * 2, 84 + grow * 1.2, 16);
    }

    // Chevrons pointing inward toward the prompt.
    for (const s of [-1, 1]) {
      const bx = s * 158;
      const off = this.reducedMotion ? 0 : (1 - pulse) * 9;
      g.fillStyle(0xffd27a, 0.75);
      for (let i = 0; i < 2; i++) {
        const x = bx + s * (i * 10 + off);
        g.fillTriangle(x, -8, x, 8, x - s * 8, 0);
      }
    }

    this.wavePrompt.setScale(1 + (this.reducedMotion ? 0 : pulse * 0.025));
  }

  /** Live top-bar meters: wave progress, hostile pressure, and command integrity. */
  private drawTopBars(g: Phaser.GameObjects.Graphics): void {
    // Wave progress
    g.fillStyle(0x101820, 1);
    g.fillRect(212, 45, 122, 2);
    if (this.survival) {
      // Survival has no finite completion point. Use a scanner instead of a
      // misleading campaign-style 20/30 segment meter.
      g.fillStyle(0xf89e2b, 0.28);
      g.fillRect(212, 45, 122, 2);
      const scanX = 212 + ((this.time.now / 900) % 1) * 122;
      g.fillStyle(0xffd27a, 0.95);
      g.fillRect(scanX, 44, 3, 4);
    } else {
      const total = Math.max(1, this.waveList.length);
      const wpct = Math.min(1, this.waveIndex / total);
      g.fillStyle(0xf89e2b, 0.9);
      g.fillRect(212, 45, 122 * wpct, 2);
      g.lineStyle(1, 0x0a1014, 0.9);
      for (let i = 1; i < total; i++) g.lineBetween(212 + (122 / total) * i, 45, 212 + (122 / total) * i, 47);
    }

    // Hostile pressure
    const hp = Math.min(1, (this.spawnQueue.length + this.enemies.length) / 30);
    g.fillStyle(0x101820, 1);
    g.fillRect(370, 42, 122, 2);
    g.fillStyle(hp > 0.66 ? 0xff4a4a : hp > 0.33 ? 0xff7a3c : 0x7dce5a, 0.95);
    g.fillRect(370, 42, 122 * hp, 2);

    // Twenty compact LED cells make integrity read like a premium hardware
    // meter rather than the old decorative triangle row.
    const integrityPct = Phaser.Math.Clamp(this.integrity / Math.max(1, this.maxIntegrity), 0, 1);
    const pips = 20;
    for (let i = 0; i < pips; i++) {
      const filled = i < Math.ceil(integrityPct * pips);
      const x = 532 + i * 13;
      g.fillStyle(filled ? 0x38d6f8 : 0x203240, filled ? 0.96 : 0.7);
      g.fillRoundedRect(x, 28, 9, 8, 2);
      if (filled) {
        g.fillStyle(0xd8faff, 0.78);
        g.fillRect(x + 1, 29, 7, 1);
      }
    }
  }

  private drawEnemyShots(g: Phaser.GameObjects.Graphics, now: number): void {
    for (const shot of this.enemyShots) {
      const progress = Phaser.Math.Clamp(shot.age / shot.life, 0, 1);
      const dx = shot.tx - shot.sx;
      const dy = shot.ty - shot.sy;
      const distance = Math.max(1, Math.hypot(dx, dy));
      const nx = dx / distance;
      const ny = dy / distance;
      const pulse = 0.72 + Math.sin(now / 55 + shot.id) * 0.18;

      // Bright tracer with a compact tail keeps the attack legible without
      // covering the playfield in permanent beams.
      g.lineStyle(5, shot.colour, 0.12 * pulse);
      g.lineBetween(shot.x - nx * 12, shot.y - ny * 12, shot.x, shot.y);
      g.lineStyle(2.2, shot.colour, 0.9 * pulse);
      g.lineBetween(shot.x - nx * 9, shot.y - ny * 9, shot.x, shot.y);
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(shot.x, shot.y, 2.2);
      g.lineStyle(1, shot.colour, 0.7 * pulse);
      g.strokeCircle(shot.x, shot.y, 4 + (1 - progress) * 2);
    }
  }

  private drawProjectile(g: Phaser.GameObjects.Graphics, p: import('../entities/Projectile').Projectile, now: number): void {
    const trail = p.history.points;

    if (p.kind === 'shell') {
      // Thick vapour contrail
      for (let i = 0; i < trail.length; i++) {
        const pt = trail[i];
        const t = i / Math.max(1, trail.length - 1);
        const a = pt.t * 0.55 * t;
        g.fillStyle(p.colour, a * 0.25);
        g.fillCircle(pt.x, pt.y, 3 + t * 6);
        g.fillStyle(0xc8c0b0, a * 0.35);
        g.fillCircle(pt.x, pt.y, 1.5 + t * 3);
      }
      // exhaust puffs
      if (trail.length) {
        const last = trail[Math.floor(trail.length / 2)];
        if (last && Math.random() < 0.5) {
          this.vfx.vapour(last.x, last.y, 0xd0c8b8, 1.4);
        }
      }
      g.fillStyle(0x2a2218, 1);
      g.fillCircle(p.x, p.y, 5);
      g.fillStyle(p.colour, 1);
      g.fillCircle(p.x, p.y, 3.5);
      g.fillStyle(0xffeeaa, 0.9);
      g.fillCircle(p.x - p.vx * 0.008, p.y - p.vy * 0.008, 2);
      return;
    }

    if (p.kind === 'missile') {
      // Thin smoke trail
      for (let i = 0; i < trail.length; i += 2) {
        const pt = trail[i];
        const t = i / Math.max(1, trail.length - 1);
        g.fillStyle(0xd8d0c0, pt.t * 0.22 * t);
        g.fillCircle(pt.x, pt.y, 2 + t * 5);
      }
      const ca = Math.cos(p.angle);
      const sa = Math.sin(p.angle);
      // Exhaust plume
      g.fillStyle(0xff6622, 0.5);
      g.fillCircle(p.x - ca * 11, p.y - sa * 11, 5);
      g.fillStyle(0xffcc66, 0.9);
      g.fillCircle(p.x - ca * 8, p.y - sa * 8, 3 + Math.sin(now / 40));
      // Rotated body
      g.save();
      g.translateCanvas(p.x, p.y);
      g.rotateCanvas(p.angle);
      g.fillStyle(0x3a3a3a, 1);
      g.fillEllipse(0, 0, 15, 6);
      g.fillStyle(p.colour, 1);
      g.fillEllipse(0, 0, 10, 4);
      g.fillStyle(0xffffff, 0.8);
      g.fillCircle(3, 0, 1.4);
      g.restore();
      return;
    }

    if (p.kind === 'plasma') {
      for (let i = 0; i < trail.length; i += 2) {
        const pt = trail[i];
        const t = i / Math.max(1, trail.length - 1);
        g.fillStyle(p.colour, pt.t * 0.35 * t);
        g.fillCircle(pt.x, pt.y, 2 + t * 4);
      }
      g.fillStyle(p.colour, 0.35);
      g.fillCircle(p.x, p.y, 9 + Math.sin(now / 40) * 2);
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(p.x, p.y, 3.5);
      return;
    }

    if (p.kind === 'cluster') {
      g.fillStyle(0x2a2a2e, 1);
      g.fillCircle(p.x, p.y, 6);
      g.lineStyle(1.5, p.colour, 0.9);
      g.strokeCircle(p.x, p.y, 6);
      const blink = Math.sin(now / 90) > 0 ? 1 : 0.3;
      g.fillStyle(0xff5533, blink);
      g.fillCircle(p.x, p.y, 2);
      return;
    }

    if (p.kind === 'frost') {
      for (let i = 0; i < trail.length; i += 2) {
        const pt = trail[i];
        const t = i / Math.max(1, trail.length - 1);
        g.fillStyle(0xbfe9ff, pt.t * 0.28 * t);
        g.fillCircle(pt.x, pt.y, 1.5 + t * 3);
      }
      g.save();
      g.translateCanvas(p.x, p.y);
      g.rotateCanvas(Math.atan2(p.vy, p.vx));
      g.fillStyle(0x0e2230, 1);
      g.fillTriangle(-7, 0, 7, -5, 7, 5);
      g.fillStyle(p.colour, 1);
      g.fillTriangle(-4, 0, 5, -3, 5, 3);
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(2, 0, 1.5);
      g.restore();
      return;
    }

    if (p.kind === 'beam') {
      // Glowing energy lance
      const x0 = p.x - p.vx * 0.05;
      const y0 = p.y - p.vy * 0.05;
      g.lineStyle(8, p.colour, 0.25);
      g.lineBetween(x0, y0, p.x, p.y);
      g.lineStyle(4, p.colour, 0.7);
      g.lineBetween(x0, y0, p.x, p.y);
      g.lineStyle(1.5, 0xffffff, 0.95);
      g.lineBetween(x0, y0, p.x, p.y);
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(p.x, p.y, 3);
      g.fillStyle(p.colour, 0.5);
      g.fillCircle(p.x, p.y, 7 + Math.sin(now / 30) * 1.5);
      for (let i = 0; i < trail.length; i += 2) {
        const pt = trail[i];
        g.fillStyle(p.colour, pt.t * 0.3);
        g.fillCircle(pt.x, pt.y, 2 + i * 0.15);
      }
      return;
    }

    if (p.kind === 'flame') {
      // Animated flame tongue
      for (let i = 0; i < trail.length; i++) {
        const pt = trail[i];
        const t = i / Math.max(1, trail.length - 1);
        const a = pt.t * 0.7;
        const r = 4 + t * 10 + Math.sin(now / 40 + i) * 1.5;
        g.fillStyle(i % 2 === 0 ? 0xff6622 : 0xffaa33, a * 0.45);
        g.fillCircle(pt.x, pt.y, r);
      }
      g.fillStyle(0xffee88, 0.85);
      g.fillCircle(p.x, p.y, 5 + Math.sin(now / 25) * 2);
      g.fillStyle(0xff4400, 0.5);
      g.fillCircle(p.x, p.y, 10);
      if (Math.random() < 0.35) this.vfx.spawn({
        x: p.x, y: p.y,
        vx: (Math.random() - 0.5) * 30,
        vy: (Math.random() - 0.5) * 30 - 10,
        kind: 'ember', c: 0xff6622, maxLife: 0.25, r: 1.5, gravity: 40,
      });
      return;
    }

    // bullet — tracer
    for (let i = 0; i < trail.length; i++) {
      const pt = trail[i];
      const t = i / Math.max(1, trail.length - 1);
      g.fillStyle(p.colour, pt.t * 0.4 * t);
      g.fillCircle(pt.x, pt.y, 1 + t * 1.5);
    }
    g.lineStyle(2, p.colour, 0.85);
    g.lineBetween(p.x - p.vx * 0.02, p.y - p.vy * 0.02, p.x, p.y);
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(p.x, p.y, 1.8);
  }

  private refreshTowerList(): void {
    const lines: string[] = [];
    if (!this.towers.length) lines.push('  — none —');
    for (const t of this.towers.slice(0, 6)) {
      lines.push(
        `${t.def.short} L${t.stageIndex + 1} ${formatVeterancy(t.kills)} K:${t.kills} ${t.priority}${t.canSpecial() ? ' *' : ''}`,
      );
    }
    if (this.towers.length > 6) lines.push(`  +${this.towers.length - 6} more`);
    this.txtTowerList.setText(lines.join('\n'));
  }

  private refreshHud(): void {
    const remaining =
      this.spawnQueue.length + this.enemies.filter((e) => e.alive).length;
    this.txtSupply.setText(this.supplyLabel());
    this.txtWave.setText(this.survival ? `SURVIVAL · WAVE ${this.waveIndex}` : this.waveIndex >= this.waveList.length ? 'WAVE CLEAR' : `WAVE ${this.waveIndex}/${this.waveList.length}`);
    this.txtEnemies.setText(`HOSTILES  ${remaining}`);
    this.txtIntegrity.setText(`BASE INTEGRITY  ${this.integrity}/${this.maxIntegrity}`);
    this.txtKillStreak.setText(this.killStreak > 0 ? `STREAK ${this.killStreak}  NEXT +${BattleScene.KILL_STREAK_STEP - (this.killStreak % BattleScene.KILL_STREAK_STEP || BattleScene.KILL_STREAK_STEP)}` : 'STREAK —  NO LEAKS');
    this.txtScore.setText(`SCORE  ${Math.floor(this.score)}`);
    this.refreshMusicLabel();

    this.refreshTowerList();

    // Selected panel
    if (this.selectedTower) {
      const t = this.selectedTower;
      const s = t.stage;
      const up = t.upgradeCost;
      this.txtPanel.setText(
        [
          `— ${t.def.name} —`,
          `${s.name}`,
          `DMG ${Math.round(t.damage * WEAPON_DAMAGE_MULTIPLIER)}  ·  GLOBAL ×${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)}  RNG ${Math.round(t.range)}`,
          `ROF ${s.fireRate.toFixed(1)}/s  ${s.damageType}`,
          `KILLS ${t.kills}  DMG ${Math.round(t.damageDealt)}  VET ${formatVeterancy(t.kills)}  PRI ${t.priority}`,
          t.hasSecondary ? `SYM ${t.secondaryDef?.short}` : '',
          '',
          up != null ? `U  UPGRADE  $${up}` : 'U  MAX STAGE',
          `T  PRIORITY  X  SELL $${t.sellValue}`,
          t.isMax && s.specialName
            ? `S  ${s.specialName}${t.specialCooldown > 0 ? ` (${t.specialCooldown.toFixed(0)}s)` : ' [READY]'}`
            : '',
        ]
          .filter(Boolean)
          .join('\n'),
      );
    } else if (this.baseSelected) {
      this.txtPanel.setText(
        [
          '— COMMAND BASTION —',
          this.baseLevel >= 1 ? 'BASE MK II  ·  UPGRADED' : 'BASE MK I  ·  STANDARD',
          `INTEGRITY ${this.integrity}/${this.maxIntegrity}`,
          this.baseLevel >= 1 ? `ROOF HARDPOINTS  ${this.getBaseHardpointCount()}/${this.getBaseHardpointCount()}  ONLINE` : `ROOF HARDPOINTS  0/${this.getBaseHardpointCount()}  LOCKED`,
          '',
          this.baseLevel >= 1 ? 'Two roof slots available for mounted towers.' : `RIGHT-CLICK BASE  ·  UPGRADE $${this.getBaseUpgradeCost()}`,
          'Click a tower card, then click a roof pad.',
        ].join('\n'),
      );
    } else if (this.selectedEnemy) {
      const e = this.selectedEnemy;
      const res = e.def.resist;
      this.txtPanel.setText(
        [
          `— ${e.def.name} —`,
          e.isBoss ? 'BOSS' : e.isElite ? 'ELITE' : 'HOSTILE',
          `HP ${Math.ceil(e.hp)}/${e.maxHp}${e.maxShield ? `  SH ${Math.ceil(e.shield)}` : ''}`,
          `ARM ${e.armour}  SPD ${Math.round(e.speed)}`,
          `BOUNTY ${e.def.bounty}`,
          `RESIST ${JSON.stringify(res)}`,
          e.def.regen ? `REGEN ${e.def.regen}/s` : '',
          e.def.desc,
        ]
          .filter(Boolean)
          .join('\n'),
      );
    } else {
      this.txtPanel.setText(
        [
          'COMMAND BASTION',
          `INTEGRITY ${this.integrity}/${this.maxIntegrity}`,
          this.baseLevel >= 1 ? `ROOF HARDPOINTS  ${this.getBaseHardpointCount()}/${this.getBaseHardpointCount()}  ONLINE` : `ROOF HARDPOINTS  0/${this.getBaseHardpointCount()}  LOCKED`,
          '',
          this.baseLevel >= 1
            ? 'Select a tower below, then click a roof pad.'
            : 'Right-click the base to unlock both roof pads.',
          'Field pads remain around the route.',
          'SPACE / CALL WAVE for bonus supply.',
        ].join('\n'),
      );
    }

    this.txtDrone.setText(
      [
        `ROLLING DEATH  ${this.rollingBombMode.toUpperCase()}`,
        `E launch · ${Math.round(this.research.rollingBombCount)} balls`,
        this.research.rollingBombSnowUnlocked ? 'T switch EXPLOSIVE / SNOW' : 'Research CRYO CONVERSION for SNOW mode',
      ].join('\n'),
    );

    // The command-deck button is the sole deployment affordance. Keep the
    // legacy in-field prompt hidden so it cannot overlap the battlefield.
    this.wavePrompt.setVisible(false);
    this.refreshWavePreview();
    this.refreshWaveControl();
    this.updatePanelButtons();
  }

  /** Compact icon row for the next wave's unique enemy types. */
  private refreshWavePreview(): void {
    const preview = this.txtWavePreview;
    if (!preview) return;
    const nextNumber = this.waveIndex + 1;
    const previewState = `${this.survival ? 'survival' : 'campaign'}:${nextNumber}:${this.nightmareMutationSignature()}`;
    if (previewState === this.wavePreviewState) return;
    this.wavePreviewState = previewState;
    const next = this.survival ? undefined : this.waveList[this.waveIndex];
    const groups = this.survival ? this.buildSurvivalWave(nextNumber) : this.nightmareMutatedGroups(next?.groups ?? [], nextNumber);
    if (!groups.length) {
      this.wavePreviewItemCount = 0;
      preview.setText('NO FURTHER WAVES').setColor('#6a7a88');
      for (const icon of this.wavePreviewIcons) icon.setVisible(false);
      for (const count of this.wavePreviewCounts) count.setVisible(false);
      for (const button of this.wavePreviewButtons) button.setVisible(false);
      return;
    }

    const counts = new Map<string, { count: number; boss: boolean; elite: boolean }>();
    for (const group of groups) {
      const def = ENEMIES[group.enemy];
      if (!def) continue;
      const current = counts.get(group.enemy) ?? { count: 0, boss: false, elite: false };
      current.count += group.count;
      current.boss ||= !!def.boss;
      current.elite ||= !!def.elite;
      counts.set(group.enemy, current);
    }
    const total = [...counts.values()].reduce((sum, group) => sum + group.count, 0);
    preview.setText(`${this.survival ? 'SURVIVAL' : 'NEXT'} WAVE ${nextNumber}  ·  ${total} HOSTILES`).setColor('#9ad4e8');

    const entries = [...counts.entries()];
    this.wavePreviewItemCount = Math.min(entries.length, this.wavePreviewIcons.length);
    const startX = BattleScene.WAVE_CONSOLE_X + 24;
    for (let i = 0; i < this.wavePreviewIcons.length; i++) {
      const icon = this.wavePreviewIcons[i];
      const count = this.wavePreviewCounts[i];
      const button = this.wavePreviewButtons[i];
      const entry = entries[i];
      if (!entry) {
        button.setVisible(false);
        icon.setVisible(false);
        count.setVisible(false);
        continue;
      }
      const [enemy, group] = entry;
      const def = ENEMIES[enemy];
      const familyFallback: Record<string, string> = {
        insect: 'enemy-locust-v2',
        robot: 'enemy-reaperbot-v2',
        human: 'enemy-gunner-v2',
        mesh: 'enemy-goliath-v2',
      };
      const keys = [
        `enemy-${enemy}-v2`,
        `enemy-${enemy}-v3`,
        familyFallback[def?.family ?? 'mesh'],
      ];
      const textureKey = keys.find((key) => this.textures.exists(key));
      button.setPosition(startX + i * 25, GAME_HEIGHT - UI_BOTTOM_H + 38).setVisible(this.wavePreviewExpanded);
      icon.setPosition(startX + i * 25, GAME_HEIGHT - UI_BOTTOM_H + 34).setVisible(this.wavePreviewExpanded);
      count.setPosition(startX + i * 25, GAME_HEIGHT - UI_BOTTOM_H + 44).setVisible(this.wavePreviewExpanded);
      button.setStrokeStyle(1, group.boss ? 0xff5a5a : group.elite ? 0xd4a24a : 0x31566a, 1);
      if (textureKey) {
        icon.setTexture(textureKey).setDisplaySize(15, 15).setVisible(true);
      } else {
        icon.setVisible(false);
      }
      count.setText(`×${group.count}${group.boss ? ' B' : group.elite ? ' E' : ''}`).setVisible(true);
      if (!this.wavePreviewExpanded) {
        button.setVisible(false);
        icon.setVisible(false);
        count.setVisible(false);
      }
    }
  }

  /** Collapse the compact threat row, or slide it back into the console header. */
  private setWavePreviewExpanded(expanded: boolean): void {
    if (!this.wavePreviewToggle || expanded === this.wavePreviewExpanded) return;
    this.wavePreviewExpanded = expanded;
    this.wavePreviewToggle.setText(expanded ? '⌃' : '⌄');

    const by = GAME_HEIGHT - UI_BOTTOM_H;
    const startX = BattleScene.WAVE_CONSOLE_X + 24;
    const buttonY = by + 38;
    const iconY = by + 34;
    const countY = by + 44;
    const hiddenButtonY = by + 54;
    const hiddenIconY = by + 50;
    const hiddenCountY = by + 60;
    for (let i = 0; i < this.wavePreviewItemCount; i++) {
      const button = this.wavePreviewButtons[i];
      const icon = this.wavePreviewIcons[i];
      const count = this.wavePreviewCounts[i];
      const x = startX + i * 25;
      if (expanded) {
        button.setPosition(x, hiddenButtonY).setAlpha(0).setVisible(true);
        icon.setPosition(x, hiddenIconY).setAlpha(0).setVisible(true);
        count.setPosition(x, hiddenCountY).setAlpha(0).setVisible(true);
        this.tweens.add({ targets: button, y: buttonY, alpha: 1, duration: 180, ease: 'Cubic.easeOut' });
        this.tweens.add({ targets: icon, y: iconY, alpha: 1, duration: 180, ease: 'Cubic.easeOut' });
        this.tweens.add({ targets: count, y: countY, alpha: 1, duration: 180, ease: 'Cubic.easeOut' });
      } else {
        button.setVisible(true);
        icon.setVisible(true);
        count.setVisible(true);
        this.tweens.add({ targets: button, y: hiddenButtonY, alpha: 0, duration: 150, ease: 'Cubic.easeIn', onComplete: () => button.setVisible(false) });
        this.tweens.add({ targets: icon, y: hiddenIconY, alpha: 0, duration: 150, ease: 'Cubic.easeIn', onComplete: () => icon.setVisible(false) });
        this.tweens.add({ targets: count, y: hiddenCountY, alpha: 0, duration: 150, ease: 'Cubic.easeIn', onComplete: () => count.setVisible(false) });
      }
    }
  }

  /** One fixed-size surface: changing countdown/reward text cannot move the hit target. */
  private refreshWaveControl(): void {
    if (!this.waveControl) return;
    const visible = !this.gameOver && !this.allWavesDone;
    const ready = visible && !this.waveActive;
    const canRush = visible
      && this.waveActive
      && this.rushesUsed < BattleScene.MAX_WAVE_RUSHES
      && (this.survival || this.waveIndex < this.waveList.length);
    const rushLocked = visible && this.waveActive && !canRush;
    const wait = (this.autoTimer?.getRemaining() ?? 0) / 1000;
    const bonus = ready ? Math.floor(this.earlyCallBonus() * EARNED_INCOME_MULTIPLIER * this.nightmareIncomeMultiplier() * CREDIT_REWARD_MULTIPLIER * this.devCreditMul) : 0;
    const rushBonus = canRush ? Math.floor(this.earlyRushBonus() * EARNED_INCOME_MULTIPLIER * this.nightmareIncomeMultiplier() * CREDIT_REWARD_MULTIPLIER * this.devCreditMul) : 0;
    const left = this.spawnQueue.length + this.enemies.filter((e) => e.alive).length;
    const title = ready
      ? (this.autoAdvance && wait > 0.1 ? `${this.survival ? 'SURVIVAL · ' : ''}NEXT WAVE  ${wait.toFixed(1)}s` : `${this.survival ? 'SURVIVAL · ' : ''}START WAVE`)
      : canRush ? `RUSH · ${2 ** (this.rushesUsed + 1)} ABREAST` : rushLocked ? 'HOLD · CLEAR HOSTILES' : `${this.survival ? 'SURVIVAL · ' : ''}WAVE ${this.waveIndex} ACTIVE`;
    const hint = ready
      ? (bonus > 0 ? `SPACE  ·  +${bonus} SUPPLY` : 'SPACE  ·  DEPLOY')
      : canRush ? `SPACE  ·  +${rushBonus} BONUS` : rushLocked ? `${left} HOSTILES LEFT` : `${left} HOSTILES LEFT`;
    const actionable = ready || canRush;
    const hover = actionable && this.waveControlHover;
    const pressed = hover && this.waveControlPressed;
    const state = `${visible}/${ready}/${canRush}/${hover}/${pressed}/${title}/${hint}`;
    if (state === this.waveControlState) return;
    this.waveControlState = state;
    this.waveControl.setVisible(visible);
    this.earlyCallBtn.setVisible(visible).setText(title).setColor(actionable ? '#d9f8ff' : '#e8b772');
    this.waveControlHint.setVisible(visible).setText(hint).setColor(actionable ? '#81bac9' : '#a19683');
    this.waveControlHit.setVisible(visible);
    if (this.waveControlHit.input) this.waveControlHit.input.enabled = actionable;
    const g = this.waveControl;
    const x = BattleScene.WAVE_CONSOLE_X + 6;
    const y = GAME_HEIGHT - UI_BOTTOM_H + 64;
    const w = BattleScene.WAVE_CONSOLE_W - 12;
    const h = 78;
    const edge = actionable ? 0x50d4f2 : 0x796342;
    const outline = [x + 10, y, x + w, y, x + w, y + h - 10, x + w - 10, y + h, x, y + h, x, y + 10];
    g.clear().fillStyle(pressed ? 0x1c495b : hover ? 0x163c4d : 0x0e2532, 0.98);
    g.beginPath();
    g.moveTo(outline[0], outline[1]);
    for (let i = 2; i < outline.length; i += 2) g.lineTo(outline[i], outline[i + 1]);
    g.closePath().fillPath().lineStyle(hover ? 2 : 1.5, edge, actionable ? 0.9 : 0.5).strokePath();
    g.lineStyle(1, edge, 0.16).lineBetween(x + 18, y + 45, x + w - 18, y + 45);
    g.lineStyle(2, edge, actionable ? 0.95 : 0.35).lineBetween(x + 18, y + 1, x + 58, y + 1);
    g.fillStyle(edge, actionable ? 0.85 : 0.35).fillCircle(x + 15, y + 26, 2);
  }

  private refreshHudSoft(dt: number): void {
    if (this.gameOver) return;
    this.refreshWavePreview();
    this.refreshWaveControl();
    // Update more frequently without full rebuild of lists
    this.towerMenu?.updateLive();
    const remaining = this.spawnQueue.length + this.enemies.filter((e) => e.alive).length;
    this.txtSupply.setText(this.supplyLabel());
    this.txtWave.setText(this.survival ? `SURVIVAL · WAVE ${this.waveIndex}` : this.waveIndex >= this.waveList.length ? 'WAVE CLEAR' : `WAVE ${this.waveIndex}/${this.waveList.length}`);
    this.txtEnemies.setText(`HOSTILES  ${remaining}`);
    this.txtIntegrity.setText(`BASE INTEGRITY  ${this.integrity}/${this.maxIntegrity}`);
    this.txtKillStreak.setText(this.killStreak > 0 ? `STREAK ${this.killStreak}  NEXT +${BattleScene.KILL_STREAK_STEP - (this.killStreak % BattleScene.KILL_STREAK_STEP || BattleScene.KILL_STREAK_STEP)}` : 'STREAK —  NO LEAKS');
    this.txtScore.setText(`SCORE  ${Math.floor(this.score)}`);
    this.refreshMusicLabel();
    this.refreshTowerList();
    this.updatePanelButtons();
    for (const b of this.buildBtnInfo) {
      const ok = this.isDevMode() || this.supply >= b.cost;
      b.label.setColor(ok ? '#c8d4e0' : '#55636e');
      b.costTxt.setColor(ok ? '#d4a24a' : '#6a5a38');
    }

    if (this.selectedTower) {
      const t = this.selectedTower;
      const s = t.stage;
      const up = t.upgradeCost;
      this.txtPanel.setText(
        [
          `— ${t.def.name} —`,
          `${s.name}`,
          `DMG ${Math.round(t.damage * WEAPON_DAMAGE_MULTIPLIER)}  ·  GLOBAL ×${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)}  RNG ${Math.round(t.range)}`,
          `ROF ${s.fireRate.toFixed(1)}/s  TYPE ${s.damageType}`,
          `KILLS ${t.kills}  DMG ${Math.round(t.damageDealt)}  VET ${formatVeterancy(t.kills)}  PRIORITY ${t.priority}`,
          t.hasSecondary ? `SYM ${t.secondaryDef?.short}` : '',
          '',
          up != null ? `U  UPGRADE  $${up}` : 'U  MAX STAGE',
          `T  PRIORITY  X  SELL $${t.sellValue}`,
          t.isMax && s.specialName
            ? `S  ${s.specialName}${t.specialCooldown > 0 ? ` (${t.specialCooldown.toFixed(0)}s)` : ' [READY]'}`
            : '',
        ]
          .filter(Boolean)
          .join('\n'),
      );
    } else if (this.baseSelected) {
      this.txtPanel.setText(
        [
          '— COMMAND BASTION —',
          this.baseLevel >= 1 ? 'BASE MK II  ·  UPGRADED' : 'BASE MK I  ·  STANDARD',
          `INTEGRITY ${this.integrity}/${this.maxIntegrity}`,
          this.baseLevel >= 1 ? `ROOF HARDPOINTS  ${this.getBaseHardpointCount()}/${this.getBaseHardpointCount()}  ONLINE` : `ROOF HARDPOINTS  0/${this.getBaseHardpointCount()}  LOCKED`,
          '',
          this.baseLevel >= 1 ? 'Two roof slots available for mounted towers.' : `RIGHT-CLICK BASE  ·  UPGRADE $${this.getBaseUpgradeCost()}`,
          'Click a tower card, then click a roof pad.',
        ].join('\n'),
      );
    } else if (this.selectedEnemy && this.selectedEnemy.alive) {
      const e = this.selectedEnemy;
      this.txtPanel.setText(
        [
          `— ${e.def.name} —`,
          e.isBoss ? 'BOSS' : e.isElite ? 'ELITE' : 'HOSTILE',
          `HP ${Math.ceil(e.hp)}/${e.maxHp}${e.maxShield ? `  SH ${Math.ceil(e.shield)}` : ''}`,
          `ARM ${e.armour}  SPD ${Math.round(e.speed)}`,
          e.def.desc,
        ].join('\n'),
      );
    }

    // Redraw pads hover
    this.redrawPads();
  }

  private supplyLabel(): string {
    return this.isDevMode() ? 'SUPPLY  ∞  · DEV' : `SUPPLY  ${Math.floor(this.supply)}`;
  }

  /** Keep the current procedural or commander-selected track visible in the field HUD. */
  private refreshMusicLabel(): void {
    if (!this.txtMusic) return;
    const local = localMusic.snapshot();
    const localTrack = local.playing ? local.tracks[local.index] : undefined;
    const name = localTrack ? `LOCAL · ${localTrack}` : `MUSIC · ${music.currentSongName}`;
    const clipped = name.length > 34 ? `${name.slice(0, 33)}…` : name;
    if (clipped === this.currentTrackLabel) return;
    this.currentTrackLabel = clipped;
    this.txtMusic.setText(`♪  ${clipped}`);
  }

  private isTowerUnlocked(id: string): boolean {
    return this.isDevMode() || saveManager.data.unlockedTowers.includes(id);
  }

  private isDevMode(): boolean {
    return saveManager.data.settings.devMode === true;
  }

  private canSpend(cost: number): boolean {
    return this.isDevMode() || this.supply >= cost;
  }

  private spend(cost: number): void {
    if (!this.isDevMode()) this.supply -= cost;
  }

  private updatePanelButtons(): void {
    const t = this.selectedTower;
    const show = !!t;
    this.panelBtns.forEach((b, i) => {
      b.bg.setVisible(show);
      b.label.setVisible(show);
      if (!t) return;
      if (i === 0) {
        const cost = t.upgradeCost;
        b.label.setText(cost != null ? `UPGRADE  $${cost}` : 'MAX STAGE');
      } else if (i === 1) {
        b.label.setText(`PRIORITY  ${t.priority}`);
      } else if (i === 2) {
        b.label.setText(
          t.isMax && t.stage.specialName
            ? t.canSpecial()
              ? `${t.stage.specialName}`
              : `SPECIAL  ${t.specialCooldown.toFixed(0)}s`
            : 'SPECIAL  —',
        );
      } else if (i === 3) {
        b.label.setText(`SELL  $${t.sellValue}`);
      }
    });
    if (this.baseUpgradeBtn) {
      const showBase = !t && !this.selectedEnemy && !this.gameOver;
      this.baseUpgradeBtn.bg.setVisible(showBase);
      this.baseUpgradeBtn.label.setVisible(showBase);
      this.baseUpgradeBtn.label.setText(
        this.baseLevel >= 1 ? 'HARDPOINTS ONLINE  ·  2 SLOTS' : `RIGHT-CLICK BASE  ·  $${this.getBaseUpgradeCost()}`,
      );
      this.baseUpgradeBtn.label.setColor(this.baseLevel >= 1 ? '#7dce5a' : '#c8d4e0');
      this.baseUpgradeBtn.bg.setStrokeStyle(1, this.baseLevel >= 1 ? COLORS.good : this.theme.commandColour);
    }
  }
}




